// AdminPage.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import { flushSync } from "react-dom";
import {
    Box, Paper, Typography, TextField, MenuItem, Button, IconButton,
    Tooltip, Grid, Dialog, DialogTitle, DialogContent, DialogActions,
    CircularProgress, Stack, Collapse, Chip, LinearProgress, Divider,
    useMediaQuery, Tabs, Tab, Rating, Alert, Checkbox, FormControlLabel, RadioGroup, Radio,
    Drawer, List, ListItemButton, ListItemIcon, ListItemText, Avatar
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import GroupRoundedIcon from '@mui/icons-material/GroupRounded';
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DiscountIcon from '@mui/icons-material/Discount';
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import PasswordIcon from "@mui/icons-material/Password";
import RefreshIcon from "@mui/icons-material/Refresh";
import FilterListIcon from "@mui/icons-material/FilterList";
import LogoutIcon from "@mui/icons-material/Logout";
import MenuRoundedIcon from "@mui/icons-material/MenuRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import AdminPanelSettingsRoundedIcon from "@mui/icons-material/AdminPanelSettingsRounded";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Diversity3Icon from "@mui/icons-material/Diversity3";
import HandshakeIcon from '@mui/icons-material/Handshake';
import ToggleOnIcon from "@mui/icons-material/ToggleOn";
import ToggleOffIcon from "@mui/icons-material/ToggleOff";

import AcUnitIcon from '@mui/icons-material/AcUnit';
import DeviceThermostatIcon from '@mui/icons-material/DeviceThermostat';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import WhatsAppIcon from "@mui/icons-material/WhatsApp";

import StarRateRoundedIcon from '@mui/icons-material/StarRateRounded';
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';

import InfoIcon from '@mui/icons-material/Info';

import PaymentRoundedIcon from "@mui/icons-material/PaymentRounded";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import LinkRoundedIcon from "@mui/icons-material/LinkRounded";

import EmailRoundedIcon from "@mui/icons-material/EmailRounded";
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";
import FormatUnderlinedIcon from "@mui/icons-material/FormatUnderlined";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered";
import FormatQuoteIcon from "@mui/icons-material/FormatQuote";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import SelectAllRoundedIcon from "@mui/icons-material/SelectAllRounded";
import InsertEmoticonRoundedIcon from "@mui/icons-material/InsertEmoticonRounded";
import AddBoxRoundedIcon from "@mui/icons-material/AddBoxRounded";

import SmartToyRoundedIcon from "@mui/icons-material/SmartToyRounded";
import QueryStatsRoundedIcon from "@mui/icons-material/QueryStatsRounded";
import AttachMoneyRoundedIcon from "@mui/icons-material/AttachMoneyRounded";
import BugReportRoundedIcon from "@mui/icons-material/BugReportRounded";
import AssessmentRoundedIcon from "@mui/icons-material/AssessmentRounded";
import TrendingUpRoundedIcon from "@mui/icons-material/TrendingUpRounded";
import AccountBalanceWalletRoundedIcon from "@mui/icons-material/AccountBalanceWalletRounded";
import ReceiptLongRoundedIcon from "@mui/icons-material/ReceiptLongRounded";
import TableChartRoundedIcon from "@mui/icons-material/TableChartRounded";
import PictureAsPdfRoundedIcon from "@mui/icons-material/PictureAsPdfRounded";

import html2pdf from "html2pdf.js";

import { useAdminAuth } from "../contexts/AdminAuthContext";
import { useSnackbar } from "../contexts/SnackbarContext";
import apiAdm from "../services/apiAdm";
import {
    getApiErrorMessage,
    normalizeContact,
    normalizeEvaluation,
    normalizePartner,
    normalizeReferredUser,
    normalizeTeacherForAdmin,
} from "../services/apiUtils";

const STATUS_OPTS = [
    { value: "", label: "Todos" },
    { value: "G", label: "Gratuito (G)" },
    { value: "A", label: "Ativo (A)" },
    { value: "B", label: "Parceiro (B)" },
    { value: "P", label: "Pendente (P)" },
    { value: "C", label: "Cancelado (C)" },
];

const PLANO_OPTS = [
    { value: "", label: "Todos" },
    { value: "PROFESSOR", label: "PROFESSOR" },
    { value: "MENTOR", label: "MENTOR" },
    { value: "MESTRE", label: "MESTRE" },
];

const AVAL_SENTIMENTO_OPTS = [
    { value: "", label: "Todas" },
    { value: "pos", label: "Positivas (3 a 5 estrelas)" },
    { value: "neg", label: "Negativas (1 e 2 estrelas)" },
];

const TIPO_AVALIADOR_OPTS = [
    { value: "", label: "Todos" },
    { value: "A", label: "Assinante (A)" },
    { value: "B", label: "Parceiro (B)" },
    { value: "G", label: "Testando (G)" },
];

const CONTEXTO_LABEL = {
    avaliacao_assinante: "Avaliação de um assinante",
    primeira_avaliacao_trial: "Primeira avaliação",
    terceira_avaliacao_trial: "Terceira avaliação",
};

const STATUS_LABEL = { G: "Testando", A: "Assinante", B: "Parceiro", P: "Pendente", C: "Cancelado" };
const STATUS_COLOR = { G: "default", A: "success", B: "default", P: "warning", C: "error" };

const getStatusChipSx = (status) => (theme) => ({
    ...(status === "G"
        ? {
            bgcolor: "#eaac10",
            color: "#FFFFFF",
        }
        : {}),
    ...(status === "B"
        ? {
            bgcolor: "#4169E1",
            color: "#FFFFFF",
        }
        : {}),
    fontWeight: 600,
});

const PLAN_STYLES = {
    PROFESSOR: { bg: (t) => t.palette.info.light, fg: (t) => t.palette.info.contrastText },
    MENTOR: { bg: () => "#20B2AA", fg: () => "#FFFFFF" },
    MESTRE: { bg: (t) => t.palette.secondary.light, fg: (t) => t.palette.secondary.contrastText },
};

// Deixa só a primeira letra maiúscula e o resto minúsculo
const formatPlano = (s) =>
    typeof s === "string" && s.length
        ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase()
        : s ?? "";

const REMARKETING_DEFAULT_HTML = `
<p>Olá, <strong>{{primeiroNome}}</strong>! ✨</p>

<p>Passando para lembrar que a <strong>Docen</strong> ajuda você a criar materiais pedagógicos com IA em até 1 minuto, com mais praticidade para sua rotina.</p>

<div style="margin:18px 0;padding:16px 18px;background:#f5f7fb;border:1px solid #eaeef5;border-radius:10px;">
  <p style="margin:0 0 10px 0;"><strong>Com a Docen, você pode:</strong></p>
  <ul style="margin:0;padding-left:20px;line-height:1.7;">
    <li>Gerar atividades, avaliações e planos de aula;</li>
    <li>Criar materiais alinhados à BNCC;</li>
    <li>Editar e exportar em Word ou PDF.</li>
  </ul>
</div>

<p style="text-align:center;margin:24px 0;">
  <a href="https://www.docen.com.br/login" target="_blank" style="display:inline-block;background:#1976d2;color:#ffffff;text-decoration:none;padding:12px 20px;border-radius:8px;font-weight:700;">
    Acessar a Docen agora
  </a>
</p>

<p>Menos tempo montando material. Mais tempo para focar no que realmente importa: ensinar. 💙</p>
`;

const REMARKETING_EMOJIS = ["✨", "📚", "⏱️", "🚀", "🎯", "💡", "✅", "💙"];

function normalizeRemarketingRecipient(raw = {}) {
    return {
        id: raw.id ?? raw.userId ?? raw.id_usuario,
        name: raw.name ?? raw.nome ?? "",
        email: raw.email ?? "",
    };
}

function stripHtmlToText(html) {
    if (typeof document === "undefined") {
        return String(html || "").replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
    }

    const div = document.createElement("div");
    div.innerHTML = html || "";
    return (div.textContent || div.innerText || "").replace(/\s+/g, " ").trim();
}

function RichEmailEditor({ value, onChange, disabled }) {
    const editorRef = useRef(null);

    useEffect(() => {
        if (!editorRef.current) return;
        if (editorRef.current.innerHTML !== value) {
            editorRef.current.innerHTML = value || "";
        }
    }, [value]);

    function emitChange() {
        onChange(editorRef.current?.innerHTML || "");
    }

    function focusEditor() {
        editorRef.current?.focus();
    }

    function exec(command, commandValue = null) {
        if (disabled) return;
        focusEditor();
        document.execCommand(command, false, commandValue);
        emitChange();
    }

    function insertHtml(html) {
        if (disabled) return;
        focusEditor();
        document.execCommand("insertHTML", false, html);
        emitChange();
    }

    function insertLink() {
        if (disabled) return;
        const url = window.prompt("Cole a URL do link:");
        if (!url) return;

        const text = window.prompt("Texto do botão/link:") || "Acessar agora";
        insertHtml(
            `<a href="${url}" target="_blank" style="color:#1976d2;font-weight:700;text-decoration:none;">${text}</a>`
        );
    }

    function insertButton() {
        if (disabled) return;
        const url = window.prompt("Cole a URL do botão:");
        if (!url) return;

        const text = window.prompt("Texto do botão:") || "Acessar agora";
        insertHtml(
            `<p style="text-align:center;margin:24px 0;"><a href="${url}" target="_blank" style="display:inline-block;background:#1976d2;color:#ffffff;text-decoration:none;padding:12px 20px;border-radius:8px;font-weight:700;">${text}</a></p>`
        );
    }

    function insertCard() {
        insertHtml(
            `<div style="margin:18px 0;padding:16px 18px;background:#f5f7fb;border:1px solid #eaeef5;border-radius:10px;"><p style="margin:0;"><strong>Título do bloco</strong><br>Escreva aqui o conteúdo destacado do e-mail.</p></div>`
        );
    }

    const toolbarButtonSx = { minWidth: 38, px: 1 };

    return (
        <Box>
            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
                <Tooltip title="Negrito">
                    <span>
                        <Button size="small" variant="outlined" sx={toolbarButtonSx} disabled={disabled} onClick={() => exec("bold")}>
                            <FormatBoldIcon fontSize="small" />
                        </Button>
                    </span>
                </Tooltip>

                <Tooltip title="Itálico">
                    <span>
                        <Button size="small" variant="outlined" sx={toolbarButtonSx} disabled={disabled} onClick={() => exec("italic")}>
                            <FormatItalicIcon fontSize="small" />
                        </Button>
                    </span>
                </Tooltip>

                <Tooltip title="Sublinhado">
                    <span>
                        <Button size="small" variant="outlined" sx={toolbarButtonSx} disabled={disabled} onClick={() => exec("underline")}>
                            <FormatUnderlinedIcon fontSize="small" />
                        </Button>
                    </span>
                </Tooltip>

                <Tooltip title="Lista com marcadores">
                    <span>
                        <Button size="small" variant="outlined" sx={toolbarButtonSx} disabled={disabled} onClick={() => exec("insertUnorderedList")}>
                            <FormatListBulletedIcon fontSize="small" />
                        </Button>
                    </span>
                </Tooltip>

                <Tooltip title="Lista numerada">
                    <span>
                        <Button size="small" variant="outlined" sx={toolbarButtonSx} disabled={disabled} onClick={() => exec("insertOrderedList")}>
                            <FormatListNumberedIcon fontSize="small" />
                        </Button>
                    </span>
                </Tooltip>

                <Tooltip title="Citação/destaque">
                    <span>
                        <Button size="small" variant="outlined" sx={toolbarButtonSx} disabled={disabled} onClick={() => exec("formatBlock", "blockquote")}>
                            <FormatQuoteIcon fontSize="small" />
                        </Button>
                    </span>
                </Tooltip>

                <Button size="small" variant="outlined" startIcon={<AddBoxRoundedIcon />} disabled={disabled} onClick={insertCard}>
                    Card/bloco
                </Button>

                <Button size="small" variant="outlined" startIcon={<LinkRoundedIcon />} disabled={disabled} onClick={insertLink}>
                    Link
                </Button>

                <Button size="small" variant="outlined" startIcon={<LinkRoundedIcon />} disabled={disabled} onClick={insertButton}>
                    Botão
                </Button>

                <Button size="small" variant="outlined" startIcon={<DeleteOutlineRoundedIcon />} disabled={disabled} onClick={() => exec("removeFormat")}>
                    Limpar formatação
                </Button>
            </Stack>

            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
                {REMARKETING_EMOJIS.map((emoji) => (
                    <Button
                        key={emoji}
                        size="small"
                        variant="text"
                        disabled={disabled}
                        onClick={() => insertHtml(emoji)}
                        sx={{ minWidth: 34, fontSize: 18 }}
                    >
                        {emoji}
                    </Button>
                ))}
            </Stack>

            <Paper
                variant="outlined"
                sx={{
                    p: 2,
                    minHeight: 330,
                    borderRadius: 2,
                    bgcolor: disabled ? "action.disabledBackground" : "background.paper",
                    cursor: disabled ? "not-allowed" : "text",
                    "&:focus-within": {
                        borderColor: "primary.main",
                        boxShadow: (theme) => `0 0 0 2px ${theme.palette.primary.main}22`,
                    },
                    "& blockquote": {
                        margin: "16px 0",
                        padding: "12px 16px",
                        borderLeft: "4px solid #1976d2",
                        background: "#f8fbff",
                        borderRadius: "8px",
                    },
                }}
                onClick={focusEditor}
            >
                <Box
                    ref={editorRef}
                    contentEditable={!disabled}
                    suppressContentEditableWarning
                    onInput={emitChange}
                    sx={{
                        outline: "none",
                        minHeight: 300,
                        fontSize: 14,
                        lineHeight: 1.7,
                        overflowWrap: "anywhere",
                        "& p": { marginTop: 0 },
                    }}
                />
            </Paper>
        </Box>
    );
}

function brDateToIso(value) {
    const digits = String(value || "").replace(/\D/g, "");
    if (digits.length !== 8) return undefined;
    const dd = digits.slice(0, 2);
    const mm = digits.slice(2, 4);
    const yyyy = digits.slice(4);
    return `${yyyy}-${mm}-${dd}`;
}

function normalizeIABalanceSummary(raw = {}) {
    return {
        totalGenerations: raw.totalGenerations ?? raw.total_generations ?? 0,
        totalTokens: raw.totalTokens ?? raw.total_tokens ?? 0,
        totalCostUsd: raw.totalCostUsd ?? raw.total_cost_usd ?? 0,
        avgCostUsd: raw.avgCostUsd ?? raw.avg_cost_usd ?? 0,
        totalRepairs: raw.totalRepairs ?? raw.total_repairs ?? 0,
        totalImages: raw.totalImages ?? raw.total_images ?? 0,
        usdToBrlReference: raw.usdToBrlReference ?? raw.usd_to_brl_reference ?? 5,
        topUsersByCost: raw.topUsersByCost ?? raw.top_users_by_cost ?? [],
        costByPlan: raw.costByPlan ?? raw.cost_by_plan ?? [],
        costByContentType: raw.costByContentType ?? raw.cost_by_content_type ?? [],
        costBySubject: raw.costBySubject ?? raw.cost_by_subject ?? [],
        costByFlow: raw.costByFlow ?? raw.cost_by_flow ?? [],
        highestCostGenerations: raw.highestCostGenerations ?? raw.highest_cost_generations ?? [],
        topIssues: raw.topIssues ?? raw.top_issues ?? [],
    };
}

function formatAdminUsd(value) {
    return `US$ ${Number(value || 0).toFixed(4)}`;
}

function formatAdminBrl(value) {
    return Number(value || 0).toLocaleString("pt-BR", {
        style: "currency",
        currency: "BRL",
    });
}

function compactText(value, fallback = "-") {
    const text = String(value || "").trim();
    return text || fallback;
}

function formatAdminDate(value) {
    const text = String(value || "").trim();
    if (!text) return "-";

    const onlyDate = text.slice(0, 10);
    if (/^\d{4}-\d{2}-\d{2}$/.test(onlyDate)) {
        const [year, month, day] = onlyDate.split("-");
        return `${day}/${month}/${year}`;
    }

    const date = new Date(text);
    if (Number.isNaN(date.getTime())) return text;

    return date.toLocaleDateString("pt-BR");
}

function formatAdminMonth(value) {
    const text = String(value || "").trim();
    if (!text) return "-";

    if (/^\d{4}-\d{2}$/.test(text)) {
        const [year, month] = text.split("-");
        return `${month}/${year}`;
    }

    if (/^\d{4}-\d{2}-\d{2}/.test(text)) {
        const [year, month] = text.slice(0, 10).split("-");
        return `${month}/${year}`;
    }

    return text;
}

function escapeHtml(value) {
    return String(value ?? "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");
}

function normalizePlanName(value) {
    const plan = String(value || "").trim().toUpperCase();
    if (!["PROFESSOR", "MENTOR", "MESTRE"].includes(plan)) return "";
    return plan;
}

function PlanChip({ plan }) {
    const normalizedPlan = normalizePlanName(plan);

    if (!normalizedPlan) return "-";

    return (
        <Chip
            size="small"
            label={formatPlano(normalizedPlan)}
            sx={(theme) => ({
                bgcolor: PLAN_STYLES[normalizedPlan]?.bg(theme) || "action.selected",
                color: PLAN_STYLES[normalizedPlan]?.fg(theme) || theme.palette.text.primary,
                fontWeight: 800,
                minWidth: 82,
            })}
        />
    );
}

function MoneyChip({ value, currency = "usd" }) {
    const label = currency === "brl" ? formatAdminBrl(value) : formatAdminUsd(value);

    return (
        <Chip
            size="small"
            label={label}
            color="success"
            variant="outlined"
            sx={{ fontWeight: 850, borderRadius: 999 }}
        />
    );
}

function CountChip({ value, color = "primary" }) {
    return (
        <Chip
            size="small"
            label={Number(value || 0).toLocaleString("pt-BR")}
            color={color}
            variant="outlined"
            sx={{ fontWeight: 800, borderRadius: 999 }}
        />
    );
}

// function formatFlowLabel(value) {
//     const text = String(value || "").trim();
//     if (!text) return "-";
//     return text.split("/")[0].trim() || text;
// }

// function FlowChip({ value }) {
//     const label = formatFlowLabel(value);

//     if (label === "-") return "-";

//     return (
//         <Chip
//             size="small"
//             label={label}
//             color="secondary"
//             variant="outlined"
//             sx={{ fontWeight: 800, borderRadius: 999 }}
//         />
//     );
// }


const SAAS_HEALTH_STORAGE_KEY = "docen_saas_health_inputs_v1";

const SAAS_HEALTH_DEFAULT_INPUTS = {
    usdToBrlReference: 0,
    taxPercent: 6,
    asaasPixFixedFeeBrl: 1.99,
    asaasCreditCardPercent: 1.99,
    asaasCreditCardFixedFeeBrl: 0.49,
    asaasBoletoFixedFeeBrl: 1.99,
    fixedCostsBrl: 320,
    marketingCostsBrl: 300,
    adsCostsBrl: 500,
    toolsCostsBrl: 0,
    otherCostsBrl: 0,
};

function getCurrentMonthRef() {
    return new Date().toISOString().slice(0, 7);
}

function loadSaasHealthInputs() {
    try {
        const raw = window.localStorage.getItem(SAAS_HEALTH_STORAGE_KEY);
        if (!raw) return SAAS_HEALTH_DEFAULT_INPUTS;

        const parsed = JSON.parse(raw);
        return { ...SAAS_HEALTH_DEFAULT_INPUTS, ...parsed };
    } catch (_) {
        return SAAS_HEALTH_DEFAULT_INPUTS;
    }
}

function normalizeSaasHealth(raw = {}) {
    return {
        month: raw.month,
        status: raw.status || "attention",
        assumptions: raw.assumptions || {},
        summary: raw.summary || {},
        subscribers: raw.subscribers || {},
        usage: raw.usage || {},
        costs: raw.costs || {},
        dre: raw.dre || [],
        paymentMethods: raw.paymentMethods ?? raw.payment_methods ?? [],
        planEconomics: raw.planEconomics ?? raw.plan_economics ?? [],
    };
}

function formatAdminPercent(value) {
    return `${Number(value || 0).toFixed(1).replace(".", ",")}%`;
}

function SaasMetricCard({ title, value, helper, icon, color = "primary", severity = "default" }) {
    const borderColor = severity === "success"
        ? "success.main"
        : severity === "error"
            ? "error.main"
            : severity === "warning"
                ? "warning.main"
                : "divider";

    return (
        <Paper
            variant="outlined"
            sx={{
                p: 2,
                borderRadius: 3,
                height: "100%",
                borderColor,
                bgcolor: severity === "success"
                    ? "success.light"
                    : severity === "error"
                        ? "error.light"
                        : severity === "warning"
                            ? "warning.light"
                            : "background.paper",
                color: ["success", "error", "warning"].includes(severity) ? "common.white" : "text.primary",
            }}
        >
            <Stack spacing={1.1}>
                <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                    <Typography variant="subtitle2" sx={{ opacity: 0.86, fontWeight: 850 }}>
                        {title}
                    </Typography>
                    <Box sx={{ display: "inline-flex", opacity: 0.9 }} color={color}>{icon}</Box>
                </Stack>
                <Typography variant="h5" fontWeight={950}>
                    {value}
                </Typography>
                {helper && (
                    <Typography variant="caption" sx={{ opacity: 0.86 }}>
                        {helper}
                    </Typography>
                )}
            </Stack>
        </Paper>
    );
}

function SaasStatusChip({ status }) {
    const normalized = String(status || "attention");
    const map = {
        healthy: { label: "Saudável", color: "success" },
        attention: { label: "Atenção", color: "warning" },
        critical: { label: "Crítico", color: "error" },
    };
    const info = map[normalized] || map.attention;

    return <Chip size="small" color={info.color} label={info.label} sx={{ fontWeight: 900 }} />;
}

function SaasNumberInput({ label, value, onChange, suffix, helperText, disabled = false }) {
    return (
        <TextField
            label={label}
            type="number"
            fullWidth
            size="small"
            value={value}
            disabled={disabled}
            onChange={(e) => onChange(Number(e.target.value || 0))}
            InputProps={suffix ? { endAdornment: <Typography variant="caption" color="text.secondary">{suffix}</Typography> } : undefined}
            helperText={helperText}
        />
    );
}

function buildSaasGridColumns(columns = {}) {
    const xs = columns.xs ?? 1;
    const sm = columns.sm ?? xs;
    const md = columns.md ?? sm;
    const lg = columns.lg ?? md;

    return {
        xs: `repeat(${xs}, minmax(0, 1fr))`,
        sm: `repeat(${sm}, minmax(0, 1fr))`,
        md: `repeat(${md}, minmax(0, 1fr))`,
        lg: `repeat(${lg}, minmax(0, 1fr))`,
    };
}

function SaasGrid({ children, columns = { xs: 1, sm: 2, md: 4 }, templateColumns, sx = {} }) {
    return (
        <Box
            sx={{
                display: "grid",
                gridTemplateColumns: templateColumns || buildSaasGridColumns(columns),
                gap: 2,
                width: "100%",
                maxWidth: "100%",
                minWidth: 0,
                boxSizing: "border-box",

                // Neutraliza os estilos do Grid item antigo do MUI dentro deste grid
                "& > .MuiGrid-root": {
                    width: "auto !important",
                    maxWidth: "none !important",
                    flexBasis: "auto !important",
                    padding: "0 !important",
                    minWidth: 0,
                },

                ...sx,
            }}
        >
            {children}
        </Box>
    );
}

function paginateClientRows(rows, page, rowsPerPage) {
    const list = Array.isArray(rows) ? rows : [];
    const safePage = Math.max(1, Number(page) || 1);
    const safeRowsPerPage = Math.max(1, Number(rowsPerPage) || 5);
    const start = (safePage - 1) * safeRowsPerPage;

    return list.slice(start, start + safeRowsPerPage);
}

function normalizeIABalanceRow(raw = {}) {
    return {
        date: raw.date ?? raw.dia,
        month: raw.month ?? raw.mes_ref,
        totalGenerations: raw.totalGenerations ?? raw.total_generations ?? 0,
        totalImages: raw.totalImages ?? raw.total_images ?? raw.images ?? 0,
        totalTokens: raw.totalTokens ?? raw.total_tokens ?? 0,
        totalCostUsd: raw.totalCostUsd ?? raw.total_cost_usd ?? 0,
        avgCostUsd: raw.avgCostUsd ?? raw.avg_cost_usd ?? 0,
        totalRepairs: raw.totalRepairs ?? raw.total_repairs ?? 0,
    };
}

function normalizeIAGenerationRow(raw = {}) {
    return {
        id: raw.id ?? raw.id_geracao,
        userId: raw.userId ?? raw.user_id,
        userName: raw.userName ?? raw.user_name,
        userEmail: raw.userEmail ?? raw.user_email,
        planName: raw.planName ?? raw.plan_name,
        subscriptionStatus: raw.subscriptionStatus ?? raw.subscription_status,
        createdAt: raw.createdAt ?? raw.created_at ?? raw.criado_em,
        model: raw.model ?? raw.modelo,
        contentType: raw.contentType ?? raw.content_type ?? raw.tipo_conteudo,
        educationStage: raw.educationStage ?? raw.education_stage ?? raw.etapa_ensino,
        grade: raw.grade ?? raw.serie,
        subject: raw.subject ?? raw.disciplina,
        theme: raw.theme ?? raw.tema,
        questionCount: raw.questionCount ?? raw.question_count ?? raw.quantidade_questoes,
        images: raw.images ?? raw.imagens,
        totalTokens: raw.totalTokens ?? raw.total_tokens,
        totalCostUsd: raw.totalCostUsd ?? raw.total_cost_usd,
        mainTotalTokens: raw.mainTotalTokens ?? raw.main_total_tokens ?? raw.total_tokens,
        repairTotalTokens: raw.repairTotalTokens ?? raw.repair_total_tokens ?? 0,
        hadRepair: raw.hadRepair ?? raw.had_repair ?? raw.teve_repair,
        repairIssues: raw.repairIssues ?? raw.repair_issues,
        promptMode: raw.promptMode ?? raw.prompt_mode,
    };
}


function normalizeWhatsappConversation(raw = {}) {
    return {
        id: raw.id,
        contactId: raw.contactId ?? raw.contact_id,
        phone: raw.phone || "",
        name: raw.name || "",
        source: raw.source || "whatsapp",
        status: raw.status || "open",
        agentEnabled: raw.agentEnabled ?? raw.agent_enabled ?? false,
        humanTakeover: raw.humanTakeover ?? raw.human_takeover ?? false,
        memorySummary: raw.memorySummary ?? raw.memory_summary ?? "",
        lastMessageAt: raw.lastMessageAt ?? raw.last_message_at,
        createdAt: raw.createdAt ?? raw.created_at,
        lastMessage: raw.lastMessage ?? raw.last_message ?? "",
        lastMessageDirection: raw.lastMessageDirection ?? raw.last_message_direction,
        lastMessageDeliveryStatus: raw.lastMessageDeliveryStatus ?? raw.last_message_delivery_status,
        lastMessageDeliveryErrorCode: raw.lastMessageDeliveryErrorCode ?? raw.last_message_delivery_error_code,
        lastMessageDeliveryErrorTitle: raw.lastMessageDeliveryErrorTitle ?? raw.last_message_delivery_error_title,
        totalMessages: raw.totalMessages ?? raw.total_messages ?? 0,
    };
}

function normalizeWhatsappMessage(raw = {}) {
    return {
        id: raw.id,
        conversationId: raw.conversationId ?? raw.conversation_id,
        direction: raw.direction,
        content: raw.content || "",
        providerMessageId: raw.providerMessageId ?? raw.provider_message_id,
        deliveryStatus: raw.deliveryStatus ?? raw.delivery_status,
        deliveryErrorCode: raw.deliveryErrorCode ?? raw.delivery_error_code,
        deliveryErrorTitle: raw.deliveryErrorTitle ?? raw.delivery_error_title,
        deliveryErrorMessage: raw.deliveryErrorMessage ?? raw.delivery_error_message,
        deliveryStatusUpdatedAt: raw.deliveryStatusUpdatedAt ?? raw.delivery_status_updated_at,
        createdAt: raw.createdAt ?? raw.created_at,
    };
}

function normalizeWhatsappBalance(raw = {}) {
    return {
        totalInteractions: raw.totalInteractions ?? raw.total_interactions ?? 0,
        answeredInteractions: raw.answeredInteractions ?? raw.answered_interactions ?? 0,
        totalTokens: raw.totalTokens ?? raw.total_tokens ?? 0,
        totalCostUsd: raw.totalCostUsd ?? raw.total_cost_usd ?? 0,
        totalCostBrl: raw.totalCostBrl ?? raw.total_cost_brl ?? 0,
        todayTokens: raw.todayTokens ?? raw.today_tokens ?? 0,
        todayCostUsd: raw.todayCostUsd ?? raw.today_cost_usd ?? 0,
        todayCostBrl: raw.todayCostBrl ?? raw.today_cost_brl ?? 0,
        monthTokens: raw.monthTokens ?? raw.month_tokens ?? 0,
        monthCostUsd: raw.monthCostUsd ?? raw.month_cost_usd ?? 0,
        monthCostBrl: raw.monthCostBrl ?? raw.month_cost_brl ?? 0,
        usdToBrl: raw.usdToBrl ?? raw.usd_to_brl ?? 5,
        usdToBrlSource: raw.usdToBrlSource ?? raw.usd_to_brl_source ?? "",
    };
}

function formatDateTime(value) {
    if (!value) return "-";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "-";
    return date.toLocaleString("pt-BR");
}

function buildWhatsappUrl(phone) {
    const digits = String(phone || "").replace(/\D/g, "");
    return digits ? `https://wa.me/${digits}` : "#";
}

function getWhatsappDeliveryLabel(status) {
    const normalized = String(status || "").toLowerCase();
    const labels = {
        accepted: "Aceita",
        sent: "Enviada",
        delivered: "Entregue",
        read: "Lida",
        failed: "Falhou",
    };

    return labels[normalized] || "-";
}

function getWhatsappDeliveryColor(status) {
    const normalized = String(status || "").toLowerCase();
    if (normalized === "failed") return "error";
    if (normalized === "delivered" || normalized === "read") return "success";
    if (normalized === "sent" || normalized === "accepted") return "info";
    return "default";
}

function WhatsappDeliveryChip({ status, errorCode, errorTitle }) {
    if (!status) return <Chip size="small" label="-" variant="outlined" />;

    const label = status === "failed" && errorCode
        ? `Falhou (${errorCode})`
        : getWhatsappDeliveryLabel(status);

    return (
        <Chip
            size="small"
            label={label}
            color={getWhatsappDeliveryColor(status)}
            variant={status === "failed" ? "filled" : "outlined"}
            title={errorTitle || undefined}
            sx={{ fontWeight: 800, borderRadius: 999 }}
        />
    );
}

function totalPorPlano(row) {
    const { status, plan, trialLimit } = row || {};
    if (status === 'G') return Number(trialLimit) || 1;
    if (status === 'B') return 25;
    if (status === 'A') {
        if (plan === 'PROFESSOR') return 10;
        if (plan === 'MENTOR') return 25;
        if (plan === 'MESTRE') return 50;
    }
    return 0;
}

function MobileUserCard({ r, copy, onSetPartner, partnerUpdatingId, isMaster }) {

    const total = totalPorPlano(r);
    const usado = Number(r.usedGeneration || 0);
    const pct = total ? Math.max(0, Math.min(100, Math.round((usado / total) * 100))) : 0;
    const settingPartner = partnerUpdatingId === r.id;
    const canSetPartner = isMaster && r.status === "G";
    const partnerTooltip = r.status === "B"
        ? "Usuário parceiro"
        : !isMaster
            ? "Apenas ADMIN MASTER pode definir como parceiro"
            : r.status === "G"
                ? "Definir como parceiro"
                : "Disponível apenas para usuários em teste";

    return (
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
            <Stack spacing={1.25}>
                {/* Cabeçalho do card com wrap para evitar vazamento */}
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        justifyContent: "space-between",
                        flexWrap: "wrap",
                        gap: 1,
                    }}
                >
                    {/* Bloco esquerdo (nome/e-mail) pode encolher e quebrar linhas */}
                    <Box sx={{ flex: "1 1 260px", minWidth: 0 }}>
                        <Stack direction="row" alignItems="center" spacing={0.75}>
                            <Stack sx={{ display: isMaster ? "flex" : "none" }}>
                                <Tooltip title={partnerTooltip}>
                                    <span>
                                        <IconButton
                                            size="small"
                                            onClick={() => onSetPartner?.(r)}
                                            disabled={!canSetPartner || settingPartner}
                                            color={r.status === "B" ? "secondary" : "default"}
                                        >
                                            {settingPartner ? <CircularProgress size={16} /> : <HandshakeIcon fontSize="small" />}
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            </Stack>

                            <Stack>
                                <Typography variant="subtitle1" fontWeight={700}>
                                    {r.name || "-"}
                                </Typography>
                            </Stack>
                        </Stack>


                        <Stack direction="row" alignItems="center" spacing={0.5} flexWrap="wrap">
                            {!!r.email && (
                                <Tooltip title="Copiar E-mail">
                                    <IconButton onClick={() => copy(r.email, "E-mail")} size="small">
                                        <ContentCopyIcon fontSize="inherit" />
                                    </IconButton>
                                </Tooltip>
                            )}
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ overflowWrap: "anywhere" }}
                            >
                                {r.email || "-"}
                            </Typography>
                        </Stack>

                        <Stack direction="row" alignItems="center" spacing={0.5} flexWrap="wrap">
                            {!!r.phone && (
                                <Tooltip title="Abrir WhatsApp">
                                    <IconButton
                                        size="small"
                                        component="a"
                                        href={`https://wa.me/${r.phone.replace(/\D/g, '')}?text=Ol%C3%A1%2C+quero+saber+mais+sobre+a+solu%C3%A7%C3%A3o+Docen!`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        <WhatsAppIcon fontSize="small" sx={{ color: "#25D366" }} />
                                    </IconButton>
                                </Tooltip>
                            )}
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ overflowWrap: "anywhere" }}
                            >
                                {r.phone || "-"}
                            </Typography>

                        </Stack>

                    </Box>

                    {/* Chips (status/plano) */}
                    <Stack
                        direction="row"
                        spacing={1}
                        sx={{
                            width: { xs: "100%", sm: "auto" },
                            justifyContent: { xs: "flex-start", sm: "flex-end" },
                            flexWrap: "wrap",
                            rowGap: 0.75,
                            columnGap: 0.75,
                            alignSelf: { xs: "flex-start", sm: "center" },
                            maxWidth: "100%",
                        }}
                    >
                        {r.status ? (
                            <Chip
                                size="small"
                                label={STATUS_LABEL[r.status] || r.status}
                                color={STATUS_COLOR[r.status] || "default"}
                                sx={getStatusChipSx(r.status)}
                                variant="filled"
                            />
                        ) : null}

                        {r.status === "A" && r.plan ? (
                            <Chip
                                size="small"
                                label={formatPlano(r.plan)}
                                sx={(theme) => ({
                                    bgcolor: PLAN_STYLES[r.plan]?.bg(theme) || "action.selected",
                                    color: PLAN_STYLES[r.plan]?.fg(theme) || theme.palette.text.primary,
                                    fontWeight: 600,
                                })}
                            />
                        ) : null}
                    </Stack>
                </Box>

                <Divider />

                {/* Corpo do card */}
                <Grid container spacing={1.2}>
                    <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary">Região</Typography>
                        <Typography variant="body2">
                            {(r.state && r.city) ? `${r.state} / ${r.city}` : "-"}
                        </Typography>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary">Cadastro</Typography>
                        <Typography variant="body2">
                            {r.createdAt ? new Date(r.createdAt).toLocaleDateString('pt-BR') : "-"}
                        </Typography>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary">Adesão</Typography>
                        <Typography variant="body2">
                            {r.subscribedAt ? new Date(r.subscribedAt).toLocaleDateString('pt-BR') : "-"}
                        </Typography>
                    </Grid>

                    <Grid item xs={12}>
                        <Typography variant="caption" color="text.secondary">Gerações usadas</Typography>
                        {total ? (
                            <Box>
                                <Box display="flex" justifyContent="space-between" mb={0.5}>
                                    <Typography variant="caption" color="text.secondary">Uso</Typography>
                                    <Typography variant="caption" fontWeight={600}>{usado} / {total}</Typography>
                                </Box>
                                <LinearProgress
                                    variant="determinate"
                                    value={pct}
                                    sx={{ height: 8, borderRadius: 999, "& .MuiLinearProgress-bar": { borderRadius: 999 } }}
                                />
                            </Box>
                        ) : (
                            <Typography variant="body2">-</Typography>
                        )}
                    </Grid>
                </Grid>

            </Stack>
        </Paper>
    );
}

function MobilePartnerCard({ p, onToggle, copy }) {

    return (
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
            <Stack spacing={1.25}>
                {/* Cabeçalho */}
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        justifyContent: "space-between",
                        flexWrap: "wrap",
                        gap: 1,
                    }}
                >
                    <Box sx={{ flex: "1 1 260px", minWidth: 0 }}>
                        <Typography variant="subtitle1" fontWeight={700}>
                            {p.name || "-"}
                        </Typography>
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ overflowWrap: "anywhere" }}
                        >
                            {p.email || "-"}
                        </Typography>
                    </Box>

                    <Stack
                        direction="row"
                        spacing={1}
                        sx={{
                            width: { xs: "100%", sm: "auto" },
                            justifyContent: { xs: "flex-start", sm: "flex-end" },
                            flexWrap: "wrap",
                            rowGap: 0.75,
                            columnGap: 0.75,
                            alignSelf: { xs: "flex-start", sm: "center" },
                            maxWidth: "100%",
                        }}
                    >
                        <Chip
                            size="small"
                            label={p.status === 1 ? "Ativo" : "Inativo"}
                            color={p.status === 1 ? "success" : "default"}
                            variant="filled"
                        />
                    </Stack>
                </Box>

                <Divider />

                {/* Corpo */}
                <Grid container spacing={1.2}>
                    <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary">
                            Celular
                        </Typography>
                        <Typography variant="body2">
                            {p.phone || "-"}
                        </Typography>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary">
                            Código indicação
                        </Typography>
                        <Typography
                            variant="body2"
                            sx={{ overflowWrap: "anywhere", display: "flex", alignItems: "center", gap: 0.5 }}
                        >
                            {p.referralCode ? (
                                <>
                                    <Box
                                        sx={{
                                            display: "inline-block",
                                            px: 0.7,
                                            py: 0.2,
                                            borderRadius: 1,
                                            bgcolor: "action.selected",
                                            fontFamily: "monospace",
                                            fontSize: "0.85rem",
                                        }}
                                    >
                                        {p.referralCode}
                                    </Box>

                                    <Tooltip title="Copiar Código">
                                        <IconButton onClick={() => copy(p.referralCode, "Código")} size="small">
                                            <ContentCopyIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </>
                            ) : (
                                "-"
                            )}
                        </Typography>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary">
                            Link indicação
                        </Typography>

                        <Typography
                            variant="body2"
                            sx={{ overflowWrap: "anywhere", display: "flex", alignItems: "center", gap: 0.5 }}
                        >
                            {p.referralCode ? (
                                <>
                                    <Box
                                        sx={{
                                            display: "inline-block",
                                            px: 0.7,
                                            py: 0.2,
                                            borderRadius: 1,
                                            bgcolor: "action.selected", // destaque mais forte
                                            fontFamily: "monospace",
                                            fontSize: "0.85rem"
                                        }}
                                    >
                                        {`${window.location.origin}/cadastro?indicacao=${p.referralCode}`}
                                    </Box>

                                    <Tooltip title="Copiar Link">
                                        <IconButton
                                            onClick={() =>
                                                copy(`${window.location.origin}/cadastro?indicacao=${p.referralCode}`, "Link")
                                            }
                                            size="small"
                                        >
                                            <ContentCopyIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </>
                            ) : (
                                "-"
                            )}
                        </Typography>
                    </Grid>


                </Grid>

                <Box sx={{ pt: 1 }}>
                    <Button
                        fullWidth
                        sx={{ textTransform: "none" }}
                        size="small"
                        variant="outlined"
                        color={p.status === 1 ? "error" : "success"}
                        startIcon={p.status === 1 ? <ToggleOffIcon /> : <ToggleOnIcon />}
                        onClick={onToggle}
                    >
                        {p.status === 1 ? "Desativar" : "Ativar"}
                    </Button>
                </Box>
            </Stack>
        </Paper>
    );
}

function MobileIndicacaoUserCard({ u }) {
    return (
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
            <Stack spacing={1.25}>
                {/* Cabeçalho */}
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        justifyContent: "space-between",
                        flexWrap: "wrap",
                        gap: 1,
                    }}
                >
                    <Box sx={{ flex: "1 1 260px", minWidth: 0 }}>
                        <Typography variant="subtitle1" fontWeight={700}>
                            {u.name || "-"}
                        </Typography>
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ overflowWrap: "anywhere" }}
                        >
                            {u.email || "-"}
                        </Typography>
                    </Box>

                    <Stack
                        direction="row"
                        spacing={1}
                        sx={{
                            width: { xs: "100%", sm: "auto" },
                            justifyContent: { xs: "flex-start", sm: "flex-end" },
                            flexWrap: "wrap",
                            rowGap: 0.75,
                            columnGap: 0.75,
                            alignSelf: { xs: "flex-start", sm: "center" },
                            maxWidth: "100%",
                        }}
                    >
                        {u.status ? (
                            <Chip
                                size="small"
                                label={STATUS_LABEL[u.status] || u.status}
                                color={STATUS_COLOR[u.status] || "default"}
                                sx={getStatusChipSx(u.status)}
                                variant="filled"
                            />
                        ) : null}

                        {u.status === "A" && u.plan ? (
                            <Chip
                                size="small"
                                label={formatPlano(u.plan)}
                                sx={(theme) => ({
                                    bgcolor: PLAN_STYLES[u.plan]?.bg(theme) || "action.selected",
                                    color: PLAN_STYLES[u.plan]?.fg(theme) || theme.palette.text.primary,
                                    fontWeight: 600,
                                })}
                            />
                        ) : null}
                    </Stack>
                </Box>

                <Divider />

                <Grid container spacing={1.2}>
                    <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary">
                            Região
                        </Typography>
                        <Typography variant="body2">
                            {(u.state && u.city) ? `${u.state} / ${u.city}` : "-"}
                        </Typography>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary">
                            Código indicação
                        </Typography>
                        <Typography
                            variant="body2"
                            sx={{ overflowWrap: "anywhere" }}
                        >
                            {u.referralCode || "-"}
                        </Typography>
                    </Grid>
                </Grid>
            </Stack>
        </Paper>
    );
}

function AvaliacaoCard({ a }) {
    const estrelas = Number(a.stars || 0);
    const motivos = Array.isArray(a.reasons) ? a.reasons : [];
    const contextoLabel = CONTEXTO_LABEL[a.context] || a.context || "Contexto não informado";

    return (
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: "100%" }}>
            <Stack spacing={1.25} sx={{ height: "100%" }}>
                {/* Cabeçalho */}
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        justifyContent: "space-between",
                        flexWrap: "wrap",
                        gap: 1,
                    }}
                >
                    {/* Coluna esquerda: nome, e-mail, chips */}
                    <Box sx={{ flex: "1 1 220px", minWidth: 0 }}>
                        <Typography variant="subtitle1" fontWeight={700} noWrap>
                            {a.userName || `Usuário #${a.userId}`}
                        </Typography>
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ overflowWrap: "anywhere" }}
                        >
                            {a.userEmail || "-"}
                        </Typography>

                        <Stack
                            direction="row"
                            spacing={1}
                            sx={{
                                mt: 1,
                                flexWrap: "wrap",
                                rowGap: 0.75,
                                columnGap: 0.75,
                                maxWidth: "100%",
                            }}
                        >
                            {a.subscriptionStatus && (
                                <Chip
                                    size="small"
                                    label={estrelas >= 3 ? "Positiva" : "Negativa"}
                                    variant="outlined"
                                    sx={{
                                        backgroundColor: estrelas >= 3 ? "#2E7D32" : "#D32F2F",
                                        borderColor: estrelas >= 3 ? "#2E7D32" : "#D32F2F",
                                        color: "#FFF"
                                    }}
                                />
                            )}
                        </Stack>
                    </Box>

                    {/* Lado direito: joinha */}
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "flex-end",
                            alignSelf: "flex-start",
                            pl: { xs: 0, sm: 1 },
                        }}
                    >
                        {estrelas >= 3 ? (
                            <ThumbUpIcon
                                sx={{ color: "#2E7D32", fontSize: 26, flexShrink: 0 }}
                            />
                        ) : (
                            <ThumbDownIcon
                                sx={{ color: "#D32F2F", fontSize: 26, flexShrink: 0 }}
                            />
                        )}
                    </Box>
                </Box>


                <Divider />

                {/* Estrelas e contexto */}
                <Stack direction="row" spacing={1} alignItems="center">
                    <Rating

                        value={estrelas}
                        readOnly
                        precision={1}
                        size="small"
                    />
                    <Typography variant="body2" fontWeight={600}>
                        {estrelas} estrela{estrelas === 1 ? "" : "s"}
                    </Typography>
                </Stack>

                <Stack spacing={0.5}>
                    <Typography variant="caption" color="text.secondary">
                        Contexto
                    </Typography>
                    <Chip
                        size="small"
                        label={contextoLabel}
                        variant="filled"
                    />
                </Stack>

                {/* Motivos */}
                {motivos.length > 0 && (
                    <Box>
                        <Typography variant="caption" color="text.secondary">
                            Motivos selecionados
                        </Typography>

                        <Stack spacing={0.5} mt={0.5}>
                            {motivos.map((m, idx) => (
                                <Stack
                                    key={idx}
                                    direction="row"
                                    spacing={1}
                                    alignItems="flex-start"
                                >
                                    <CheckCircleRoundedIcon
                                        fontSize="small"
                                        sx={{
                                            mt: "2px",
                                            color: estrelas >= 3 ? "#2E7D32" : "#D32F2F"
                                        }}
                                    />

                                    <Typography variant="body2">
                                        {m}
                                    </Typography>
                                </Stack>
                            ))}
                        </Stack>
                    </Box>
                )}

                {/* Descrição */}
                {a.description && (
                    <Box>
                        <Typography variant="caption" color="text.secondary">
                            Comentário
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: "pre-wrap" }}>
                            {a.description}
                        </Typography>
                    </Box>
                )}

                {/* Rodapé: data */}
                <Box sx={{ mt: "auto" }}>
                    <Typography variant="caption" color="text.secondary">
                        Criado em:{" "}
                        {a.createdAt
                            ? new Date(a.createdAt).toLocaleString("pt-BR")
                            : "-"}
                    </Typography>
                </Box>
            </Stack>
        </Paper>
    );
}

function mascaraCelular(value = "") {
    const digits = value.replace(/\D/g, "").slice(0, 11);
    if (digits.length <= 10) {
        return digits
            .replace(/^(\d{0,2})/, "($1")
            .replace(/^\((\d{2})(\d)/, "($1) $2")
            .replace(/(\d{4})(\d)/, "$1-$2");
    }
    return digits
        .replace(/^(\d{0,2})/, "($1")
        .replace(/^\((\d{2})(\d)/, "($1) $2")
        .replace(/(\d{5})(\d)/, "$1-$2");
}

const somenteNumeros = (valor) => {
    return valor.replace(/\D/g, '');
};

function pickRows(data, ...keys) {
    for (const key of keys) {
        if (Array.isArray(data?.[key])) return data[key];
    }

    return Array.isArray(data) ? data : [];
}

function pickTotal(data, rows) {
    const total = Number(data?.total);
    return Number.isFinite(total) ? total : rows.length;
}

function PaginationControls({
    page,
    total,
    rowsPerPage,
    onPageChange,
    onRowsPerPageChange,
    loading = false,
    rowsPerPageOptions = [10, 20, 50, 100],
    layout = "inline",
}) {
    const totalPages = Math.max(1, Math.ceil((Number(total) || 0) / rowsPerPage));
    const stackedLeft = layout === "stacked-left";

    return (
        <Box
            mt={2}
            sx={{
                width: "100%",
                minWidth: 0,
                display: "flex",
                flexDirection: stackedLeft ? "column" : { xs: "column", md: "row" },
                alignItems: stackedLeft ? "flex-start" : { xs: "stretch", md: "center" },
                justifyContent: stackedLeft ? "flex-start" : "space-between",
                gap: stackedLeft ? 1.25 : { xs: 1.25, md: 2 },
            }}
        >
            <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1}
                alignItems={{ xs: "stretch", sm: "center" }}
                useFlexGap
                flexWrap="wrap"
                sx={{
                    width: stackedLeft ? "auto" : { xs: "100%", md: "auto" },
                    minWidth: 0,
                    flexShrink: 0,
                }}
            >
                <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ flexShrink: 0 }}
                >
                    Total: {Number(total) || 0}
                </Typography>

                <TextField
                    select
                    size="small"
                    label="Registros por página"
                    value={rowsPerPage}
                    onChange={(e) => onRowsPerPageChange(Number(e.target.value))}
                    sx={{
                        width: { xs: "100%", sm: 225 },
                        maxWidth: "100%",
                        flexShrink: 0,
                    }}
                    disabled={loading}
                >
                    {rowsPerPageOptions.map(n => (
                        <MenuItem key={n} value={n}>{n}</MenuItem>
                    ))}
                </TextField>
            </Stack>

            <Stack
                direction="row"
                alignItems="center"
                justifyContent={stackedLeft ? "flex-start" : { xs: "stretch", sm: "flex-start", md: "flex-end" }}
                useFlexGap
                flexWrap="wrap"
                gap={1}
                sx={{
                    width: stackedLeft ? "auto" : { xs: "100%", md: "auto" },
                    minWidth: 0,
                }}
            >
                <Button
                    variant="outlined"
                    size="small"
                    onClick={() => onPageChange(1)}
                    disabled={loading || page === 1}
                    sx={{
                        minWidth: 76,
                        px: 1.25,
                        flex: { xs: "1 1 100%", sm: "0 0 auto" },
                    }}
                >
                    Primeira
                </Button>

                <Button
                    variant="outlined"
                    size="small"
                    onClick={() => onPageChange(Math.max(1, page - 1))}
                    disabled={loading || page === 1}
                    sx={{
                        minWidth: 76,
                        px: 1.25,
                        flex: { xs: "1 1 100%", sm: "0 0 auto" },
                    }}
                >
                    Anterior
                </Button>

                <Typography
                    variant="body2"
                    noWrap
                    sx={{
                        whiteSpace: "nowrap",
                        flexShrink: 0,
                        px: { xs: 0, sm: 1 },
                        textAlign: "center",
                        flex: { xs: "1 1 100%", sm: "0 0 auto" },
                        order: { xs: -1, sm: 0 },
                    }}
                >
                    Página {page} de {totalPages}
                </Typography>

                <Button
                    variant="outlined"
                    size="small"
                    onClick={() => onPageChange(Math.min(totalPages, page + 1))}
                    disabled={loading || page === totalPages}
                    sx={{
                        minWidth: 76,
                        px: 1.25,
                        flex: { xs: "1 1 100%", sm: "0 0 auto" },
                    }}
                >
                    Próxima
                </Button>

                <Button
                    variant="outlined"
                    size="small"
                    onClick={() => onPageChange(totalPages)}
                    disabled={loading || page === totalPages}
                    sx={{
                        minWidth: 76,
                        px: 1.25,
                        flex: { xs: "1 1 100%", sm: "0 0 auto" },
                    }}
                >
                    Última
                </Button>
            </Stack>
        </Box>
    );
}


const ADMIN_SIDEBAR_WIDTH = 292;
const ADMIN_SIDEBAR_COLLAPSED_WIDTH = 88;

function AdminNavigation({
    sections,
    activeValue,
    onChange,
    onLogout,
    collapsed = false,
    onToggleCollapsed,
    showCollapseToggle = false,
}) {
    return (
        <Box sx={{ height: "100%", display: "flex", flexDirection: "column", minHeight: 0 }}>
            <Stack
                direction="row"
                spacing={collapsed ? 0 : 1.25}
                alignItems="center"
                justifyContent={collapsed ? "center" : "space-between"}
                sx={{ mb: 2, minHeight: 42 }}
            >
                <Stack
                    direction="row"
                    spacing={1.25}
                    alignItems="center"
                    justifyContent={collapsed ? "center" : "flex-start"}
                    sx={{ minWidth: 0, flex: 1 }}
                >


                    {!collapsed && (
                        <>

                            <Tooltip title={collapsed ? "Docen Admin" : ""} placement="right">
                                <Avatar
                                    variant="rounded"
                                    sx={{
                                        width: 42,
                                        height: 42,
                                        borderRadius: 2.5,
                                        bgcolor: "primary.main",
                                        color: "primary.contrastText",
                                        flexShrink: 0,
                                    }}
                                >
                                    <AdminPanelSettingsRoundedIcon />
                                </Avatar>
                            </Tooltip>

                            <Box sx={{ minWidth: 0 }}>
                                <Typography variant="subtitle1" fontWeight={900} noWrap>
                                    Docen Admin
                                </Typography>
                                <Typography variant="caption" color="text.secondary" noWrap>
                                    Gestão do SaaS
                                </Typography>
                            </Box>

                        </>
                    )}
                </Stack>

                {showCollapseToggle && (
                    <Tooltip title={collapsed ? "Expandir menu" : "Minimizar menu"} placement="right">
                        <IconButton
                            size="small"
                            onClick={onToggleCollapsed}
                            aria-label={collapsed ? "Expandir sidebar" : "Minimizar sidebar"}
                            sx={{
                                display: { xs: "none", md: "inline-flex" },
                                border: "1px solid",
                                borderColor: "divider",
                                bgcolor: "background.paper",
                                '&:hover': { bgcolor: "action.hover" },
                            }}
                        >
                            {collapsed ? <ChevronRightRoundedIcon fontSize="small" /> : <ChevronLeftRoundedIcon fontSize="small" />}
                        </IconButton>
                    </Tooltip>
                )}
            </Stack>

            <Divider sx={{ mb: 1.5 }} />

            <List disablePadding sx={{ flex: 1, overflowY: "auto", pr: collapsed ? 0 : 0.5 }}>
                {sections.map((item) => {
                    const active = activeValue === item.value;

                    const button = (
                        <ListItemButton
                            selected={active}
                            onClick={() => onChange(item.value)}
                            sx={{
                                mb: 0.75,
                                borderRadius: 2.5,
                                px: collapsed ? 1 : 1.25,
                                py: 1.15,
                                minHeight: 48,
                                alignItems: "center",
                                justifyContent: collapsed ? "center" : "flex-start",
                                border: "1px solid",
                                borderColor: active ? "primary.main" : "transparent",
                                "&.Mui-selected": {
                                    bgcolor: "primary.main",
                                    color: "primary.contrastText",
                                    boxShadow: (theme) => `0 10px 24px ${theme.palette.primary.main}33`,
                                    "&:hover": { bgcolor: "primary.dark" },
                                    "& .MuiListItemIcon-root": { color: "inherit" },
                                },
                                "&:hover": {
                                    bgcolor: active ? "primary.dark" : "action.hover",
                                },
                            }}
                        >
                            <ListItemIcon
                                sx={{
                                    minWidth: collapsed ? 0 : 40,
                                    color: active ? "inherit" : "text.secondary",
                                    justifyContent: "center",
                                }}
                            >
                                {item.icon}
                            </ListItemIcon>

                            {!collapsed && (
                                <>
                                    <ListItemText
                                        primary={item.label}
                                        secondary={item.description}
                                        primaryTypographyProps={{
                                            fontWeight: 850,
                                            fontSize: 14.5,
                                            noWrap: true,
                                        }}
                                        secondaryTypographyProps={{
                                            fontSize: 12,
                                            noWrap: true,
                                            sx: {
                                                color: active ? "rgba(255,255,255,0.78)" : "text.secondary",
                                            },
                                        }}
                                    />

                                    {active && <ChevronRightRoundedIcon fontSize="small" />}
                                </>
                            )}
                        </ListItemButton>
                    );

                    return collapsed ? (
                        <Tooltip key={item.value} title={item.label} placement="right">
                            {button}
                        </Tooltip>
                    ) : (
                        <Box key={item.value}>{button}</Box>
                    );
                })}
            </List>

            <Divider sx={{ my: 1.5 }} />

            {collapsed ? (
                <Tooltip title="Sair" placement="right">
                    <IconButton
                        color="error"
                        onClick={onLogout}
                        aria-label="Sair"
                        sx={{
                            width: "100%",
                            textTransform: "none",
                            height: 44,
                            borderRadius: 2,
                            border: "1px solid",
                            borderColor: "error.main",
                        }}
                    >
                        <LogoutIcon />
                    </IconButton>
                </Tooltip>
            ) : (
                <Button
                    color="error"
                    variant="outlined"
                    startIcon={<LogoutIcon />}
                    onClick={onLogout}
                    fullWidth
                    sx={{ justifyContent: "flex-start", borderRadius: 2, py: 1, textTransform: "none" }}
                >
                    Sair
                </Button>
            )}
        </Box>
    );
}


function AdminPageOperacional() {
    const { isMaster, logoutAdm } = useAdminAuth();
    const { showSnackbar } = useSnackbar();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    // Aba principal: 0 = Usuários, 1 = Saúde SaaS, 2 = Balanço IA, 3 = Automação, 4 = Avaliações, 5 = Parceiros, 6 = Assinatura, 7 = Remarketing
    const [mainTab, setMainTab] = useState(0);
    const [mobileNavOpen, setMobileNavOpen] = useState(false);
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

    const adminSections = useMemo(() => {
        const sections = [
            {
                value: 0,
                label: "Usuários",
                description: "Cadastros, planos e contatos",
                icon: <GroupRoundedIcon fontSize="small" />,
            },
            {
                value: 1,
                label: "Saúde SaaS",
                description: "DRE, MRR, CAC e margem",
                icon: <AssessmentRoundedIcon fontSize="small" />,
                masterOnly: true,
            },
            {
                value: 2,
                label: "Balanço IA",
                description: "Tokens, custos e repairs",
                icon: <SmartToyRoundedIcon fontSize="small" />,
                masterOnly: true,
            },
            {
                value: 3,
                label: "Automação",
                description: "Agente IA e conversas WhatsApp",
                icon: <WhatsAppIcon fontSize="small" />,
                masterOnly: true,
            },
            {
                value: 4,
                label: "Avaliações",
                description: "Feedbacks dos professores",
                icon: <StarRateRoundedIcon fontSize="small" />,
                masterOnly: true,
            },
            {
                value: 5,
                label: "Parceiros",
                description: "Influenciadores e indicações",
                icon: <Diversity3Icon fontSize="small" />,
            },
            {
                value: 6,
                label: "Assinatura",
                description: "Links de checkout Asaas",
                icon: <PaymentRoundedIcon fontSize="small" />,
                masterOnly: true,
            },
            {
                value: 7,
                label: "Remarketing",
                description: "Disparos de e-mail",
                icon: <EmailRoundedIcon fontSize="small" />,
                masterOnly: true,
            },
        ];

        return sections.filter((item) => !item.masterOnly || isMaster);
    }, [isMaster]);

    const activeSection = useMemo(
        () => adminSections.find((item) => item.value === mainTab) || adminSections[0],
        [adminSections, mainTab]
    );

    function handleMainTabChange(value) {
        setMainTab(value);
        setMobileNavOpen(false);
    }

    // ==== ESTADO MÓDULO REMARKETING ====
    const [remarketingSubject, setRemarketingSubject] = useState("Professor(a), veja como ganhar tempo com a Docen");
    const [remarketingHtml, setRemarketingHtml] = useState(REMARKETING_DEFAULT_HTML);
    const [remarketingTarget, setRemarketingTarget] = useState("all");
    const [remarketingSearch, setRemarketingSearch] = useState("");
    const [remarketingUsers, setRemarketingUsers] = useState([]);
    const [remarketingSelectedUsers, setRemarketingSelectedUsers] = useState([]);
    const [remarketingLoadingUsers, setRemarketingLoadingUsers] = useState(false);
    const [remarketingSending, setRemarketingSending] = useState(false);
    const [remarketingResult, setRemarketingResult] = useState(null);
    const [remarketingTotalUsers, setRemarketingTotalUsers] = useState(0);


    // ==== ESTADO MÓDULO AUTOMAÇÃO WHATSAPP ====
    const [automationSettings, setAutomationSettings] = useState(null);
    const [automationLoading, setAutomationLoading] = useState(false);
    const [automationUpdating, setAutomationUpdating] = useState(false);
    const [automationBalance, setAutomationBalance] = useState(null);
    const [automationConversations, setAutomationConversations] = useState([]);
    const [automationTotal, setAutomationTotal] = useState(0);
    const [automationPage, setAutomationPage] = useState(1);
    const [automationRowsPerPage, setAutomationRowsPerPage] = useState(10);
    const [automationSearch, setAutomationSearch] = useState("");
    const [automationSelectedConversation, setAutomationSelectedConversation] = useState(null);
    const [automationConversationLoading, setAutomationConversationLoading] = useState(false);
    const [automationDialogOpen, setAutomationDialogOpen] = useState(false);

    // ==== ESTADO MÓDULO ASSINATURA (LINK ASAAS) ====
    const [assinEmail, setAssinEmail] = useState("");
    const [assinPlano, setAssinPlano] = useState("MENTOR");

    const [assinLookupLoading, setAssinLookupLoading] = useState(false);
    const [assinUser, setAssinUser] = useState(null);

    const [assinGerarLoading, setAssinGerarLoading] = useState(false);
    const [assinCheckoutUrl, setAssinCheckoutUrl] = useState("");


    // ==== ESTADO MÓDULO USUÁRIOS (mantido) ====
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [partnerUpdatingId, setPartnerUpdatingId] = useState(null);

    // filtros
    const [fEmail, setFEmail] = useState("");
    const [fNome, setFNome] = useState("");
    const [fStatus, setFStatus] = useState("");
    const [fPlano, setFPlano] = useState("");
    const [fDataAdesao, setFDataAdesao] = useState("");

    // UI: filtros retráteis
    const [filtersOpen, setFiltersOpen] = useState(false);

    // paginação real no backend
    const [total, setTotal] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    // const totalPages = useMemo(() => Math.max(1, Math.ceil((total || 0) / rowsPerPage)), [total, rowsPerPage]);
    const paginatedRows = rows;

    // dialogs usuários/ADM
    const [dlgNovoOpen, setDlgNovoOpen] = useState(false);
    const [novoEmail, setNovoEmail] = useState("");
    const [novoLoading, setNovoLoading] = useState(false);
    const [dlgSenhaOpen, setDlgSenhaOpen] = useState(false);
    const [novaSenha, setNovaSenha] = useState("");
    const [senhaLoading, setSenhaLoading] = useState(false);

    // ==== ESTADO MÓDULO PARCEIROS ====

    // Tabs internas do módulo parceiros: 0 = Parceiros, 1 = Usuários por indicação
    const [partnerTab, setPartnerTab] = useState(0);

    // Controles de abertura dos filtros em cada tab
    const [partnerFiltersOpen, setPartnerFiltersOpen] = useState(false);
    const [indicacaoFiltersOpen, setIndicacaoFiltersOpen] = useState(false);

    // Lista de parceiros
    const [partners, setPartners] = useState([]);
    const [partnersLoading, setPartnersLoading] = useState(false);
    const [partnerPage, setPartnerPage] = useState(1);
    const [partnerRowsPerPage, setPartnerRowsPerPage] = useState(10);
    const [partnerTotal, setPartnerTotal] = useState(0);

    // Filtros parceiros
    const [pNome, setPNome] = useState("");
    const [pEmail, setPEmail] = useState("");
    const [pIndicacao, setPIndicacao] = useState("");
    const [pStatus, setPStatus] = useState(1);

    // Dialog novo parceiro
    const [dlgNovoParceiroOpen, setDlgNovoParceiroOpen] = useState(false);
    const [parceiroNome, setParceiroNome] = useState("");
    const [parceiroEmail, setParceiroEmail] = useState("");
    const [parceiroCelular, setParceiroCelular] = useState("");
    const [parceiroDesconto, setParceiroDesconto] = useState(null);
    const [parceiroLoading, setParceiroLoading] = useState(false);

    const [parceiroCodigoGerado, setParceiroCodigoGerado] = useState("");

    // Usuários por indicação
    const [indicacaoUsers, setIndicacaoUsers] = useState([]);
    const [indicacaoLoading, setIndicacaoLoading] = useState(false);
    const [indicacaoPage, setIndicacaoPage] = useState(1);
    const [indicacaoRowsPerPage, setIndicacaoRowsPerPage] = useState(10);
    const [indicacaoTotal, setIndicacaoTotal] = useState(0);
    const [uNome, setUNome] = useState("");
    const [uEmail, setUEmail] = useState("");
    const [uIndicacao, setUIndicacao] = useState("");

    // ==== ESTADO MÓDULO AVALIAÇÕES ====
    const [avaliacoes, setAvaliacoes] = useState([]);
    const [avaliacoesLoading, setAvaliacoesLoading] = useState(false);
    const [avaliacoesPage, setAvaliacoesPage] = useState(1);
    const [avaliacoesRowsPerPage, setAvaliacoesRowsPerPage] = useState(9);
    const [avaliacoesTotal, setAvaliacoesTotal] = useState(0);

    const [fAvalSentimento, setFAvalSentimento] = useState("");
    const [fAvalMesAno, setFAvalMesAno] = useState("");
    const [fAvalNome, setFAvalNome] = useState("");
    const [fAvalTipo, setFAvalTipo] = useState("");

    const [avaliacoesFiltersOpen, setAvaliacoesFiltersOpen] = useState(false);

    // ==== CONTATO (Usuários) ====
    const [dlgContatoOpen, setDlgContatoOpen] = useState(false);
    const [contatoUser, setContatoUser] = useState(null);

    const [contatoData, setContatoData] = useState(new Intl.DateTimeFormat('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
    }).format(new Date()));
    const [contatoFeedback, setContatoFeedback] = useState("");
    const [contatoLoading, setContatoLoading] = useState(false);

    const [contatoHistorico, setContatoHistorico] = useState([]);

    // ==== ESTADO MÓDULO BALANÇO IA ====
    const [iaResumo, setIaResumo] = useState(null);
    const [iaUsdToBrl, setIaUsdToBrl] = useState(5);
    const [iaDiario, setIaDiario] = useState([]);
    const [iaDiarioTotal, setIaDiarioTotal] = useState(0);
    const [iaDiarioPage, setIaDiarioPage] = useState(1);
    const [iaDiarioRowsPerPage, setIaDiarioRowsPerPage] = useState(5);
    const [iaMensal, setIaMensal] = useState([]);
    const [iaMensalTotal, setIaMensalTotal] = useState(0);
    const [iaMensalPage, setIaMensalPage] = useState(1);
    const [iaMensalRowsPerPage, setIaMensalRowsPerPage] = useState(5);
    const [iaLista, setIaLista] = useState([]);
    const [iaListaTotal, setIaListaTotal] = useState(0);
    const [iaLoading, setIaLoading] = useState(false);
    const [iaFiltersOpen, setIaFiltersOpen] = useState(false);

    const hoje = new Date();
    const primeiroDiaMes = new Date(hoje.getFullYear(), hoje.getMonth(), 1);

    const [iaStartDate, setIaStartDate] = useState(primeiroDiaMes.toISOString().slice(0, 10));
    const [iaEndDate, setIaEndDate] = useState(hoje.toISOString().slice(0, 10));
    const [iaTeveRepair, setIaTeveRepair] = useState("");

    const [iaCurrentPage, setIaCurrentPage] = useState(1);
    const [iaRowsPerPage, setIaRowsPerPage] = useState(10);

    const [iaPlanPage, setIaPlanPage] = useState(1);
    const [iaPlanRowsPerPage, setIaPlanRowsPerPage] = useState(5);
    const [iaTopUsersPage, setIaTopUsersPage] = useState(1);
    const [iaTopUsersRowsPerPage, setIaTopUsersRowsPerPage] = useState(5);
    const [iaContentTypePage, setIaContentTypePage] = useState(1);
    const [iaContentTypeRowsPerPage, setIaContentTypeRowsPerPage] = useState(5);
    const [iaSubjectPage, setIaSubjectPage] = useState(1);
    const [iaSubjectRowsPerPage, setIaSubjectRowsPerPage] = useState(5);

    const iaPaginatedRows = iaLista;
    const iaPlanRows = (iaResumo?.costByPlan || []).filter((row) => normalizePlanName(row.plan_name ?? row.planName));
    const iaTopUserRows = (iaResumo?.topUsersByCost || []).filter((row) => normalizePlanName(row.plan_name ?? row.planName));
    const iaContentTypeRows = iaResumo?.costByContentType || [];
    const iaSubjectRows = iaResumo?.costBySubject || [];

    // ==== ESTADO MÓDULO SAÚDE SAAS ====
    const [saasHealth, setSaasHealth] = useState(null);
    const [saasHealthLoading, setSaasHealthLoading] = useState(false);
    const [saasHealthInputsOpen, setSaasHealthInputsOpen] = useState(true);
    const [saasHealthMonth, setSaasHealthMonth] = useState(getCurrentMonthRef());
    const [saasHealthInputs, setSaasHealthInputs] = useState(loadSaasHealthInputs);

    // ==== FUNÇÕES MÓDULO USUÁRIOS ====

    async function fetchRows(pageToFetch = currentPage, limitToFetch = rowsPerPage) {
        setLoading(true);
        try {
            const { data } = await apiAdm.get('/admin/teachers', {
                params: {
                    email: fEmail || undefined,
                    name: fNome || undefined,
                    status: fStatus || undefined,
                    plan: fPlano || undefined,
                    subscribedAt: brDateToIso(fDataAdesao),
                    page: pageToFetch,
                    limit: limitToFetch,
                }
            });

            const rawRows = pickRows(data, 'teachers');
            const rows = rawRows.map(normalizeTeacherForAdmin);
            setRows(rows);
            setTotal(pickTotal(data, rows));
            setCurrentPage(pageToFetch);
            setRowsPerPage(limitToFetch);
        } catch (e) {
            console.error(e);
            showSnackbar(getApiErrorMessage(e, "Erro ao carregar usuários."), "error");
        } finally {
            setLoading(false);
        }
    }

    async function handleSetUserAsPartner(row) {
        if (!row?.id) return;

        if (!isMaster) {
            showSnackbar("Apenas ADMIN MASTER pode definir um usuário como parceiro.", "warning");
            return;
        }

        if (row.status === "B") {
            showSnackbar("Este usuário já está como parceiro.", "info");
            return;
        }

        if (row.status !== "G") {
            showSnackbar("Somente usuários em período de teste podem ser definidos como parceiros.", "warning");
            return;
        }

        setPartnerUpdatingId(row.id);
        try {
            const { data } = await apiAdm.patch(`/admin/users/${row.id}/partner-status`);
            showSnackbar(data?.message || "Usuário definido como parceiro com sucesso.", "success");
            await fetchRows(currentPage, rowsPerPage);
        } catch (e) {
            console.error(e);
            showSnackbar(getApiErrorMessage(e, "Não foi possível definir o usuário como parceiro."), "error");
        } finally {
            setPartnerUpdatingId(null);
        }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { fetchRows(); fetchPartners(); }, []);

    useEffect(() => {
        if (!adminSections.some((item) => item.value === mainTab)) {
            setMainTab(0);
        }
    }, [adminSections, mainTab]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (mainTab === 1 && isMaster && !saasHealthLoading && !saasHealth) {
            fetchSaasHealth();
        }
        if (mainTab === 2 && isMaster && !iaLoading && !iaResumo) {
            fetchIABalanco();
        }
        if (mainTab === 3 && isMaster && !automationLoading && automationConversations.length === 0) {
            fetchAutomationDashboard();
        }
        if (mainTab === 4 && isMaster && avaliacoes.length === 0 && !avaliacoesLoading) {
            fetchAvaliacoes();
        }
        if (mainTab === 7 && isMaster && remarketingUsers.length === 0 && !remarketingLoadingUsers) {
            fetchRemarketingRecipients();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mainTab]);

    async function fetchRemarketingRecipients(search = remarketingSearch) {
        setRemarketingLoadingUsers(true);
        try {
            const { data } = await apiAdm.get('/admin/remarketing/recipients', {
                params: {
                    search: String(search || "").trim() || undefined,
                    page: 1,
                    limit: 30,
                }
            });

            const rawRows = pickRows(data, 'recipients', 'users');
            const users = rawRows.map(normalizeRemarketingRecipient);
            setRemarketingUsers(users);
            setRemarketingTotalUsers(pickTotal(data, users));
        } catch (e) {
            console.error(e);
            showSnackbar(getApiErrorMessage(e, "Erro ao carregar destinatários."), "error");
        } finally {
            setRemarketingLoadingUsers(false);
        }
    }


    async function fetchAutomationDashboard(overrides = {}) {
        await Promise.all([
            fetchAutomationSettings(),
            fetchAutomationBalance(),
            fetchAutomationConversations(overrides),
        ]);
    }

    async function fetchAutomationSettings() {
        try {
            const { data } = await apiAdm.get('/admin/whatsapp-agent/settings');
            setAutomationSettings(data);
        } catch (e) {
            console.error(e);
            showSnackbar(getApiErrorMessage(e, "Erro ao carregar configurações da automação."), "error");
        }
    }

    async function updateAutomationSettings(patch) {
        setAutomationUpdating(true);
        try {
            const { data } = await apiAdm.patch('/admin/whatsapp-agent/settings', patch);
            setAutomationSettings(data);

            if (Object.prototype.hasOwnProperty.call(patch || {}, "enabled")) {
                showSnackbar(data?.enabled ? "Agente ligado com sucesso." : "Agente desligado com sucesso.", "success");
            } else {
                showSnackbar("Configurações da automação atualizadas.", "success");
            }
        } catch (e) {
            console.error(e);
            showSnackbar(getApiErrorMessage(e, "Erro ao atualizar automação."), "error");
        } finally {
            setAutomationUpdating(false);
        }
    }

    async function fetchAutomationBalance() {
        try {
            const { data } = await apiAdm.get('/admin/whatsapp-agent/balance');
            setAutomationBalance(normalizeWhatsappBalance(data));
        } catch (e) {
            console.error(e);
            showSnackbar(getApiErrorMessage(e, "Erro ao carregar custos da automação."), "error");
        }
    }

    async function fetchAutomationConversations(overrides = {}) {
        const pageToFetch = overrides.page ?? automationPage;
        const limitToFetch = overrides.limit ?? automationRowsPerPage;
        const searchToFetch = overrides.search ?? automationSearch;

        setAutomationLoading(true);
        try {
            const { data } = await apiAdm.get('/admin/whatsapp-agent/conversations', {
                params: {
                    page: pageToFetch,
                    limit: limitToFetch,
                    search: searchToFetch || undefined,
                },
            });

            const rawRows = pickRows(data, 'conversations');
            const rows = rawRows.map(normalizeWhatsappConversation);
            setAutomationConversations(rows);
            setAutomationTotal(pickTotal(data, rows));
            setAutomationPage(pageToFetch);
            setAutomationRowsPerPage(limitToFetch);
        } catch (e) {
            console.error(e);
            showSnackbar(getApiErrorMessage(e, "Erro ao carregar conversas da automação."), "error");
        } finally {
            setAutomationLoading(false);
        }
    }

    async function openAutomationConversation(conversationId) {
        setAutomationDialogOpen(true);
        setAutomationConversationLoading(true);
        setAutomationSelectedConversation(null);
        try {
            const { data } = await apiAdm.get(`/admin/whatsapp-agent/conversations/${conversationId}`);
            setAutomationSelectedConversation({
                ...data,
                messages: pickRows(data, 'messages').map(normalizeWhatsappMessage),
            });
        } catch (e) {
            console.error(e);
            showSnackbar(getApiErrorMessage(e, "Erro ao carregar conversa."), "error");
            setAutomationDialogOpen(false);
        } finally {
            setAutomationConversationLoading(false);
        }
    }

    function toggleRemarketingSelected(user) {
        setRemarketingSelectedUsers((prev) => {
            const exists = prev.some((item) => item.id === user.id);
            if (exists) return prev.filter((item) => item.id !== user.id);
            return [...prev, user];
        });
    }

    function clearRemarketingForm() {
        setRemarketingSubject("Professor(a), veja como ganhar tempo com a Docen");
        setRemarketingHtml(REMARKETING_DEFAULT_HTML);
        setRemarketingTarget("selected");
        setRemarketingSelectedUsers([]);
        setRemarketingResult(null);
    }

    async function handleSendRemarketingEmail() {
        const subject = remarketingSubject.trim();
        const bodyText = stripHtmlToText(remarketingHtml);

        if (subject.length < 3) {
            showSnackbar("Informe um título com pelo menos 3 caracteres.", "warning");
            return;
        }

        if (bodyText.length < 3) {
            showSnackbar("O corpo do e-mail não pode estar vazio.", "warning");
            return;
        }

        if (remarketingTarget === "selected" && remarketingSelectedUsers.length === 0) {
            showSnackbar("Selecione pelo menos um usuário ou altere para enviar para todos.", "warning");
            return;
        }

        const confirmMessage = remarketingTarget === "all"
            ? "Você confirma o envio deste e-mail para TODOS os usuários cadastrados?"
            : remarketingTarget === "non_subscribers"
                ? "Você confirma o envio deste e-mail somente para os usuários que NÃO são assinantes?"
                : `Você confirma o envio deste e-mail para ${remarketingSelectedUsers.length} usuário(s) selecionado(s)?`;

        if (!window.confirm(confirmMessage)) return;

        setRemarketingSending(true);
        setRemarketingResult(null);

        try {
            const { data } = await apiAdm.post('/admin/remarketing/emails/send', {
                subject,
                html: remarketingHtml,
                target: remarketingTarget,
                recipientIds: remarketingTarget === "selected"
                    ? remarketingSelectedUsers.map((user) => user.id)
                    : [],
            });

            setRemarketingResult(data);
            showSnackbar(
                `Envio concluído: ${data?.successCount || 0} sucesso(s), ${data?.failedCount || 0} falha(s), ${data?.skippedCount || 0} ignorado(s).`,
                (data?.failedCount || 0) > 0 ? "warning" : "success"
            );
        } catch (e) {
            console.error(e);
            showSnackbar(getApiErrorMessage(e, "Erro ao disparar e-mail de remarketing."), "error");
        } finally {
            setRemarketingSending(false);
        }
    }

    function copy(text, snackText = "Texto") {
        navigator.clipboard.writeText(text || '').then(() => {
            showSnackbar(`${snackText} copiado!`, "info");
        });
    }

    function maskDateInput(v) {
        const digits = v.replace(/\D/g, '').slice(0, 8);
        const parts = [];
        if (digits.length <= 2) return digits;
        parts.push(digits.slice(0, 2));
        if (digits.length <= 4) return parts.join('/') + '/' + digits.slice(2);
        parts.push(digits.slice(2, 4));
        parts.push(digits.slice(4));
        return parts[0] + '/' + parts[1] + '/' + parts[2];
    }

    function maskMonthYearInput(v) {
        const digits = v.replace(/\D/g, '').slice(0, 6); // MMYYYY
        if (digits.length <= 2) return digits;
        return digits.slice(0, 2) + '/' + digits.slice(2);
    }

    async function handleCriarAdm() {
        if (!novoEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(novoEmail)) {
            showSnackbar("Informe um e-mail válido.", "warning");
            return;
        }
        setNovoLoading(true);
        try {
            await apiAdm.post('/admin', { email: novoEmail.trim().toLowerCase() });
            showSnackbar("ADM criado com sucesso. Senha padrão: adm@123", "success");
            setDlgNovoOpen(false);
            setNovoEmail("");
            fetchRows();
        } catch (e) {
            console.error(e);
            showSnackbar(getApiErrorMessage(e, "Erro ao criar ADM."), "error");
        } finally {
            setNovoLoading(false);
        }
    }

    async function handleAlterarSenha() {
        if (!novaSenha || novaSenha.trim().length < 6) {
            showSnackbar("Informe uma nova senha (mín. 6 caracteres).", "warning");
            return;
        }
        setSenhaLoading(true);
        try {
            await apiAdm.put('/admin/password', { newPassword: novaSenha.trim() });
            showSnackbar("Senha alterada com sucesso.", "success");
            setDlgSenhaOpen(false);
            setNovaSenha("");
        } catch (e) {
            console.error(e);
            showSnackbar(getApiErrorMessage(e, "Erro ao alterar senha."), "error");
        } finally {
            setSenhaLoading(false);
        }
    }

    function handleClearFilters() {
        flushSync(() => {
            setFEmail("");
            setFNome("");
            setFStatus("");
            setFPlano("");
            setFDataAdesao("");
            setCurrentPage(1);
        });
        fetchRows();
    }

    const activeFilterChips = (
        <Stack direction="row" gap={1} flexWrap="wrap">
            {fEmail && <Chip size="small" label={`E-mail: ${fEmail}`} />}
            {fNome && <Chip size="small" label={`Nome: ${fNome}`} />}
            {fStatus && <Chip size="small" label={`Status: ${STATUS_OPTS.find(o => o.value === fStatus)?.label || fStatus}`} />}
            {fPlano && <Chip size="small" label={`Plano: ${fPlano}`} />}
            {fDataAdesao && <Chip size="small" label={`Adesão: ${fDataAdesao}`} />}
        </Stack>
    );

    // ==== FUNÇÕES MÓDULO PARCEIROS ====

    const PARTNER_STATUS_OPTS = [
        { value: "1", label: "Ativo" },
        { value: "0", label: "Inativo" },
    ];

    async function fetchPartners(pageToFetch = partnerPage, limitToFetch = partnerRowsPerPage) {
        setPartnersLoading(true);
        try {
            const { data } = await apiAdm.get('/admin/partners', {
                params: {
                    name: pNome || undefined,
                    email: pEmail || undefined,
                    referralCode: pIndicacao || undefined,
                    status: pStatus || undefined,
                    page: pageToFetch,
                    limit: limitToFetch,
                }
            });

            const rawRows = pickRows(data, 'partners');
            const partners = rawRows.map(normalizePartner);
            setPartners(partners);
            setPartnerTotal(pickTotal(data, partners));
            setPartnerPage(pageToFetch);
            setPartnerRowsPerPage(limitToFetch);
        } catch (e) {
            console.error(e);
            showSnackbar(getApiErrorMessage(e, "Erro ao carregar parceiros."), "error");
        } finally {
            setPartnersLoading(false);
        }
    }

    function clearPartnerFilters() {
        setPNome("");
        setPEmail("");
        setPIndicacao("");
        setPStatus("");
        setPartnerPage(1);
        setPartnerTotal(0);
        setPartners([]);
    }

    function handleCloseNovoParceiro() {
        if (parceiroLoading) return;
        setDlgNovoParceiroOpen(false);
        setParceiroNome("");
        setParceiroEmail("");
        setParceiroCelular("");
        setParceiroDesconto();
        setParceiroCodigoGerado("");
    }

    async function handleNovoParceiro() {
        if (!parceiroNome.trim()) {
            showSnackbar("Informe o nome do parceiro.", "warning");
            return;
        }
        if (!parceiroEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(parceiroEmail)) {
            showSnackbar("Informe um e-mail válido.", "warning");
            return;
        }

        if (!parceiroCelular.trim()) {
            showSnackbar("Informe o celular do parceiro.", "warning");
            return;
        }

        const desconto = parceiroDesconto !== null ? Number(parceiroDesconto) : 0;

        if (desconto === 0) {
            showSnackbar("Informe o desconto atrelado ao parceiro.", "warning");
            return;
        }

        if (desconto > 50) {
            showSnackbar("O desconto não pode ser maior que 50%", "warning");
            return;
        }

        setParceiroLoading(true);
        try {
            const { data } = await apiAdm.post('/admin/partners', {
                name: parceiroNome.trim(),
                email: parceiroEmail.trim(),
                phone: parceiroCelular.trim(),
                couponDiscountPercentage: desconto,
            });

            setParceiroCodigoGerado(data?.referralCode || "");
            showSnackbar(data?.message || "Parceiro criado com sucesso.", "success");
            fetchPartners();
        } catch (e) {
            console.error(e);
            showSnackbar(getApiErrorMessage(e, "Erro ao criar parceiro."), "error");
        } finally {
            setParceiroLoading(false);
        }
    }

    async function handleToggleParceiroStatus(p) {
        try {
            const { data } = await apiAdm.patch(`/admin/partners/${p.partnerId}/status`);
            const statusAtual = data?.currentStatus;

            showSnackbar(
                statusAtual === 1 ? "Parceiro ativado." : "Parceiro desativado.",
                "success"
            );

            fetchPartners();
        } catch (e) {
            console.error(e);
            showSnackbar(getApiErrorMessage(e, "Erro ao alterar status do parceiro."), "error");
        }
    }

    // Usuários por indicação
    const hasIndicacaoUserFilter = uNome || uEmail || uIndicacao;

    async function fetchIndicacaoUsers(pageToFetch = indicacaoPage, limitToFetch = indicacaoRowsPerPage) {
        if (!hasIndicacaoUserFilter) {
            showSnackbar("Informe pelo menos um filtro.", "warning");
            return;
        }

        setIndicacaoLoading(true);
        try {
            const { data } = await apiAdm.get('/admin/partners/referred-users', {
                params: {
                    partnerName: uNome || undefined,
                    partnerEmail: uEmail || undefined,
                    referralCode: uIndicacao || undefined,
                    page: pageToFetch,
                    limit: limitToFetch,
                }
            });

            const rawRows = pickRows(data, 'rows', 'users');
            const rows = rawRows.map(normalizeReferredUser);
            setIndicacaoUsers(rows);
            setIndicacaoTotal(pickTotal(data, rows));
            setIndicacaoPage(pageToFetch);
            setIndicacaoRowsPerPage(limitToFetch);
        } catch (e) {
            console.error(e);
            showSnackbar(getApiErrorMessage(e, "Erro ao carregar usuários por indicação."), "error");
        } finally {
            setIndicacaoLoading(false);
        }
    }

    function clearIndicacaoUserFilters() {
        setUNome("");
        setUEmail("");
        setUIndicacao("");
        setIndicacaoPage(1);
        setIndicacaoTotal(0);
        setIndicacaoUsers([]);
    }

    // ==== FUNÇÕES MÓDULO AVALIAÇÕES ====

    function parseMesAno(mesAnoStr) {
        if (!mesAnoStr) return { mes: undefined, ano: undefined };
        const digits = mesAnoStr.replace(/\D/g, '');
        if (digits.length !== 6) return { mes: undefined, ano: undefined };
        const mes = Number(digits.slice(0, 2));
        const ano = Number(digits.slice(2));
        if (!Number.isFinite(mes) || !Number.isFinite(ano)) {
            return { mes: undefined, ano: undefined };
        }
        return { mes, ano };
    }

    async function fetchAvaliacoes(pageToFetch = avaliacoesPage, limitToFetch = avaliacoesRowsPerPage) {
        setAvaliacoesLoading(true);
        try {
            let minStars;
            let maxStars;

            if (fAvalSentimento === "pos") {
                minStars = 3;
            } else if (fAvalSentimento === "neg") {
                minStars = 1;
                maxStars = 2;
            }

            const { mes, ano } = parseMesAno(fAvalMesAno);

            const { data } = await apiAdm.get('/admin/relationship/evaluations', {
                params: {
                    userName: fAvalNome || undefined,
                    subscriptionStatus: fAvalTipo || undefined,
                    minStars,
                    maxStars,
                    month: mes || undefined,
                    year: ano || undefined,
                    page: pageToFetch,
                    limit: limitToFetch,
                }
            });

            const rawRows = pickRows(data, 'rows', 'evaluations');
            const rows = rawRows.map(normalizeEvaluation);
            setAvaliacoes(rows);
            setAvaliacoesTotal(pickTotal(data, rows));
            setAvaliacoesPage(pageToFetch);
            setAvaliacoesRowsPerPage(limitToFetch);
        } catch (e) {
            console.error(e);
            showSnackbar(getApiErrorMessage(e, "Erro ao carregar avaliações."), "error");
        } finally {
            setAvaliacoesLoading(false);
        }
    }

    function clearAvaliacoesFilters() {
        setFAvalSentimento("");
        setFAvalMesAno("");
        setFAvalNome("");
        setFAvalTipo("");
        setAvaliacoesPage(1);
        setAvaliacoesTotal(0);
        setAvaliacoes([]);
    }

    const activeAvalFilterChips = (
        <Stack direction="row" gap={1} flexWrap="wrap">
            {fAvalSentimento && (
                <Chip
                    size="small"
                    label={`Sentimento: ${AVAL_SENTIMENTO_OPTS.find(o => o.value === fAvalSentimento)?.label || fAvalSentimento
                        }`}
                />
            )}
            {fAvalMesAno && <Chip size="small" label={`Mês/Ano: ${fAvalMesAno}`} />}
            {fAvalNome && <Chip size="small" label={`Nome: ${fAvalNome}`} />}
            {fAvalTipo && (
                <Chip
                    size="small"
                    label={`Tipo: ${TIPO_AVALIADOR_OPTS.find(o => o.value === fAvalTipo)?.label || fAvalTipo
                        }`}
                />
            )}
        </Stack>
    );

    // ==== FUNÇÕES CONTATO ====

    function isoToBrDate(iso) {
        if (!iso) return "";
        const d = new Date(iso);
        if (Number.isNaN(d.getTime())) return "";
        return d.toLocaleDateString("pt-BR");
    }

    function parseBrToDate(br) {
        const digits = (br || "").replace(/\D/g, "");
        if (digits.length !== 8) return null;
        const dd = Number(digits.slice(0, 2));
        const mm = Number(digits.slice(2, 4));
        const yyyy = Number(digits.slice(4));
        if (!dd || !mm || !yyyy) return null;
        return new Date(yyyy, mm - 1, dd);
    }

    function getContatoChipInfoByLastDate(lastBrDateStr) {
        const d = parseBrToDate(lastBrDateStr);

        if (!d) {
            return {
                label: "Sem contato ainda ✨",
                icon: null,
                sx: (t) => ({
                    bgcolor: t.palette.action.selected,
                    color: t.palette.text.secondary,
                    fontWeight: 800,
                }),
            };
        }

        const hoje = new Date();
        const diffDays = Math.floor((hoje - d) / (1000 * 60 * 60 * 24));

        if (diffDays < 7) {
            return {
                label: "Quente",
                icon: <LocalFireDepartmentIcon sx={{ fontSize: 16 }} color="#FFF" />,
                sx: {
                    bgcolor: "#E53935",   // vermelho vivo
                    color: "#FFFFFF",
                    fontWeight: 900,
                },
            };
        }

        if (diffDays <= 14) {
            return {
                label: "Morno",
                icon: <DeviceThermostatIcon sx={{ fontSize: 16 }} color="#FFF" />,
                sx: {
                    bgcolor: "#FB8C00",   // laranja
                    color: "#FFFFFF",
                    fontWeight: 900,
                },
            };
        }

        return {
            label: "Frio",
            icon: <AcUnitIcon sx={{ fontSize: 16 }} color="#FFF" />,
            sx: {
                bgcolor: "#1E88E5",   // azul frio
                color: "#FFFFFF",
                fontWeight: 900,
            },
        };
    }

    async function fetchContatoHistorico(userId) {
        try {
            const { data } = await apiAdm.get(`/admin/users/${userId}/contacts`, {
                params: { page: 1, limit: 100 },
            });

            const contatos = Array.isArray(data?.contacts)
                ? data.contacts.map(normalizeContact)
                : [];

            setContatoHistorico(contatos);
        } catch (e) {
            console.error(e);
            setContatoHistorico([]);
            showSnackbar(getApiErrorMessage(e, "Erro ao carregar histórico."), "error");
        }
    }

    // async function openContatoDialog(userRow) {
    //     setContatoUser(userRow);

    //     // campos SEMPRE vazios
    //     setContatoData(new Intl.DateTimeFormat('pt-BR', {
    //         day: '2-digit',
    //         month: '2-digit',
    //         year: 'numeric'
    //     }).format(new Date()));
    //     setContatoFeedback("");
    //     setContatoHistorico([]);

    //     setDlgContatoOpen(true);

    //     setContatoLoading(true);
    //     await fetchContatoHistorico(userRow.id);
    //     setContatoLoading(false);
    // }

    function closeContatoDialog() {
        if (contatoLoading) return;
        setDlgContatoOpen(false);
        setContatoUser(null);
        setContatoFeedback("");
        setContatoHistorico([]);
    }

    async function salvarContato() {
        if (!contatoUser?.id) return;

        if (contatoData === "" || contatoData.replace(/\D/g, "").length !== 8) {
            showSnackbar("Informe uma data válida (DD/MM/AAAA).", "warning");
            return;
        }

        if (contatoFeedback === "" || contatoFeedback === null) {
            showSnackbar("Insira algum retorno do contato (Feedback).", "warning");
            return;
        }

        const dataContato = parseBrToDate(contatoData);

        setContatoLoading(true);
        try {
            await apiAdm.post(`/admin/users/${contatoUser.id}/contacts`, {
                contactDate: dataContato ? dataContato.toISOString() : undefined,
                feedback: contatoFeedback,
            });

            showSnackbar("Contato registrado.", "success");
            setContatoFeedback("");
            setDlgContatoOpen(false);

            await fetchContatoHistorico(contatoUser.id);
            fetchRows();
        } catch (e) {
            console.error(e);
            showSnackbar(getApiErrorMessage(e, "Erro ao salvar contato."), "error");
        } finally {
            setContatoLoading(false);
        }
    }

    // ==== FUNÇÕES LINK ASSINATURA ====

    function isValidEmail(v) {
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(v || "").trim());
    }

    async function lookupUserByEmail(email) {
        const e = String(email || "").trim();
        if (!isValidEmail(e)) {
            setAssinUser(null);
            return;
        }

        setAssinLookupLoading(true);
        try {
            const { data } = await apiAdm.get("/admin/teachers", {
                params: { email: e, page: 1, limit: 10 }
            });

            const rows = Array.isArray(data?.teachers) ? data.teachers.map(normalizeTeacherForAdmin) : [];
            const found =
                rows.find(r => String(r.email || "").toLowerCase() === e.toLowerCase()) ||
                rows[0] ||
                null;

            setAssinUser(found || null);
        } catch (err) {
            console.error(err);
            setAssinUser(null);
        } finally {
            setAssinLookupLoading(false);
        }
    }

    async function gerarLinkAssinatura() {
        const teacherEmail = String(assinEmail || "").trim().toLowerCase();

        if (!isValidEmail(teacherEmail)) {
            showSnackbar("Informe um e-mail válido.", "warning");
            return;
        }

        if (!assinPlano) {
            showSnackbar("Selecione um plano.", "warning");
            return;
        }

        setAssinGerarLoading(true);
        setAssinCheckoutUrl("");

        try {
            const { data } = await apiAdm.post('/admin/billing/subscription/checkout', {
                teacherEmail,
                planName: assinPlano,
            });

            const checkoutUrl = data?.checkoutUrl || data?.checkout_url || data?.invoiceUrl || data?.invoice_url;

            if (!checkoutUrl) {
                throw new Error("Checkout URL não retornado pelo backend.");
            }

            setAssinCheckoutUrl(checkoutUrl);
            showSnackbar("Link de checkout gerado com sucesso.", "success");

            if (!assinUser) {
                await lookupUserByEmail(teacherEmail);
            }
        } catch (e) {
            console.error(e);
            showSnackbar(getApiErrorMessage(e, "Erro ao gerar link de checkout."), "error");
        } finally {
            setAssinGerarLoading(false);
        }
    }

    function limparAssinatura() {
        setAssinEmail("");
        setAssinPlano("MENTOR");
        setAssinUser(null);
        setAssinCheckoutUrl("");
    }

    // ==== BLOCO IA BALANÇO ====
    async function fetchIABalanco(overrides = {}) {
        const dailyPage = overrides.dailyPage ?? iaDiarioPage;
        const dailyLimit = overrides.dailyLimit ?? iaDiarioRowsPerPage;
        const monthlyPage = overrides.monthlyPage ?? iaMensalPage;
        const monthlyLimit = overrides.monthlyLimit ?? iaMensalRowsPerPage;
        const generationsPage = overrides.generationsPage ?? iaCurrentPage;
        const generationsLimit = overrides.generationsLimit ?? iaRowsPerPage;

        setIaLoading(true);
        try {
            const params = {
                startDate: iaStartDate || undefined,
                endDate: iaEndDate || undefined,
            };

            const [resResumo, resDiario, resMensal, resLista, resCambioAutomacao] = await Promise.all([
                apiAdm.get('/admin/pedagogical-content/balance/summary', { params }),
                apiAdm.get('/admin/pedagogical-content/balance/daily', {
                    params: { ...params, page: dailyPage, limit: dailyLimit },
                }),
                apiAdm.get('/admin/pedagogical-content/balance/monthly', {
                    params: { ...params, page: monthlyPage, limit: monthlyLimit },
                }),
                apiAdm.get('/admin/pedagogical-content/generations', {
                    params: {
                        ...params,
                        hadRepair: iaTeveRepair || undefined,
                        page: generationsPage,
                        limit: generationsLimit,
                    }
                }),
                apiAdm.get('/admin/whatsapp-agent/balance').catch(() => null),
            ]);

            const diarioRows = pickRows(resDiario?.data, 'rows');
            const mensalRows = pickRows(resMensal?.data, 'rows');
            const listaRows = pickRows(resLista?.data, 'rows', 'generations');

            const cambioAtual = Number(resCambioAutomacao?.data?.usd_to_brl || resCambioAutomacao?.data?.usdToBrl || 5);
            setIaUsdToBrl(Number.isFinite(cambioAtual) && cambioAtual > 0 ? cambioAtual : 5);
            setIaResumo(resResumo?.data ? normalizeIABalanceSummary(resResumo.data) : null);
            setIaDiario(diarioRows.map(normalizeIABalanceRow));
            setIaDiarioTotal(pickTotal(resDiario?.data, diarioRows));
            setIaDiarioPage(dailyPage);
            setIaDiarioRowsPerPage(dailyLimit);
            setIaMensal(mensalRows.map(normalizeIABalanceRow));
            setIaMensalTotal(pickTotal(resMensal?.data, mensalRows));
            setIaMensalPage(monthlyPage);
            setIaMensalRowsPerPage(monthlyLimit);
            setIaLista(listaRows.map(normalizeIAGenerationRow));
            setIaListaTotal(pickTotal(resLista?.data, listaRows));
            setIaCurrentPage(generationsPage);
            setIaRowsPerPage(generationsLimit);
            setIaPlanPage(1);
            setIaTopUsersPage(1);
            setIaContentTypePage(1);
            setIaSubjectPage(1);
        } catch (e) {
            console.error(e);
            showSnackbar(getApiErrorMessage(e, "Erro ao carregar balanço da IA."), "error");
        } finally {
            setIaLoading(false);
        }
    }

    function clearIaFilters() {
        const now = new Date();
        const start = new Date(now.getFullYear(), now.getMonth(), 1);
        setIaStartDate(start.toISOString().slice(0, 10));
        setIaEndDate(now.toISOString().slice(0, 10));
        setIaTeveRepair("");
        setIaDiarioPage(1);
        setIaMensalPage(1);
        setIaCurrentPage(1);
        setIaPlanPage(1);
        setIaTopUsersPage(1);
        setIaContentTypePage(1);
        setIaSubjectPage(1);
    }

    function updateSaasHealthInput(key, value) {
        setSaasHealthInputs((prev) => {
            const next = { ...prev, [key]: value };
            try {
                window.localStorage.setItem(SAAS_HEALTH_STORAGE_KEY, JSON.stringify(next));
            } catch (_) { }
            return next;
        });
    }

    function resetSaasHealthInputs() {
        setSaasHealthInputs((prev) => {
            const currentUsdToBrl =
                Number(saasHealth?.assumptions?.usd_to_brl_reference) ||
                Number(saasHealth?.assumptions?.usdToBrlReference) ||
                Number(prev.usdToBrlReference) ||
                Number(automationBalance?.usdToBrl) ||
                5;

            const next = {
                ...SAAS_HEALTH_DEFAULT_INPUTS,
                usdToBrlReference: currentUsdToBrl,
            };

            try {
                window.localStorage.setItem(SAAS_HEALTH_STORAGE_KEY, JSON.stringify(next));
            } catch (_) { }

            return next;
        });

        showSnackbar("Premissas restauradas para o padrão.", "info");
    }

    async function fetchAutomaticUsdToBrl() {
        try {
            const { data } = await apiAdm.get('/admin/whatsapp-agent/balance');
            const rate = Number(data?.usdToBrl ?? data?.usd_to_brl);
            if (Number.isFinite(rate) && rate > 0) return rate;
        } catch (_) { }

        try {
            const response = await fetch('https://economia.awesomeapi.com.br/json/last/USD-BRL');
            const data = await response.json();
            const rate = Number(data?.USDBRL?.bid);
            if (Number.isFinite(rate) && rate > 0) return rate;
        } catch (_) { }

        return Number(saasHealthInputs.usdToBrlReference || 0) || 5;
    }

    async function fetchSaasHealth(overrides = {}) {
        setSaasHealthLoading(true);
        try {
            const automaticUsdToBrl = await fetchAutomaticUsdToBrl();
            const currentInputs = {
                ...saasHealthInputs,
                usdToBrlReference: automaticUsdToBrl,
                ...(overrides.inputs || {}),
            };

            setSaasHealthInputs((prev) => {
                const next = { ...prev, usdToBrlReference: automaticUsdToBrl };
                try {
                    window.localStorage.setItem(SAAS_HEALTH_STORAGE_KEY, JSON.stringify(next));
                } catch (_) { }
                return next;
            });

            const params = {
                month: overrides.month ?? saasHealthMonth,
                ...currentInputs,
            };

            const { data } = await apiAdm.get('/admin/saas-health/monthly', { params });
            setSaasHealth(normalizeSaasHealth(data));
        } catch (e) {
            console.error(e);
            showSnackbar(getApiErrorMessage(e, "Erro ao carregar saúde do SaaS."), "error");
        } finally {
            setSaasHealthLoading(false);
        }
    }

    // ==== DRE EXPORT (EXCEL & PDF) ====

    function formatMonthRefBr(monthRef) {
        const [year, month] = String(monthRef || "").split("-");
        if (!year || !month) return String(monthRef || "");
        return `${month}-${year}`;
    }

    function formatMonthRefBrLabel(monthRef) {
        const [year, month] = String(monthRef || "").split("-");
        if (!year || !month) return String(monthRef || "");
        return `${month}/${year}`;
    }

    function formatExportBrl(value) {
        const number = Number(value || 0);

        return number.toLocaleString("pt-BR", {
            style: "currency",
            currency: "BRL",
        });
    }

    function formatDreTypeLabel(type, isLast = false) {
        if (isLast) return "Total";

        const normalized = String(type || "").toLowerCase();

        const labels = {
            positive: "Entrada",
            negative: "Saída",
            subtotal: "Subtotal",
            total: "Total",
            attention: "Atenção",
            success: "Positivo",
            error: "Negativo",
            default: "Normal",
        };

        return labels[normalized] || "Normal";
    }

    function formatDreExportValue(row, isLast = false) {
        const type = String(row.tipo || row.type || "").toLowerCase();
        const rawValue = Number(row.valor || row.value_brl || 0);
        const absValue = Math.abs(rawValue);

        if (!isLast && type === "negative") {
            return `- ${formatExportBrl(absValue)}`;
        }

        return formatExportBrl(rawValue);
    }

    function getDreValueColor(row, isLast = false) {
        const type = String(row.tipo || row.type || "").toLowerCase();
        const value = Number(row.valor || row.value_brl || 0);

        if (!isLast && type === "negative") {
            return "#EF6C00"; // laranja para saídas
        }

        if (isLast && value < 0) {
            return "#D32F2F"; // vermelho para resultado final negativo
        }

        if (value > 0) {
            return "#1B5E20"; // verde
        }

        return "#263238";
    }

    function getDreExportRowStyle(row, index, rows) {
        const isLast = index === rows.length - 1;
        const type = String(row.tipo || row.type || "").toLowerCase();

        const isHighlighted =
            isLast ||
            type === "subtotal" ||
            type === "total" ||
            String(row.item || "").toLowerCase().includes("receita líquida") ||
            String(row.item || "").toLowerCase().includes("margem bruta") ||
            String(row.item || "").toLowerCase().includes("resultado");

        return {
            isHighlighted,
            isLast,
        };
    }

    function buildSaasDreRowsForExport() {
        const dreRows = Array.isArray(saasHealth?.dre) ? saasHealth.dre : [];
        return dreRows.map((row) => ({
            item: row.label,
            valor: Number(row.value_brl || 0),
            tipo: row.type || "",
        }));
    }

    function downloadBlob(content, filename, type) {
        const blob = new Blob([content], { type });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(url);
    }

    function downloadSaasDreExcel() {
        if (!saasHealth) return;

        const rows = buildSaasDreRowsForExport();
        const monthLabel = formatMonthRefBrLabel(saasHealthMonth);
        const fileMonth = formatMonthRefBr(saasHealthMonth);

        const htmlRows = rows.map((row, idx) => {
            const { isHighlighted, isLast } = getDreExportRowStyle(row, idx, rows);

            const bg = isLast
                ? "#e9ecef"
                : isHighlighted
                    ? "#f1f3f5"
                    : "#ffffff";

            const fontWeight = isHighlighted ? "700" : "400";
            const valueColor = getDreValueColor(row, isLast);

            return `
            <tr style="background:${bg};font-weight:${fontWeight};">
                <td style="padding:8px;border:1px solid #d0d7de;">${escapeHtml(row.item)}</td>
                <td style="padding:8px;border:1px solid #d0d7de;text-align:right;color:${valueColor};">${escapeHtml(formatDreExportValue(row, isLast))}</td>
                <td style="padding:8px;border:1px solid #d0d7de;">${escapeHtml(formatDreTypeLabel(row.tipo, isLast))}</td>
            </tr>
        `;
        }).join("");

        const html = `﻿
        <html>
            <head>
                <meta charset="UTF-8" />
            </head>
            <body>
                <table border="1" style="border-collapse:collapse;font-family:Arial,sans-serif;font-size:13px;min-width:620px;">
                    <thead>
                        <tr>
                            <th colspan="3" style="padding:12px;background:#263238;color:#ffffff;font-size:16px;text-align:left;">
                                DRE gerencial - ${escapeHtml(monthLabel)}
                            </th>
                        </tr>
                        <tr style="background:#e9ecef;">
                            <th style="padding:8px;border:1px solid #d0d7de;text-align:left;">Item</th>
                            <th style="padding:8px;border:1px solid #d0d7de;text-align:right;">Valor</th>
                            <th style="padding:8px;border:1px solid #d0d7de;text-align:left;">Tipo</th>
                        </tr>
                    </thead>
                    <tbody>${htmlRows}</tbody>
                </table>
            </body>
        </html>
    `;

        downloadBlob(
            html,
            `dre-gerencial-${fileMonth}.xls`,
            "application/vnd.ms-excel;charset=utf-8"
        );
    }

    function downloadSaasDrePdf() {
        if (!saasHealth) return;

        const rows = buildSaasDreRowsForExport();
        const monthLabel = formatMonthRefBrLabel(saasHealthMonth);
        const fileMonth = formatMonthRefBr(saasHealthMonth);

        const tableRows = rows.map((row, idx) => {
            const { isHighlighted, isLast } = getDreExportRowStyle(row, idx, rows);

            const bg = isLast
                ? "#e9ecef"
                : isHighlighted
                    ? "#f6f8fa"
                    : "#ffffff";

            const fontWeight = isHighlighted ? "700" : "400";
            const valueColor = getDreValueColor(row, isLast);

            return `
            <tr style="background:${bg};font-weight:${fontWeight};">
                <td style="padding:9px 10px;border-bottom:1px solid #e0e0e0;">${escapeHtml(row.item)}</td>
                <td style="padding:9px 10px;border-bottom:1px solid #e0e0e0;text-align:right;color:${valueColor};white-space:nowrap;">${escapeHtml(formatDreExportValue(row, isLast))}</td>
                <td style="padding:9px 10px;border-bottom:1px solid #e0e0e0;">${escapeHtml(formatDreTypeLabel(row.tipo, isLast))}</td>
            </tr>
        `;
        }).join("");

        const html = `
        <div style="font-family:Arial,sans-serif;color:#263238;padding:18px;">
            <div style="margin-bottom:16px;">
                <h2 style="margin:0 0 4px 0;font-size:20px;">DRE gerencial do mês</h2>
                <p style="margin:0;color:#607d8b;font-size:12px;">
                    Período: ${escapeHtml(monthLabel)} • Visão gerencial, não contábil
                </p>
            </div>

            <table style="width:100%;border-collapse:collapse;font-size:12px;border:1px solid #d0d7de;border-radius:8px;overflow:hidden;">
                <thead>
                    <tr style="background:#263238;color:#ffffff;">
                        <th style="padding:10px;text-align:left;">Item</th>
                        <th style="padding:10px;text-align:right;">Valor</th>
                        <th style="padding:10px;text-align:left;">Tipo</th>
                    </tr>
                </thead>
                <tbody>
                    ${tableRows}
                </tbody>
            </table>

            <p style="margin-top:14px;color:#78909c;font-size:11px;">
                Exportado pelo painel administrativo da Docen.
            </p>
        </div>
    `;

        const element = document.createElement("div");
        element.innerHTML = html;

        html2pdf()
            .set({
                margin: 8,
                filename: `dre-gerencial-${fileMonth}.pdf`,
                image: { type: "jpeg", quality: 0.98 },
                html2canvas: { scale: 2, useCORS: true },
                jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
            })
            .from(element)
            .save();
    }

    // ==== RENDER ====

    return (
        <Box sx={{ minHeight: "100vh", bgcolor: "background.default", display: "flex" }}>
            <Drawer
                anchor="left"
                open={mobileNavOpen}
                onClose={() => setMobileNavOpen(false)}
                ModalProps={{ keepMounted: true }}
                PaperProps={{
                    sx: {
                        width: 304,
                        maxWidth: "86vw",
                        p: 2,
                        borderRadius: "0 28px 28px 0",
                        bgcolor: "background.paper",
                    },
                }}
                sx={{ display: { xs: "block", md: "none" } }}
            >
                <AdminNavigation
                    sections={adminSections}
                    activeValue={mainTab}
                    onChange={handleMainTabChange}
                    onLogout={logoutAdm}
                    collapsed={false}
                />
            </Drawer>

            <Box
                component="aside"
                sx={{
                    width: sidebarCollapsed ? ADMIN_SIDEBAR_COLLAPSED_WIDTH : ADMIN_SIDEBAR_WIDTH,
                    flexShrink: 0,
                    transition: theme.transitions.create("width", {
                        duration: theme.transitions.duration.shorter,
                    }),
                    display: { xs: "none", md: "block" },
                    p: 2,
                    position: "sticky",
                    top: 0,
                    height: "100vh",
                }}
            >
                <Paper
                    elevation={3}
                    sx={{
                        height: "100%",
                        borderRadius: 4,
                        p: sidebarCollapsed ? 1.25 : 2,
                        overflow: "hidden",
                        transition: theme.transitions.create("padding", {
                            duration: theme.transitions.duration.shorter,
                        }),
                        border: "1px solid",
                        borderColor: "divider",
                    }}
                >
                    <AdminNavigation
                        sections={adminSections}
                        activeValue={mainTab}
                        onChange={handleMainTabChange}
                        onLogout={logoutAdm}
                        collapsed={sidebarCollapsed}
                        onToggleCollapsed={() => setSidebarCollapsed((value) => !value)}
                        showCollapseToggle
                    />
                </Paper>
            </Box>

            <Box
                component="main"
                sx={{
                    flex: 1,
                    minWidth: 0,
                    py: { xs: 2, md: 3 },
                    px: { xs: 1.5, sm: 2, md: 3 },
                }}
            >
                <Box sx={{ maxWidth: 1400, mx: "auto" }}>

                    {/* HEADER */}
                    <Paper
                        elevation={3}
                        sx={{
                            mb: 3,
                            px: { xs: 1.5, sm: 2.5, md: 3 },
                            py: { xs: 1.5, sm: 2 },
                            borderRadius: { xs: 3, sm: 4 },
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 2,
                            border: "1px solid",
                            borderColor: "divider",
                            flexWrap: { xs: "wrap", md: "nowrap" },
                        }}
                    >
                        <Stack direction="row" alignItems="center" spacing={1.25} sx={{ minWidth: 0, flex: 1 }}>
                            <IconButton
                                onClick={() => setMobileNavOpen(true)}
                                sx={{ display: { xs: "inline-flex", md: "none" }, flexShrink: 0 }}
                                aria-label="Abrir menu administrativo"
                            >
                                <MenuRoundedIcon />
                            </IconButton>

                            <Box sx={{ minWidth: 0 }}>
                                <Typography variant={isMobile ? "h6" : "h5"} fontWeight={900} noWrap>
                                    Área Administrativa
                                </Typography>
                                <Typography variant="body2" color="text.secondary" noWrap>
                                    {activeSection?.description || "Central completa de gerenciamento da plataforma."}
                                </Typography>
                            </Box>
                        </Stack>

                        <Stack
                            direction="row"
                            alignItems="center"
                            justifyContent={{ xs: "flex-start", sm: "flex-end" }}
                            spacing={1}
                            useFlexGap
                            flexWrap="wrap"
                            sx={{ width: { xs: "100%", sm: "auto" }, flexShrink: 0 }}
                        >
                            <Chip
                                icon={activeSection?.icon}
                                label={activeSection?.label || "Admin"}
                                color="primary"
                                variant="filled"
                                sx={{
                                    flexShrink: 0,
                                    fontWeight: 800,
                                    display: { xs: "none", sm: "inline-flex" },
                                    "& .MuiChip-icon": { color: "inherit" },
                                }}
                            />
                        </Stack>
                    </Paper>

                    {/* MÓDULO 1 - USUÁRIOS (mantido) */}
                    {mainTab === 0 && (
                        <Paper elevation={3} sx={{ p: { xs: 2, sm: 3 }, borderRadius: 4 }}>
                            {/* Cabeçalho do módulo Usuários */}
                            <Box
                                display="flex"
                                alignItems={{ xs: "stretch", sm: "center" }}
                                justifyContent="space-between"
                                gap={2}
                                mb={2}
                                sx={{ flexDirection: { xs: "column", md: "row" } }}
                            >
                                <Typography variant="h5" fontWeight={800} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                    <IconButton size="large"><GroupRoundedIcon fontSize="large" color="primary" /></IconButton>
                                    Usuários
                                </Typography>

                                {/* Ações: responsivas e empilham no mobile */}
                                {isMobile ? (
                                    <Stack
                                        direction="row"
                                        alignItems="center"
                                        spacing={1}
                                        useFlexGap
                                        flexWrap="wrap"
                                        sx={{ width: { xs: "100%", md: "auto" } }}
                                    >
                                        {/* Recarregar */}
                                        <Tooltip title="Recarregar" sx={{ width: { xs: "100%", sm: "auto" } }}>
                                            <IconButton
                                                onClick={() => fetchRows(currentPage, rowsPerPage)}
                                                sx={{ width: { xs: "100%", sm: "auto" } }}
                                            >
                                                <RefreshIcon />
                                            </IconButton>
                                        </Tooltip>

                                        {/* Alterar senha */}
                                        <Button
                                            variant="outlined"
                                            startIcon={<PasswordIcon />}
                                            onClick={() => setDlgSenhaOpen(true)}
                                            fullWidth
                                            sx={{ width: { xs: "100%", sm: "auto" } }}
                                        >
                                            Alterar minha senha
                                        </Button>

                                        {/* Novo ADM (se master) */}
                                        {isMaster && (
                                            <Button
                                                variant="contained"
                                                startIcon={<PersonAddAlt1Icon />}
                                                onClick={() => setDlgNovoOpen(true)}
                                                fullWidth
                                                sx={{ width: { xs: "100%", sm: "auto" } }}
                                            >
                                                Novo ADM
                                            </Button>
                                        )}

                                        {/* Filtros */}
                                        <Button
                                            variant={filtersOpen ? "contained" : "outlined"}
                                            startIcon={<FilterListIcon />}
                                            endIcon={
                                                <ExpandMoreIcon
                                                    sx={{ transform: filtersOpen ? "rotate(180deg)" : "rotate(0deg)", transition: "0.2s" }}
                                                />
                                            }
                                            onClick={() => setFiltersOpen(v => !v)}
                                            fullWidth
                                            sx={{ width: { xs: "100%", sm: "auto" } }}
                                        >
                                            Filtros
                                        </Button>
                                    </Stack>
                                ) : (
                                    <Stack
                                        direction="row"
                                        alignItems="center"
                                        spacing={1}
                                        useFlexGap
                                        flexWrap="wrap"
                                        sx={{ width: { xs: "100%", md: "auto" } }}
                                    >
                                        <Tooltip title="Recarregar">
                                            <IconButton onClick={() => fetchRows(currentPage, rowsPerPage)} sx={{ width: { xs: "100%", sm: "auto" } }}>
                                                <RefreshIcon />
                                            </IconButton>
                                        </Tooltip>

                                        <Button
                                            variant={filtersOpen ? "contained" : "outlined"}
                                            startIcon={<FilterListIcon />}
                                            endIcon={<ExpandMoreIcon sx={{ transform: filtersOpen ? "rotate(180deg)" : "rotate(0deg)", transition: "0.2s" }} />}
                                            onClick={() => setFiltersOpen(v => !v)}
                                            fullWidth
                                            sx={{ width: { xs: "100%", sm: "auto" } }}
                                        >
                                            Filtros
                                        </Button>

                                        <Button
                                            variant="outlined"
                                            startIcon={<PasswordIcon />}
                                            onClick={() => setDlgSenhaOpen(true)}
                                            fullWidth
                                            sx={{ width: { xs: "100%", sm: "auto" } }}
                                        >
                                            Alterar minha senha
                                        </Button>

                                        {isMaster && (
                                            <Button
                                                variant="contained"
                                                startIcon={<PersonAddAlt1Icon />}
                                                onClick={() => setDlgNovoOpen(true)}
                                                fullWidth
                                                sx={{ width: { xs: "100%", sm: "auto" } }}
                                            >
                                                Novo ADM
                                            </Button>
                                        )}
                                    </Stack>
                                )}
                            </Box>

                            {/* Resumo dos filtros quando fechado */}
                            {!filtersOpen && (fEmail || fNome || fStatus || fPlano) && (
                                <Box mb={2}>{activeFilterChips}</Box>
                            )}

                            {/* Área de filtros retrátil */}
                            <Collapse in={filtersOpen} timeout="auto" unmountOnExit>
                                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, mb: 2 }}>
                                    <Grid container spacing={2} alignItems="center">
                                        <Grid item xs={12} md={4}>
                                            <TextField
                                                label="E-mail"
                                                fullWidth
                                                value={fEmail}
                                                onChange={(e) => setFEmail(e.target.value)}
                                            />
                                        </Grid>

                                        <Grid item xs={12} md={4}>
                                            <TextField
                                                label="Nome"
                                                fullWidth
                                                value={fNome}
                                                onChange={(e) => setFNome(e.target.value)}
                                            />
                                        </Grid>

                                        <Grid item xs={12} md={2}>
                                            <TextField
                                                select
                                                label="Status"
                                                fullWidth
                                                value={fStatus}
                                                onChange={(e) => setFStatus(e.target.value)}
                                            >
                                                {STATUS_OPTS.map(o => (
                                                    <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                                                ))}
                                            </TextField>
                                        </Grid>

                                        <Grid item xs={12} md={2}>
                                            <TextField
                                                select
                                                label="Plano"
                                                fullWidth
                                                value={fPlano}
                                                onChange={(e) => setFPlano(e.target.value)}
                                            >
                                                {PLANO_OPTS.map(o => (
                                                    <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                                                ))}
                                            </TextField>
                                        </Grid>

                                        <Grid item xs={12} md={2}>
                                            <TextField
                                                label="Data Adesão"
                                                placeholder="DD/MM/AAAA"
                                                fullWidth
                                                value={fDataAdesao}
                                                onChange={(e) => setFDataAdesao(maskDateInput(e.target.value))}
                                                inputProps={{ maxLength: 10, inputMode: "numeric" }}
                                            />
                                        </Grid>

                                        <Grid item xs={12} md={2} display="flex" gap={1}>
                                            <Button
                                                variant="contained"
                                                onClick={() => fetchRows(1, rowsPerPage)}
                                                disabled={loading}
                                                fullWidth
                                            >
                                                {loading ? <CircularProgress size={18} sx={{ color: "white" }} /> : "Aplicar"}
                                            </Button>
                                            <Button
                                                variant="outlined"
                                                onClick={handleClearFilters}
                                                disabled={loading}
                                                fullWidth
                                            >
                                                Limpar
                                            </Button>
                                        </Grid>
                                    </Grid>

                                    {(fEmail || fNome || fStatus || fPlano) && (
                                        <Box mt={2}>{activeFilterChips}</Box>
                                    )}
                                </Paper>
                            </Collapse>

                            {/* Lista responsiva: Cards no mobile, Tabela no desktop */}
                            {isMobile ? (
                                <Stack spacing={1.5} mt={3}>
                                    {rows.length === 0 && !loading && (
                                        <Typography variant="body2" color="text.secondary" align="center">Sem resultados.</Typography>
                                    )}
                                    {paginatedRows.map((r) => (
                                        <MobileUserCard
                                            key={r.id}
                                            r={r}
                                            copy={copy}
                                            onSetPartner={handleSetUserAsPartner}
                                            partnerUpdatingId={partnerUpdatingId}
                                            isMaster={isMaster}
                                        />
                                    ))}
                                </Stack>
                            ) : (
                                <Box sx={{
                                    width: "100%",
                                    overflowX: "auto",
                                    border: (theme) => `1px solid ${theme.palette.divider}`,
                                    borderRadius: 2,
                                    mt: 5
                                }}>
                                    <table style={{ width: "100%", borderCollapse: "collapse" }}>
                                        <thead>
                                            <tr>
                                                {[
                                                    { label: "Nome", align: "left" },
                                                    { label: "E-mail", align: "left" },
                                                    { label: "Celular", align: "center" },
                                                    { label: "Cadastro", align: "center" },
                                                    { label: "Status", align: "center" },
                                                    { label: "Plano", align: "center" },
                                                    { label: "Adesão", align: "center" },
                                                    { label: "Gerações Usadas", align: "center" }
                                                ].map((col) => (
                                                    <th
                                                        key={col.label}
                                                        style={{
                                                            textAlign: col.align,
                                                            padding: "12px 16px",
                                                            background: "rgba(0,0,0,0.04)",
                                                            borderBottom: "1px solid rgba(0,0,0,0.12)",
                                                            position: "sticky",
                                                            top: 0,
                                                            zIndex: 1,
                                                            backdropFilter: "blur(2px)",
                                                        }}
                                                    >
                                                        {col.label}
                                                    </th>
                                                ))}
                                            </tr>
                                        </thead>

                                        <tbody>
                                            {rows.length === 0 && !loading && (
                                                <tr>
                                                    <td colSpan={8} style={{ padding: 24, textAlign: "center", color: "#666" }}>
                                                        Sem resultados.
                                                    </td>
                                                </tr>
                                            )}
                                            {paginatedRows.map((r) => (
                                                <tr key={r.id} style={{ borderBottom: "1px solid rgba(0,0,0,0.12)" }}>
                                                    <td style={{ padding: "12px 16px" }}>
                                                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                                                            <Box sx={{ display: isMaster ? "flex" : "none" }}>
                                                                <Tooltip
                                                                    title={
                                                                        r.status === "B"
                                                                            ? "Usuário parceiro"
                                                                            : !isMaster
                                                                                ? "Apenas ADMIN MASTER pode definir como parceiro"
                                                                                : r.status === "G"
                                                                                    ? "Definir como parceiro"
                                                                                    : "Disponível apenas para usuários em teste"
                                                                    }
                                                                >
                                                                    <span>
                                                                        <IconButton
                                                                            size="small"
                                                                            onClick={() => handleSetUserAsPartner(r)}
                                                                            disabled={!isMaster || r.status !== "G" || partnerUpdatingId === r.id}
                                                                            color={r.status === "B" ? "secondary" : "default"}
                                                                        >
                                                                            {partnerUpdatingId === r.id ? <CircularProgress size={16} /> : <HandshakeIcon fontSize="small" />}
                                                                        </IconButton>
                                                                    </span>
                                                                </Tooltip>
                                                            </Box>
                                                            <Box>
                                                                <span>{r.name || "-"}</span>
                                                            </Box>
                                                        </Box>
                                                    </td>
                                                    <td style={{ padding: "6px 12px" }} align="left">
                                                        {!!r.email && (
                                                            <Tooltip title="Copiar E-mail">
                                                                <IconButton
                                                                    onClick={() => copy(r.email, "E-mail")}
                                                                    size="small"
                                                                    sx={{ mr: 0.5 }}
                                                                >
                                                                    <ContentCopyIcon fontSize="inherit" />
                                                                </IconButton>
                                                            </Tooltip>
                                                        )}

                                                        <span
                                                            style={{
                                                                display: "inline-block",
                                                                maxWidth: 150,          // ajuste conforme seu layout
                                                                whiteSpace: "nowrap",
                                                                overflow: "hidden",
                                                                textOverflow: "ellipsis",
                                                                verticalAlign: "middle",
                                                            }}
                                                            title={r.email}             // fallback nativo
                                                        >
                                                            {r.email || "-"}
                                                        </span>
                                                    </td>


                                                    <td style={{ padding: "6px 12px", whiteSpace: "nowrap" }} align="center">
                                                        {r.phone && (
                                                            <Tooltip title="Abrir WhatsApp">
                                                                <IconButton
                                                                    size="small"
                                                                    component="a"
                                                                    href={`https://wa.me/${r.phone.replace(/\D/g, '')}`}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                >
                                                                    <WhatsAppIcon fontSize="small" sx={{ color: "#25D366" }} />
                                                                </IconButton>
                                                            </Tooltip>
                                                        )}

                                                        {r.phone || "-"}
                                                    </td>

                                                    <td style={{ padding: "5px 10px" }} align="center">
                                                        {r.createdAt ? new Date(r.createdAt).toLocaleDateString('pt-BR') : "-"}
                                                    </td>
                                                    <td style={{ padding: "12px 16px", display: "none" }}>{(r.state && r.city) ? `${r.state} / ${r.city}` : "-"}</td>
                                                    <td style={{ padding: "12px 16px" }} align="center">
                                                        {r.status ? (
                                                            <Chip
                                                                size="small"
                                                                label={STATUS_LABEL[r.status] || r.status}
                                                                color={STATUS_COLOR[r.status] || "default"}
                                                                sx={getStatusChipSx(r.status)}
                                                                variant="filled"
                                                            />
                                                        ) : "-"}
                                                    </td>
                                                    <td style={{ padding: "12px 16px" }} align="center">
                                                        {r.status === "A" && r.plan ? (
                                                            <Chip
                                                                size="small"
                                                                label={formatPlano(r.plan)}
                                                                sx={(theme) => ({
                                                                    bgcolor: PLAN_STYLES[r.plan]?.bg(theme) || "action.selected",
                                                                    color: PLAN_STYLES[r.plan]?.fg(theme) || theme.palette.text.primary,
                                                                    fontWeight: 600,
                                                                })}
                                                            />
                                                        ) : "-"}
                                                    </td>


                                                    <td style={{ padding: "5px 10px" }} align="center">
                                                        {r.subscribedAt ? new Date(r.subscribedAt).toLocaleDateString('pt-BR') : "-"}
                                                    </td>

                                                    <td style={{ padding: "12px 16px" }} align="center">
                                                        {(() => {
                                                            const total = totalPorPlano(r);
                                                            const usado = Number(r.usedGeneration || 0);
                                                            if (!total) return "-";
                                                            const pct = Math.max(0, Math.min(100, Math.round((usado / total) * 100)));
                                                            return (
                                                                <Box sx={{ minWidth: 100 }}>
                                                                    <Box display="flex" justifyContent="space-between" mb={0.5}>
                                                                        <Typography variant="caption" color="text.secondary">Uso</Typography>
                                                                        <Typography variant="caption" fontWeight={600}>{usado} / {total}</Typography>
                                                                    </Box>
                                                                    <LinearProgress
                                                                        variant="determinate"
                                                                        value={pct}
                                                                        sx={{
                                                                            height: 8,
                                                                            borderRadius: 999,
                                                                            "& .MuiLinearProgress-bar": { borderRadius: 999 },
                                                                        }}
                                                                    />
                                                                </Box>
                                                            );
                                                        })()}
                                                    </td>

                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </Box>
                            )}

                            {/* Rodapé com total + paginação */}
                            <PaginationControls
                                page={currentPage}
                                total={total}
                                rowsPerPage={rowsPerPage}
                                loading={loading}
                                onPageChange={(nextPage) => fetchRows(nextPage, rowsPerPage)}
                                onRowsPerPageChange={(nextLimit) => fetchRows(1, nextLimit)}
                            />
                        </Paper>
                    )}

                    {/* MÓDULO 2 - SAÚDE SAAS (DRE, MRR, CAC ...) */}
                    {mainTab === 1 && isMaster && (
                        <Paper elevation={3} sx={{ p: { xs: 2, sm: 3 }, borderRadius: 4 }}>
                            <Box
                                display="flex"
                                alignItems={{ xs: "stretch", sm: "center" }}
                                justifyContent="space-between"
                                gap={2}
                                mb={2}
                                sx={{ flexDirection: { xs: "column", md: "row" } }}
                            >
                                <Box>
                                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                                        <Typography variant="h5" fontWeight={900} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                            <IconButton size="large"><AssessmentRoundedIcon fontSize="large" color="primary" /></IconButton>
                                            Saúde do SaaS
                                        </Typography>
                                        <SaasStatusChip status={saasHealth?.status} />
                                    </Stack>
                                    <Typography variant="body2" color="text.secondary">
                                        DRE gerencial mensal com receita, custos de IA, taxas, CAC, churn e margem.
                                    </Typography>
                                </Box>

                                <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ xs: "stretch", sm: "center" }}>
                                    <TextField
                                        label="Mês"
                                        type="month"
                                        size="small"
                                        value={saasHealthMonth}
                                        onChange={(e) => setSaasHealthMonth(e.target.value)}
                                        InputLabelProps={{ shrink: true }}
                                    />
                                    <Button
                                        variant={saasHealthInputsOpen ? "contained" : "outlined"}
                                        startIcon={<FilterListIcon />}
                                        onClick={() => setSaasHealthInputsOpen((value) => !value)}
                                    >
                                        Premissas
                                    </Button>
                                    <Button
                                        variant="contained"
                                        startIcon={saasHealthLoading ? <CircularProgress size={16} sx={{ color: "white" }} /> : <RefreshIcon />}
                                        disabled={saasHealthLoading}
                                        onClick={() => fetchSaasHealth()}
                                    >
                                        {saasHealthLoading ? "Calculando..." : "Atualizar"}
                                    </Button>
                                </Stack>
                            </Box>

                            <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
                                Este painel é gerencial, não substitui a contabilidade oficial. Receita dos assinantes vem automaticamente. Custos fixos, impostos e taxas são premissas editáveis.
                            </Alert>

                            <Collapse in={saasHealthInputsOpen} timeout="auto" unmountOnExit>
                                <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, mb: 2 }}>
                                    <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" alignItems={{ xs: "stretch", md: "center" }} spacing={1.5} mb={2}>
                                        <Box>
                                            <Typography variant="subtitle1" fontWeight={900}>Premissas do mês</Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                Ajuste os valores para simular a realidade do mês. Eles ficam salvos no navegador.
                                            </Typography>
                                        </Box>
                                        <Button variant="outlined" size="small" onClick={resetSaasHealthInputs}>
                                            Restaurar padrão
                                        </Button>
                                    </Stack>

                                    <SaasGrid columns={{ xs: 1, sm: 2, md: 4 }}>
                                        <Grid item xs={12} sm={6} md={3}>
                                            <SaasNumberInput
                                                label="Dólar automático"
                                                value={Number(saasHealthInputs.usdToBrlReference || 0).toFixed(2)}
                                                onChange={(v) => updateSaasHealthInput("usdToBrlReference", v)}
                                                suffix="R$"
                                                disabled
                                                helperText="Buscado automaticamente via AwesomeAPI."
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={3}>
                                            <SaasNumberInput label="Imposto estimado" value={saasHealthInputs.taxPercent} onChange={(v) => updateSaasHealthInput("taxPercent", v)} suffix="%" />
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={3}>
                                            <SaasNumberInput label="Asaas PIX" value={saasHealthInputs.asaasPixFixedFeeBrl} onChange={(v) => updateSaasHealthInput("asaasPixFixedFeeBrl", v)} suffix="R$ fixo" />
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={3}>
                                            <SaasNumberInput label="Asaas cartão (%)" value={saasHealthInputs.asaasCreditCardPercent} onChange={(v) => updateSaasHealthInput("asaasCreditCardPercent", v)} suffix="%" />
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={3}>
                                            <SaasNumberInput label="Asaas cartão fixo" value={saasHealthInputs.asaasCreditCardFixedFeeBrl} onChange={(v) => updateSaasHealthInput("asaasCreditCardFixedFeeBrl", v)} suffix="R$" />
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={3} display="none">
                                            <SaasNumberInput label="Asaas boleto" value={saasHealthInputs.asaasBoletoFixedFeeBrl} onChange={(v) => updateSaasHealthInput("asaasBoletoFixedFeeBrl", v)} suffix="R$" />
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={3}>
                                            <SaasNumberInput label="Custos fixos" value={saasHealthInputs.fixedCostsBrl} onChange={(v) => updateSaasHealthInput("fixedCostsBrl", v)} suffix="R$" helperText="Render, Bunny, contabilidade etc." />
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={3}>
                                            <SaasNumberInput label="Marketing" value={saasHealthInputs.marketingCostsBrl} onChange={(v) => updateSaasHealthInput("marketingCostsBrl", v)} suffix="R$" />
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={3}>
                                            <SaasNumberInput label="Anúncios" value={saasHealthInputs.adsCostsBrl} onChange={(v) => updateSaasHealthInput("adsCostsBrl", v)} suffix="R$" />
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={3}>
                                            <SaasNumberInput label="Ferramentas" value={saasHealthInputs.toolsCostsBrl} onChange={(v) => updateSaasHealthInput("toolsCostsBrl", v)} suffix="R$" />
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={3}>
                                            <SaasNumberInput label="Outros custos" value={saasHealthInputs.otherCostsBrl} onChange={(v) => updateSaasHealthInput("otherCostsBrl", v)} suffix="R$" />
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={3} display="flex" alignItems="center">
                                            <Button fullWidth variant="contained" onClick={() => fetchSaasHealth()} disabled={saasHealthLoading}>
                                                Aplicar no painel
                                            </Button>
                                        </Grid>
                                    </SaasGrid>
                                </Paper>
                            </Collapse>

                            {!saasHealth && !saasHealthLoading ? (
                                <Paper variant="outlined" sx={{ p: 3, borderRadius: 3, textAlign: "center" }}>
                                    <Typography variant="body2" color="text.secondary">
                                        Clique em atualizar para calcular a saúde do SaaS no mês selecionado.
                                    </Typography>
                                </Paper>
                            ) : (
                                <Stack spacing={2.5}>
                                    <SaasGrid columns={{ xs: 1, sm: 2, md: 4 }}>
                                        <Grid item xs={12} sm={6} md={3}>
                                            <SaasMetricCard
                                                title="MRR atual"
                                                value={formatAdminBrl(saasHealth?.summary?.mrr_brl)}
                                                helper={`Receita recorrente mensal dos assinantes ativos (${saasHealth?.subscribers?.active_subscribers || 0}).`}
                                                icon={<TrendingUpRoundedIcon />}
                                                color="primary"
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={3}>
                                            <SaasMetricCard
                                                title="Receita recebida"
                                                value={formatAdminBrl(saasHealth?.summary?.gross_revenue_brl)}
                                                helper={`Líquida: ${formatAdminBrl(saasHealth?.summary?.net_revenue_brl)}`}
                                                icon={<AttachMoneyRoundedIcon />}
                                                color="success"
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={3}>
                                            <SaasMetricCard
                                                title="Custo total com IA"
                                                value={formatAdminBrl(saasHealth?.summary?.total_llm_cost_brl)}
                                                helper={`Docs: ${formatAdminBrl(saasHealth?.summary?.pedagogical_llm_cost_brl)} | Agente: ${formatAdminBrl(saasHealth?.summary?.whatsapp_agent_llm_cost_brl)}`}
                                                icon={<SmartToyRoundedIcon />}
                                                color="secondary"
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={3}>
                                            <SaasMetricCard
                                                title="Resultado gerencial"
                                                value={formatAdminBrl(saasHealth?.summary?.operating_result_brl)}
                                                helper={`Margem: ${formatAdminPercent(saasHealth?.summary?.operating_margin_percent)}`}
                                                icon={<AccountBalanceWalletRoundedIcon />}
                                                color="success"
                                                severity={Number(saasHealth?.summary?.operating_result_brl || 0) >= 0 ? "success" : "error"}
                                            />
                                        </Grid>
                                    </SaasGrid>

                                    <SaasGrid columns={{ xs: 1, sm: 2, md: 4 }}>
                                        <Grid item xs={12} sm={6} md={3}>
                                            <SaasMetricCard
                                                title="Churn do mês"
                                                value={formatAdminPercent(saasHealth?.subscribers?.churn_percent)}
                                                helper={`Cancelamentos / base inicial: ${saasHealth?.subscribers?.canceled_subscribers || 0} / ${saasHealth?.subscribers?.active_at_start || 0}.`}
                                                icon={<QueryStatsRoundedIcon />}
                                                color="warning"
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={3}>
                                            <SaasMetricCard
                                                title="CAC simples"
                                                value={formatAdminBrl(saasHealth?.subscribers?.cac_brl)}
                                                helper={`Custo de aquisição: anúncios / novos assinantes (${saasHealth?.subscribers?.new_subscribers || 0}).`}
                                                icon={<ReceiptLongRoundedIcon />}
                                                color="primary"
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={3}>
                                            <SaasMetricCard
                                                title="Payback estimado"
                                                value={`${Number(saasHealth?.subscribers?.payback_months || 0).toFixed(1).replace(".", ",")} mês(es)`}
                                                helper={`Tempo para recuperar o CAC. Margem média/user: ${formatAdminBrl(saasHealth?.subscribers?.avg_gross_margin_per_active_user_brl)}.`}
                                                icon={<TrendingUpRoundedIcon />}
                                                color="success"
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={3}>
                                            <SaasMetricCard
                                                title="Ponto de equilíbrio"
                                                value={`${Number(saasHealth?.subscribers?.break_even_subscribers || 0).toLocaleString("pt-BR")} assinantes`}
                                                helper="Estimativa usando margem bruta média atual"
                                                icon={<GroupRoundedIcon />}
                                                color="secondary"
                                            />
                                        </Grid>
                                    </SaasGrid>

                                    <SaasGrid templateColumns={{ xs: "1fr", lg: "5fr 7fr" }}>
                                        <Grid item xs={12} lg={5}>
                                            <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, height: "100%" }}>
                                                <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} mb={1}>
                                                    <Typography variant="h6" fontWeight={900}>DRE gerencial do mês</Typography>

                                                </Stack>
                                                <Typography variant="caption" color="text.secondary" display="block" mb={2}>
                                                    Visão simples para saber quanto entrou, quanto custou e o resultado estimado.
                                                </Typography>

                                                <Stack spacing={0.75}>
                                                    {(saasHealth?.dre || []).map((row, idx) => {
                                                        const isTotal = idx === (saasHealth?.dre || []).length - 1;
                                                        const isNegative = row.type === "negative";
                                                        const isPositive = row.type === "positive";
                                                        return (
                                                            <Box
                                                                key={`${row.label}-${idx}`}
                                                                sx={{
                                                                    display: "flex",
                                                                    justifyContent: "space-between",
                                                                    alignItems: "center",
                                                                    gap: 1,
                                                                    px: 1.25,
                                                                    py: 1,
                                                                    borderRadius: 2,
                                                                    bgcolor: isTotal ? "action.selected" : row.type === "subtotal" ? "action.hover" : "transparent",
                                                                    border: isTotal ? "1px solid" : "none",
                                                                    borderColor: isTotal ? "divider" : "transparent",
                                                                }}
                                                            >
                                                                <Typography variant="body2" fontWeight={isTotal || row.type === "subtotal" ? 900 : 600}>
                                                                    {row.label}
                                                                </Typography>
                                                                <Typography
                                                                    variant="body2"
                                                                    fontWeight={900}
                                                                    color={isNegative ? "error.main" : isPositive ? "success.main" : "text.primary"}
                                                                    sx={{ whiteSpace: "nowrap" }}
                                                                >
                                                                    {isNegative ? "- " : ""}{formatAdminBrl(row.value_brl)}
                                                                </Typography>
                                                            </Box>
                                                        );
                                                    })}
                                                </Stack>

                                                <Divider sx={{ mt: 2, mb: 1.5 }} />

                                                <Stack
                                                    direction="row"
                                                    spacing={0.8}
                                                    justifyContent="center"
                                                    alignItems="center"
                                                    flexWrap="wrap"
                                                >
                                                    <Tooltip title="Baixar DRE em Excel">
                                                        <span>
                                                            <Button
                                                                size="small"
                                                                variant="outlined"
                                                                startIcon={<TableChartRoundedIcon fontSize="small" />}
                                                                onClick={downloadSaasDreExcel}
                                                                disabled={!saasHealth}
                                                                sx={{
                                                                    color: "#107C41",
                                                                    borderColor: "#107C41",
                                                                    fontWeight: 800,
                                                                    textTransform: "none",
                                                                    "& .MuiButton-startIcon": {
                                                                        color: "#107C41",
                                                                    },
                                                                    "&:hover": {
                                                                        borderColor: "#0E6F3A",
                                                                        bgcolor: "rgba(16, 124, 65, 0.08)",
                                                                    },
                                                                }}
                                                            >
                                                                Excel
                                                            </Button>
                                                        </span>
                                                    </Tooltip>

                                                    <Tooltip title="Baixar DRE em PDF">
                                                        <span>
                                                            <Button
                                                                size="small"
                                                                variant="outlined"
                                                                startIcon={<PictureAsPdfRoundedIcon fontSize="small" />}
                                                                onClick={downloadSaasDrePdf}
                                                                disabled={!saasHealth}
                                                                sx={{
                                                                    color: "#D32F2F",
                                                                    borderColor: "#D32F2F",
                                                                    fontWeight: 800,
                                                                    textTransform: "none",
                                                                    "& .MuiButton-startIcon": {
                                                                        color: "#D32F2F",
                                                                    },
                                                                    "&:hover": {
                                                                        borderColor: "#B71C1C",
                                                                        bgcolor: "rgba(211, 47, 47, 0.08)",
                                                                    },
                                                                }}
                                                            >
                                                                PDF
                                                            </Button>
                                                        </span>
                                                    </Tooltip>
                                                </Stack>
                                            </Paper>
                                        </Grid>

                                        <Grid item xs={12} lg={7}>
                                            <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, height: "100%" }}>
                                                <Typography variant="h6" fontWeight={900} mb={1}>Margem por plano</Typography>
                                                <Typography variant="caption" color="text.secondary" display="block" mb={2}>
                                                    MRR dos assinantes ativos menos taxas Asaas e custo LLM de documentos. Sem impostos neste bloco.
                                                </Typography>

                                                <Box sx={{ width: "100%", overflowX: "auto" }}>
                                                    <table style={{ width: "100%", borderCollapse: "collapse" }}>
                                                        <thead>
                                                            <tr>
                                                                {["Plano", "Ativos", "MRR", "Taxa Asaas", "LLM", "Receita", "Margem", "%"].map(col => (
                                                                    <th key={col} style={{ textAlign: "left", padding: "10px 12px", background: "rgba(0,0,0,0.04)", borderBottom: "1px solid rgba(0,0,0,0.12)" }}>{col}</th>
                                                                ))}
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {(saasHealth?.planEconomics || []).map((row) => (
                                                                <tr key={row.plan_name} style={{ borderBottom: "1px solid rgba(0,0,0,0.08)" }}>
                                                                    <td style={{ padding: "10px 12px" }}><PlanChip plan={row.plan_name} /></td>
                                                                    <td style={{ padding: "10px 12px" }}><CountChip value={row.active_users} /></td>
                                                                    <td style={{ padding: "10px 12px" }}>{formatAdminBrl(row.mrr_brl)}</td>
                                                                    <td style={{ padding: "10px 12px" }}>{formatAdminBrl(row.asaas_fees_brl)}</td>
                                                                    <td style={{ padding: "10px 12px" }}>{formatAdminBrl(row.pedagogical_llm_cost_brl)}</td>
                                                                    <td style={{ padding: "10px 12px" }}>{formatAdminBrl(row.net_revenue_after_asaas_brl ?? row.gross_revenue_brl)}</td>
                                                                    <td style={{ padding: "10px 12px" }}>
                                                                        <Chip
                                                                            size="small"
                                                                            label={formatAdminBrl(row.gross_margin_brl)}
                                                                            color={Number(row.gross_margin_brl || 0) >= 0 ? "success" : "error"}
                                                                            sx={{ fontWeight: 900 }}
                                                                        />
                                                                    </td>
                                                                    <td style={{ padding: "10px 12px" }}>{formatAdminPercent(row.gross_margin_percent)}</td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </Box>
                                            </Paper>
                                        </Grid>
                                    </SaasGrid>

                                    <SaasGrid columns={{ xs: 1, lg: 3 }}>
                                        <Grid item xs={12} lg={4}>
                                            <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, height: "100%" }}>
                                                <Typography variant="h6" fontWeight={900} mb={2}>Recebimentos e taxas</Typography>
                                                <Stack spacing={1}>
                                                    {(saasHealth?.paymentMethods || []).map((row) => (
                                                        <Box key={row.method} sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
                                                            <Stack direction="row" spacing={1} alignItems="center">
                                                                <Chip size="small" label={row.label} variant="outlined" sx={{ fontWeight: 800 }} />
                                                                <Typography variant="caption" color="text.secondary">{row.count} pagamento(s)</Typography>
                                                            </Stack>
                                                            <Box textAlign="right">
                                                                <Typography variant="body2" fontWeight={900}>{formatAdminBrl(row.gross_revenue_brl)}</Typography>
                                                                <Typography variant="caption" color="error">Taxa: {formatAdminBrl(row.estimated_fee_brl)}</Typography>
                                                            </Box>
                                                        </Box>
                                                    ))}
                                                </Stack>
                                            </Paper>
                                        </Grid>

                                        <Grid item xs={12} lg={4}>
                                            <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, height: "100%" }}>
                                                <Typography variant="h6" fontWeight={900} mb={2}>Uso de IA</Typography>
                                                <Stack spacing={1.25}>
                                                    <Box display="flex" justifyContent="space-between" gap={1}>
                                                        <Typography variant="body2">Documentos gerados</Typography>
                                                        <Chip size="small" label={Number(saasHealth?.usage?.pedagogical_generations || 0).toLocaleString("pt-BR")} color="primary" variant="outlined" />
                                                    </Box>
                                                    <Box display="flex" justifyContent="space-between" gap={1}>
                                                        <Typography variant="body2">Tokens documentos</Typography>
                                                        <Chip size="small" label={Number(saasHealth?.usage?.pedagogical_tokens || 0).toLocaleString("pt-BR")} color="secondary" variant="outlined" />
                                                    </Box>
                                                    <Box display="flex" justifyContent="space-between" gap={1}>
                                                        <Typography variant="body2">Imagens geradas</Typography>
                                                        <Chip size="small" label={Number(saasHealth?.usage?.generated_images || 0).toLocaleString("pt-BR")} color="warning" variant="outlined" />
                                                    </Box>
                                                    <Divider />
                                                    <Box display="flex" justifyContent="space-between" gap={1}>
                                                        <Typography variant="body2">Interações agente IA</Typography>
                                                        <Chip size="small" label={Number(saasHealth?.usage?.whatsapp_agent_interactions || 0).toLocaleString("pt-BR")} color="success" variant="outlined" />
                                                    </Box>
                                                    <Box display="flex" justifyContent="space-between" gap={1}>
                                                        <Typography variant="body2">Tokens agente IA</Typography>
                                                        <Chip size="small" label={Number(saasHealth?.usage?.whatsapp_agent_tokens || 0).toLocaleString("pt-BR")} color="success" variant="outlined" />
                                                    </Box>
                                                </Stack>
                                            </Paper>
                                        </Grid>

                                        <Grid item xs={12} lg={4}>
                                            <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, height: "100%" }}>
                                                <Typography variant="h6" fontWeight={900} mb={2}>Custos manuais aplicados</Typography>
                                                <Stack spacing={1}>
                                                    <Box display="flex" justifyContent="space-between"><Typography variant="body2">Fixos</Typography><b>{formatAdminBrl(saasHealth?.costs?.fixed_costs_brl)}</b></Box>
                                                    <Box display="flex" justifyContent="space-between"><Typography variant="body2">Marketing</Typography><b>{formatAdminBrl(saasHealth?.costs?.marketing_costs_brl)}</b></Box>
                                                    <Box display="flex" justifyContent="space-between"><Typography variant="body2">Anúncios</Typography><b>{formatAdminBrl(saasHealth?.costs?.ads_costs_brl)}</b></Box>
                                                    <Box display="flex" justifyContent="space-between"><Typography variant="body2">Ferramentas</Typography><b>{formatAdminBrl(saasHealth?.costs?.tools_costs_brl)}</b></Box>
                                                    <Box display="flex" justifyContent="space-between"><Typography variant="body2">Outros</Typography><b>{formatAdminBrl(saasHealth?.costs?.other_costs_brl)}</b></Box>
                                                    <Divider />
                                                    <Box display="flex" justifyContent="space-between"><Typography variant="body2" fontWeight={900}>Total</Typography><Typography variant="body2" fontWeight={900}>{formatAdminBrl(saasHealth?.summary?.operating_costs_brl)}</Typography></Box>
                                                </Stack>
                                            </Paper>
                                        </Grid>
                                    </SaasGrid>
                                </Stack>
                            )}
                        </Paper>
                    )}

                    {/* MÓDULO 3 - BALANÇO IA (TOKENS/CUSTOS LLMs) */}
                    {mainTab === 2 && isMaster && (
                        <Paper elevation={3} sx={{ p: { xs: 2, sm: 3 }, borderRadius: 4 }}>
                            <Box
                                display="flex"
                                alignItems={{ xs: "stretch", sm: "center" }}
                                justifyContent="space-between"
                                gap={2}
                                mb={2}
                                sx={{ flexDirection: { xs: "column", md: "row" } }}
                            >
                                <Typography
                                    variant="h5"
                                    fontWeight={800}
                                    sx={{ display: "flex", alignItems: "center", gap: 1 }}
                                >
                                    <IconButton size="large">
                                        <SmartToyRoundedIcon fontSize="large" color="primary" />
                                    </IconButton>
                                    Balanço da IA
                                </Typography>

                                <Stack
                                    direction={{ xs: "column", sm: "row" }}
                                    spacing={1}
                                    alignItems={{ xs: "stretch", sm: "center" }}
                                    sx={{ width: { xs: "100%", sm: "auto" } }}
                                >
                                    <Tooltip title="Recarregar balanço">
                                        <IconButton
                                            onClick={() => fetchIABalanco()}
                                            sx={{ width: { xs: "100%", sm: "auto" } }}
                                        >
                                            <RefreshIcon />
                                        </IconButton>
                                    </Tooltip>

                                    <Button
                                        variant={iaFiltersOpen ? "contained" : "outlined"}
                                        startIcon={<FilterListIcon />}
                                        endIcon={
                                            <ExpandMoreIcon
                                                sx={{
                                                    transform: iaFiltersOpen ? "rotate(180deg)" : "rotate(0deg)",
                                                    transition: "0.2s"
                                                }}
                                            />
                                        }
                                        onClick={() => setIaFiltersOpen(v => !v)}
                                        fullWidth
                                        sx={{ width: { xs: "100%", sm: "auto" } }}
                                    >
                                        Filtros
                                    </Button>
                                </Stack>
                            </Box>

                            <Collapse in={iaFiltersOpen} timeout="auto" unmountOnExit>
                                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, mb: 2 }}>
                                    <Grid container spacing={2} alignItems="center">
                                        <Grid item xs={12} md={3}>
                                            <TextField
                                                label="Data inicial"
                                                type="date"
                                                fullWidth
                                                value={iaStartDate}
                                                onChange={(e) => setIaStartDate(e.target.value)}
                                                InputLabelProps={{ shrink: true }}
                                            />
                                        </Grid>

                                        <Grid item xs={12} md={3}>
                                            <TextField
                                                label="Data final"
                                                type="date"
                                                fullWidth
                                                value={iaEndDate}
                                                onChange={(e) => setIaEndDate(e.target.value)}
                                                InputLabelProps={{ shrink: true }}
                                            />
                                        </Grid>

                                        <Grid item xs={12} md={3}>
                                            <TextField
                                                select
                                                label="Repair"
                                                fullWidth
                                                value={iaTeveRepair}
                                                onChange={(e) => setIaTeveRepair(e.target.value)}
                                            >
                                                <MenuItem value="">Todos</MenuItem>
                                                <MenuItem value="1">Somente com repair</MenuItem>
                                                <MenuItem value="0">Somente sem repair</MenuItem>
                                            </TextField>
                                        </Grid>

                                        <Grid item xs={12} md={3}>
                                            <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                                                <Button
                                                    variant="contained"
                                                    onClick={() => fetchIABalanco({ dailyPage: 1, monthlyPage: 1, generationsPage: 1 })}
                                                    disabled={iaLoading}
                                                    fullWidth
                                                >
                                                    {iaLoading ? <CircularProgress size={18} sx={{ color: "white" }} /> : "Aplicar"}
                                                </Button>
                                                <Button
                                                    variant="outlined"
                                                    onClick={clearIaFilters}
                                                    disabled={iaLoading}
                                                    fullWidth
                                                >
                                                    Limpar
                                                </Button>
                                            </Stack>
                                        </Grid>
                                    </Grid>
                                </Paper>
                            </Collapse>

                            <Grid container spacing={2} mb={2}>
                                <Grid item xs={12} sm={6} md={3}>
                                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: "100%" }}>
                                        <Stack spacing={1}>
                                            <Stack direction="row" spacing={1} alignItems="center">
                                                <QueryStatsRoundedIcon color="primary" />
                                                <Typography variant="subtitle2" color="text.secondary">Total de gerações</Typography>
                                            </Stack>
                                            <Typography variant="h5" fontWeight={800}>
                                                {iaResumo?.totalGenerations ?? 0}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                Imagens geradas: {iaResumo?.totalImages ?? 0}
                                            </Typography>
                                        </Stack>
                                    </Paper>
                                </Grid>

                                <Grid item xs={12} sm={6} md={3}>
                                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: "100%" }}>
                                        <Stack spacing={1}>
                                            <Stack direction="row" spacing={1} alignItems="center">
                                                <AttachMoneyRoundedIcon color="success" />
                                                <Typography variant="subtitle2" color="text.secondary">Custo total</Typography>
                                            </Stack>
                                            <Typography variant="h5" fontWeight={800}>
                                                US$ {Number(iaResumo?.totalCostUsd || 0).toFixed(4)}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {formatAdminBrl(Number(iaResumo?.totalCostUsd || 0) * iaUsdToBrl)} • câmbio {Number(iaUsdToBrl || 5).toFixed(2)}
                                            </Typography>
                                        </Stack>
                                    </Paper>
                                </Grid>

                                <Grid item xs={12} sm={6} md={3}>
                                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: "100%" }}>
                                        <Stack spacing={1}>
                                            <Stack direction="row" spacing={1} alignItems="center">
                                                <SmartToyRoundedIcon color="secondary" />
                                                <Typography variant="subtitle2" color="text.secondary">Tokens totais</Typography>
                                            </Stack>
                                            <Typography variant="h5" fontWeight={800}>
                                                {Number(iaResumo?.totalTokens || 0).toLocaleString("pt-BR")}
                                            </Typography>
                                        </Stack>
                                    </Paper>
                                </Grid>

                                <Grid item xs={12} sm={6} md={3}>
                                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: "100%" }}>
                                        <Stack spacing={1}>
                                            <Stack direction="row" spacing={1} alignItems="center">
                                                <BugReportRoundedIcon color="warning" />
                                                <Typography variant="subtitle2" color="text.secondary">Gerações com repair</Typography>
                                            </Stack>
                                            <Typography variant="h5" fontWeight={800}>
                                                {iaResumo?.totalRepairs ?? 0}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                Média por geração: US$ {Number(iaResumo?.avgCostUsd || 0).toFixed(4)}
                                            </Typography>
                                        </Stack>
                                    </Paper>
                                </Grid>
                            </Grid>

                            <Grid container spacing={2}>
                                <Grid item xs={12} lg={6}>
                                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: "100%" }}>
                                        <Typography variant="h6" fontWeight={800} mb={2}>
                                            Fechamento diário
                                        </Typography>

                                        <Box sx={{ width: "100%", overflowX: "auto" }}>
                                            <table style={{ width: "100%", borderCollapse: "collapse" }}>
                                                <thead>
                                                    <tr>
                                                        {["Dia", "Gerações", "Imagens", "Tokens", "Custo USD", "Repairs"].map(col => (
                                                            <th
                                                                key={col}
                                                                style={{
                                                                    textAlign: "left",
                                                                    padding: "10px 12px",
                                                                    background: "rgba(0,0,0,0.04)",
                                                                    borderBottom: "1px solid rgba(0,0,0,0.12)"
                                                                }}
                                                            >
                                                                {col}
                                                            </th>
                                                        ))}
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {(iaDiario || []).length === 0 && !iaLoading && (
                                                        <tr>
                                                            <td colSpan={6} style={{ padding: 16, textAlign: "center", color: "#666" }}>
                                                                Sem dados no período.
                                                            </td>
                                                        </tr>
                                                    )}

                                                    {(iaDiario || []).map((r, i) => (
                                                        <tr key={i} style={{ borderBottom: "1px solid rgba(0,0,0,0.08)" }}>
                                                            <td style={{ padding: "10px 12px" }}>{formatAdminDate(r.date)}</td>
                                                            <td style={{ padding: "10px 12px" }}>{r.totalGenerations}</td>
                                                            <td style={{ padding: "10px 12px" }}>{Number(r.totalImages || 0)}</td>
                                                            <td style={{ padding: "10px 12px" }}>{Number(r.totalTokens || 0).toLocaleString("pt-BR")}</td>
                                                            <td style={{ padding: "10px 12px" }}><MoneyChip value={r.totalCostUsd} /></td>
                                                            <td style={{ padding: "10px 12px" }}>{r.totalRepairs}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </Box>

                                        <PaginationControls
                                            page={iaDiarioPage}
                                            total={iaDiarioTotal}
                                            rowsPerPage={iaDiarioRowsPerPage}
                                            loading={iaLoading}
                                            rowsPerPageOptions={[5, 10, 20, 50]}
                                            layout="stacked-left"
                                            onPageChange={(nextPage) => fetchIABalanco({ dailyPage: nextPage })}
                                            onRowsPerPageChange={(nextLimit) => fetchIABalanco({ dailyPage: 1, dailyLimit: nextLimit })}
                                        />
                                    </Paper>
                                </Grid>

                                <Grid item xs={12} lg={6}>
                                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: "100%" }}>
                                        <Typography variant="h6" fontWeight={800} mb={2}>
                                            Fechamento mensal
                                        </Typography>

                                        <Box sx={{ width: "100%", overflowX: "auto" }}>
                                            <table style={{ width: "100%", borderCollapse: "collapse" }}>
                                                <thead>
                                                    <tr>
                                                        {["Mês", "Gerações", "Imagens", "Tokens", "Custo USD", "Média USD"].map(col => (
                                                            <th
                                                                key={col}
                                                                style={{
                                                                    textAlign: "left",
                                                                    padding: "10px 12px",
                                                                    background: "rgba(0,0,0,0.04)",
                                                                    borderBottom: "1px solid rgba(0,0,0,0.12)"
                                                                }}
                                                            >
                                                                {col}
                                                            </th>
                                                        ))}
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {(iaMensal || []).length === 0 && !iaLoading && (
                                                        <tr>
                                                            <td colSpan={6} style={{ padding: 16, textAlign: "center", color: "#666" }}>
                                                                Sem dados mensais.
                                                            </td>
                                                        </tr>
                                                    )}

                                                    {(iaMensal || []).map((r, i) => (
                                                        <tr key={i} style={{ borderBottom: "1px solid rgba(0,0,0,0.08)" }}>
                                                            <td style={{ padding: "10px 12px" }}>{formatAdminMonth(r.month)}</td>
                                                            <td style={{ padding: "10px 12px" }}>{r.totalGenerations}</td>
                                                            <td style={{ padding: "10px 12px" }}>{Number(r.totalImages || 0)}</td>
                                                            <td style={{ padding: "10px 12px" }}>{Number(r.totalTokens || 0).toLocaleString("pt-BR")}</td>
                                                            <td style={{ padding: "10px 12px" }}><MoneyChip value={r.totalCostUsd} /></td>
                                                            <td style={{ padding: "10px 12px" }}>US$ {Number(r.avgCostUsd || 0).toFixed(4)}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </Box>

                                        <PaginationControls
                                            page={iaMensalPage}
                                            total={iaMensalTotal}
                                            rowsPerPage={iaMensalRowsPerPage}
                                            loading={iaLoading}
                                            rowsPerPageOptions={[5, 10, 20, 50]}
                                            layout="stacked-left"
                                            onPageChange={(nextPage) => fetchIABalanco({ monthlyPage: nextPage })}
                                            onRowsPerPageChange={(nextLimit) => fetchIABalanco({ monthlyPage: 1, monthlyLimit: nextLimit })}
                                        />
                                    </Paper>
                                </Grid>

                                <Grid item xs={12}>
                                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                                        <Typography variant="h6" fontWeight={800} mb={2}>
                                            Últimas gerações
                                        </Typography>

                                        <Box sx={{ width: "100%", overflowX: "auto" }}>
                                            <table style={{ width: "100%", borderCollapse: "collapse" }}>
                                                <thead>
                                                    <tr>
                                                        {[
                                                            "Data",
                                                            "Usuário",
                                                            "Plano",
                                                            "Modelo",
                                                            "Tipo",
                                                            "Questões",
                                                            "Imagens",
                                                            "Tokens principal",
                                                            "Tokens repair",
                                                            "Custo total",
                                                            "Repair",
                                                            "Issues"
                                                        ].map(col => (
                                                            <th
                                                                key={col}
                                                                style={{
                                                                    textAlign: "left",
                                                                    padding: "10px 12px",
                                                                    background: "rgba(0,0,0,0.04)",
                                                                    borderBottom: "1px solid rgba(0,0,0,0.12)",
                                                                    ...(col === "Issues" ? { width: 320, maxWidth: 320 } : {})
                                                                }}
                                                            >
                                                                {col}
                                                            </th>
                                                        ))}
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {(iaLista || []).length === 0 && !iaLoading && (
                                                        <tr>
                                                            <td colSpan={12} style={{ padding: 16, textAlign: "center", color: "#666" }}>
                                                                Nenhuma geração encontrada.
                                                            </td>
                                                        </tr>
                                                    )}

                                                    {iaPaginatedRows.map((r) => (
                                                        <tr key={r.id} style={{ borderBottom: "1px solid rgba(0,0,0,0.08)" }}>
                                                            <td style={{ padding: "10px 12px", whiteSpace: "nowrap" }}>
                                                                {r.createdAt ? new Date(r.createdAt).toLocaleString("pt-BR") : "-"}
                                                            </td>
                                                            <td style={{ padding: "10px 12px", minWidth: 220 }}>
                                                                <Typography variant="body2" fontWeight={700}>{compactText(r.userName)}</Typography>
                                                                <Typography variant="caption" color="text.secondary">{compactText(r.userEmail, r.userId ? `ID ${r.userId}` : "Sem user_id")}</Typography>
                                                            </td>
                                                            <td style={{ padding: "10px 12px" }}><PlanChip plan={r.planName} /></td>
                                                            <td style={{ padding: "10px 12px" }}>{r.model || "-"}</td>
                                                            <td style={{ padding: "10px 12px" }}>{r.contentType || "-"}</td>
                                                            <td style={{ padding: "10px 12px" }}>{r.questionCount || "-"}</td>
                                                            <td style={{ padding: "10px 12px" }}><CountChip value={r.images || 0} color="secondary" /></td>
                                                            <td style={{ padding: "10px 12px" }}>{Number(r.mainTotalTokens || 0).toLocaleString("pt-BR")}</td>
                                                            <td style={{ padding: "10px 12px" }}>{Number(r.repairTotalTokens || 0).toLocaleString("pt-BR")}</td>
                                                            <td style={{ padding: "10px 12px" }}><MoneyChip value={r.totalCostUsd} /></td>
                                                            <td style={{ padding: "10px 12px" }}>
                                                                <Chip
                                                                    size="small"
                                                                    label={r.hadRepair ? "Sim" : "Não"}
                                                                    color={r.hadRepair ? "warning" : "success"}
                                                                />
                                                            </td>
                                                            <td
                                                                style={{
                                                                    padding: "10px 12px",
                                                                    width: 320,
                                                                    maxWidth: 320,
                                                                    verticalAlign: "top"
                                                                }}
                                                            >
                                                                <Box
                                                                    sx={{
                                                                        width: 320,
                                                                        maxWidth: 320,
                                                                        overflowX: "auto",
                                                                        whiteSpace: "nowrap",
                                                                        display: "block",
                                                                        pb: 0.5
                                                                    }}
                                                                >
                                                                    {Array.isArray(r.repairIssues) && r.repairIssues.length > 0
                                                                        ? r.repairIssues.slice(0, 3).join(" | ")
                                                                        : "-"}
                                                                </Box>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </Box>

                                        <PaginationControls
                                            page={iaCurrentPage}
                                            total={iaListaTotal}
                                            rowsPerPage={iaRowsPerPage}
                                            loading={iaLoading}
                                            rowsPerPageOptions={[10, 20, 50]}
                                            onPageChange={(nextPage) => fetchIABalanco({ generationsPage: nextPage })}
                                            onRowsPerPageChange={(nextLimit) => fetchIABalanco({ generationsPage: 1, generationsLimit: nextLimit })}
                                        />

                                        {!!iaResumo?.topIssues?.length && (
                                            <Box mt={2}>
                                                <Typography variant="subtitle2" fontWeight={800} mb={1}>
                                                    Issues mais frequentes no repair
                                                </Typography>
                                                <Stack direction="row" gap={1} flexWrap="wrap">
                                                    {iaResumo.topIssues.map((it, idx) => (
                                                        <Chip
                                                            key={idx}
                                                            label={`${it.issue} (${it.quantity ?? it.count ?? it.quantidade ?? 0})`}
                                                            size="small"
                                                            variant="outlined"
                                                        />
                                                    ))}
                                                </Stack>
                                            </Box>
                                        )}
                                    </Paper>
                                </Grid>
                            </Grid>

                            <Divider sx={{ my: 2.5 }} />

                            <Stack
                                direction={{ xs: "column", sm: "row" }}
                                alignItems={{ xs: "flex-start", sm: "center" }}
                                justifyContent="space-between"
                                spacing={1.5}
                                mb={2}
                            >
                                <Box>
                                    <Typography variant="h6" fontWeight={900}>
                                        Análises avançadas de custos
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Recortes por plano, usuário, tipo de documento e disciplina.
                                    </Typography>
                                </Box>

                                <Chip
                                    icon={<QueryStatsRoundedIcon />}
                                    label="Indicadores de rentabilidade"
                                    color="primary"
                                    variant="outlined"
                                    sx={{ fontWeight: 800, "& .MuiChip-icon": { color: "inherit" } }}
                                />
                            </Stack>

                            <Grid container spacing={2} mb={2}>
                                <Grid item xs={12} lg={6}>
                                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: "100%" }}>
                                        <Typography variant="h6" fontWeight={800} mb={1}>
                                            Rentabilidade por plano
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary" display="block" mb={2}>
                                            Margem estimada usando câmbio atual de R$ {Number(iaUsdToBrl || iaResumo?.usdToBrlReference || 5).toFixed(2)} por dólar.
                                        </Typography>

                                        <Box sx={{ width: "100%", overflowX: "auto" }}>
                                            <table style={{ width: "100%", borderCollapse: "collapse" }}>
                                                <thead>
                                                    <tr>
                                                        {["Plano", "Usuários", "Gerações", "Custo", "Custo/user", "Média/geração", "Receita", "Margem", "Margem/user"].map(col => (
                                                            <th key={col} style={{ textAlign: "left", padding: "10px 12px", background: "rgba(0,0,0,0.04)", borderBottom: "1px solid rgba(0,0,0,0.12)" }}>{col}</th>
                                                        ))}
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {iaPlanRows.length === 0 && !iaLoading && (
                                                        <tr>
                                                            <td colSpan={9} style={{ padding: 16, textAlign: "center", color: "#666" }}>
                                                                Sem dados por plano no período.
                                                            </td>
                                                        </tr>
                                                    )}
                                                    {paginateClientRows(iaPlanRows, iaPlanPage, iaPlanRowsPerPage).map((r, idx) => (
                                                        <tr key={`${r.plan_name || r.planName}-${idx}`} style={{ borderBottom: "1px solid rgba(0,0,0,0.08)" }}>
                                                            <td style={{ padding: "10px 12px" }}><PlanChip plan={r.plan_name ?? r.planName} /></td>
                                                            <td style={{ padding: "10px 12px" }}><CountChip value={r.active_users ?? r.activeUsers ?? r.total_users ?? 0} /></td>
                                                            <td style={{ padding: "10px 12px" }}><CountChip value={r.total_generations ?? r.totalGenerations ?? 0} /></td>
                                                            <td style={{ padding: "10px 12px" }}><MoneyChip value={r.total_cost_usd ?? r.totalCostUsd} /></td>
                                                            <td style={{ padding: "10px 12px" }}><MoneyChip value={r.avg_cost_per_user_usd ?? r.avgCostPerUserUsd} /></td>
                                                            <td style={{ padding: "10px 12px" }}><MoneyChip value={r.avg_cost_usd ?? r.avgCostUsd} /></td>
                                                            <td style={{ padding: "10px 12px" }}><MoneyChip value={r.estimated_revenue_brl ?? r.estimatedRevenueBrl} currency="brl" /></td>
                                                            <td style={{ padding: "10px 12px" }}><MoneyChip value={r.estimated_margin_brl ?? r.estimatedMarginBrl} currency="brl" /></td>
                                                            <td style={{ padding: "10px 12px" }}><MoneyChip value={r.estimated_margin_per_active_user_brl ?? r.estimatedMarginPerActiveUserBrl} currency="brl" /></td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </Box>

                                        <PaginationControls
                                            page={iaPlanPage}
                                            total={iaPlanRows.length}
                                            rowsPerPage={iaPlanRowsPerPage}
                                            loading={iaLoading}
                                            rowsPerPageOptions={[5, 10, 20]}
                                            layout="stacked-left"
                                            onPageChange={(nextPage) => setIaPlanPage(nextPage)}
                                            onRowsPerPageChange={(nextLimit) => {
                                                setIaPlanRowsPerPage(nextLimit);
                                                setIaPlanPage(1);
                                            }}
                                        />
                                    </Paper>
                                </Grid>

                                <Grid item xs={12} lg={6}>
                                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: "100%" }}>
                                        <Typography variant="h6" fontWeight={800} mb={2}>
                                            Usuários com maior custo LLM
                                        </Typography>

                                        <Box sx={{ width: "100%", overflowX: "auto" }}>
                                            <table style={{ width: "100%", borderCollapse: "collapse" }}>
                                                <thead>
                                                    <tr>
                                                        {["Usuário", "Plano", "Gerações", "Imagens", "Custo", "Média"].map(col => (
                                                            <th key={col} style={{ textAlign: "left", padding: "10px 12px", background: "rgba(0,0,0,0.04)", borderBottom: "1px solid rgba(0,0,0,0.12)" }}>{col}</th>
                                                        ))}
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {iaTopUserRows.length === 0 && !iaLoading && (
                                                        <tr>
                                                            <td colSpan={6} style={{ padding: 16, textAlign: "center", color: "#666" }}>
                                                                Sem usuários identificados no período.
                                                            </td>
                                                        </tr>
                                                    )}
                                                    {paginateClientRows(iaTopUserRows, iaTopUsersPage, iaTopUsersRowsPerPage).map((r, idx) => (
                                                        <tr key={`${r.user_id || r.userId || "user"}-${idx}`} style={{ borderBottom: "1px solid rgba(0,0,0,0.08)" }}>
                                                            <td style={{ padding: "10px 12px", minWidth: 220 }}>
                                                                <Typography variant="body2" fontWeight={700}>{compactText(r.name)}</Typography>
                                                                <Typography variant="caption" color="text.secondary">{compactText(r.email, r.user_id || r.userId ? `ID ${r.user_id ?? r.userId}` : "Sem user_id")}</Typography>
                                                            </td>
                                                            <td style={{ padding: "10px 12px" }}><PlanChip plan={r.plan_name ?? r.planName} /></td>
                                                            <td style={{ padding: "10px 12px" }}><CountChip value={r.total_generations ?? r.totalGenerations ?? 0} /></td>
                                                            <td style={{ padding: "10px 12px" }}><CountChip value={r.total_images ?? r.totalImages ?? 0} color="secondary" /></td>
                                                            <td style={{ padding: "10px 12px" }}><MoneyChip value={r.total_cost_usd ?? r.totalCostUsd} /></td>
                                                            <td style={{ padding: "10px 12px" }}><MoneyChip value={r.avg_cost_usd ?? r.avgCostUsd} /></td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </Box>

                                        <PaginationControls
                                            page={iaTopUsersPage}
                                            total={iaTopUserRows.length}
                                            rowsPerPage={iaTopUsersRowsPerPage}
                                            loading={iaLoading}
                                            rowsPerPageOptions={[5, 10, 20]}
                                            layout="stacked-left"
                                            onPageChange={(nextPage) => setIaTopUsersPage(nextPage)}
                                            onRowsPerPageChange={(nextLimit) => {
                                                setIaTopUsersRowsPerPage(nextLimit);
                                                setIaTopUsersPage(1);
                                            }}
                                        />
                                    </Paper>
                                </Grid>
                            </Grid>

                            <Grid container spacing={2} mb={2}>
                                <Grid item xs={12} lg={6}>
                                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: "100%" }}>
                                        <Typography variant="h6" fontWeight={800} mb={2}>Custo por tipo de documento</Typography>
                                        <Box sx={{ width: "100%", overflowX: "auto" }}>
                                            <table style={{ width: "100%", borderCollapse: "collapse" }}>
                                                <thead><tr>{["Tipo", "Gerações", "Custo", "Média"].map(col => <th key={col} style={{ textAlign: "left", padding: "10px 12px", background: "rgba(0,0,0,0.04)", borderBottom: "1px solid rgba(0,0,0,0.12)" }}>{col}</th>)}</tr></thead>
                                                <tbody>
                                                    {iaContentTypeRows.length === 0 && <tr><td colSpan={4} style={{ padding: 16, textAlign: "center", color: "#666" }}>Sem dados.</td></tr>}
                                                    {paginateClientRows(iaContentTypeRows, iaContentTypePage, iaContentTypeRowsPerPage).map((r, idx) => <tr key={`${r.label}-${idx}`} style={{ borderBottom: "1px solid rgba(0,0,0,0.08)" }}><td style={{ padding: "10px 12px" }}>{compactText(r.label)}</td><td style={{ padding: "10px 12px" }}><CountChip value={r.total_generations ?? 0} /></td><td style={{ padding: "10px 12px" }}><MoneyChip value={r.total_cost_usd} /></td><td style={{ padding: "10px 12px" }}><MoneyChip value={r.avg_cost_usd} /></td></tr>)}
                                                </tbody>
                                            </table>
                                        </Box>

                                        <PaginationControls
                                            page={iaContentTypePage}
                                            total={iaContentTypeRows.length}
                                            rowsPerPage={iaContentTypeRowsPerPage}
                                            loading={iaLoading}
                                            rowsPerPageOptions={[5, 10, 20]}
                                            layout="stacked-left"
                                            onPageChange={(nextPage) => setIaContentTypePage(nextPage)}
                                            onRowsPerPageChange={(nextLimit) => {
                                                setIaContentTypeRowsPerPage(nextLimit);
                                                setIaContentTypePage(1);
                                            }}
                                        />
                                    </Paper>
                                </Grid>

                                <Grid item xs={12} lg={6}>
                                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: "100%" }}>
                                        <Typography variant="h6" fontWeight={800} mb={2}>Custo por disciplina</Typography>
                                        <Box sx={{ width: "100%", overflowX: "auto" }}>
                                            <table style={{ width: "100%", borderCollapse: "collapse" }}>
                                                <thead><tr>{["Disciplina", "Gerações", "Custo", "Média"].map(col => <th key={col} style={{ textAlign: "left", padding: "10px 12px", background: "rgba(0,0,0,0.04)", borderBottom: "1px solid rgba(0,0,0,0.12)" }}>{col}</th>)}</tr></thead>
                                                <tbody>
                                                    {iaSubjectRows.length === 0 && <tr><td colSpan={4} style={{ padding: 16, textAlign: "center", color: "#666" }}>Sem dados.</td></tr>}
                                                    {paginateClientRows(iaSubjectRows, iaSubjectPage, iaSubjectRowsPerPage).map((r, idx) => <tr key={`${r.label}-${idx}`} style={{ borderBottom: "1px solid rgba(0,0,0,0.08)" }}><td style={{ padding: "10px 12px" }}>{compactText(r.label)}</td><td style={{ padding: "10px 12px" }}><CountChip value={r.total_generations ?? 0} /></td><td style={{ padding: "10px 12px" }}><MoneyChip value={r.total_cost_usd} /></td><td style={{ padding: "10px 12px" }}><MoneyChip value={r.avg_cost_usd} /></td></tr>)}
                                                </tbody>
                                            </table>
                                        </Box>

                                        <PaginationControls
                                            page={iaSubjectPage}
                                            total={iaSubjectRows.length}
                                            rowsPerPage={iaSubjectRowsPerPage}
                                            loading={iaLoading}
                                            rowsPerPageOptions={[5, 10, 20]}
                                            layout="stacked-left"
                                            onPageChange={(nextPage) => setIaSubjectPage(nextPage)}
                                            onRowsPerPageChange={(nextLimit) => {
                                                setIaSubjectRowsPerPage(nextLimit);
                                                setIaSubjectPage(1);
                                            }}
                                        />
                                    </Paper>
                                </Grid>

                            </Grid>

                        </Paper>
                    )}

                    {/* MÓDULO 4 - AUTOMAÇÃO (AI AGENT) */}
                    {mainTab === 3 && isMaster && (
                        <Paper elevation={3} sx={{ p: { xs: 2, sm: 3 }, borderRadius: 4 }}>
                            <Box
                                display="flex"
                                alignItems={{ xs: "stretch", sm: "center" }}
                                justifyContent="space-between"
                                gap={2}
                                mb={2}
                                sx={{ flexDirection: { xs: "column", md: "row" } }}
                            >
                                <Box>
                                    <Typography variant="h5" fontWeight={800} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                        <IconButton size="large"><WhatsAppIcon fontSize="large" color="success" /></IconButton>
                                        Automação
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Controle do agente IA e início do CRM de conversas do WhatsApp.
                                    </Typography>
                                </Box>

                                <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ xs: "stretch", sm: "center" }}>
                                    <Button
                                        variant="outlined"
                                        startIcon={<RefreshIcon />}
                                        disabled={automationLoading}
                                        onClick={() => fetchAutomationDashboard({ page: 1 })}
                                    >
                                        Recarregar
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        color={automationSettings?.enabled ? "success" : "error"}
                                        startIcon={automationSettings?.enabled ? <ToggleOnIcon /> : <ToggleOffIcon />}
                                        disabled={automationUpdating || !automationSettings}
                                        onClick={() => updateAutomationSettings({ enabled: !automationSettings?.enabled })}
                                    >
                                        {automationUpdating ? "Atualizando..." : automationSettings?.enabled ? "Agente ligado" : "Agente desligado"}
                                    </Button>
                                </Stack>
                            </Box>

                            <Alert severity={automationSettings?.enabled ? "success" : "warning"} sx={{ mb: 2, borderRadius: 2 }}>
                                {automationSettings?.enabled
                                    ? "O agente está ligado e poderá responder novos leads recebidos pelo webhook do WhatsApp."
                                    : "O agente está desligado. As mensagens recebidas serão registradas, mas não serão respondidas automaticamente."}
                            </Alert>

                            <Grid container spacing={2} mb={2}>
                                <Grid item xs={12} sm={6} md={3}>
                                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: "100%" }}>
                                        <Stack spacing={1}>
                                            <Typography variant="subtitle2" color="text.secondary">Custo hoje</Typography>
                                            <Typography variant="h5" fontWeight={900}>{formatAdminUsd(automationBalance?.todayCostUsd)}</Typography>
                                            <Typography variant="caption" color="text.secondary">{formatAdminBrl(automationBalance?.todayCostBrl)}</Typography>
                                        </Stack>
                                    </Paper>
                                </Grid>
                                <Grid item xs={12} sm={6} md={3}>
                                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: "100%" }}>
                                        <Stack spacing={1}>
                                            <Typography variant="subtitle2" color="text.secondary">Custo mensal</Typography>
                                            <Typography variant="h5" fontWeight={900}>{formatAdminUsd(automationBalance?.monthCostUsd)}</Typography>
                                            <Typography variant="caption" color="text.secondary">{formatAdminBrl(automationBalance?.monthCostBrl)}</Typography>
                                        </Stack>
                                    </Paper>
                                </Grid>
                                <Grid item xs={12} sm={6} md={3}>
                                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: "100%" }}>
                                        <Stack spacing={1}>
                                            <Typography variant="subtitle2" color="text.secondary">Tokens no mês</Typography>
                                            <Typography variant="h5" fontWeight={900}>{Number(automationBalance?.monthTokens || 0).toLocaleString("pt-BR")}</Typography>
                                            <Typography variant="caption" color="text.secondary">{automationBalance?.answeredInteractions || 0} respostas automáticas</Typography>
                                        </Stack>
                                    </Paper>
                                </Grid>
                                <Grid item xs={12} sm={6} md={3}>
                                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: "100%" }}>
                                        <Stack spacing={1}>
                                            <Typography variant="subtitle2" color="text.secondary">Câmbio usado</Typography>
                                            <Typography variant="h5" fontWeight={900}>R$ {Number(automationBalance?.usdToBrl || 5).toFixed(2)}</Typography>
                                            <Typography variant="caption" color="text.secondary">{automationBalance?.usdToBrlSource || "AwesomeAPI"}</Typography>
                                        </Stack>
                                    </Paper>
                                </Grid>
                            </Grid>

                            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, mb: 2 }}>
                                <Grid container spacing={2} alignItems="center">
                                    <Grid item xs={12} md={5}>
                                        <TextField
                                            label="Buscar por nome, telefone ou resumo"
                                            fullWidth
                                            value={automationSearch}
                                            onChange={(e) => setAutomationSearch(e.target.value)}
                                            onKeyDown={(e) => {
                                                if (e.key === "Enter") fetchAutomationConversations({ page: 1, search: automationSearch });
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={3}>
                                        <TextField
                                            type="number"
                                            label="Limite de respostas por conversa"
                                            fullWidth
                                            value={automationSettings?.maxMessagesPerConversation ?? 8}
                                            inputProps={{ min: 1, max: 30 }}
                                            onChange={(e) => setAutomationSettings((prev) => ({ ...(prev || {}), maxMessagesPerConversation: Number(e.target.value) }))}
                                            onBlur={(e) => updateAutomationSettings({ maxMessagesPerConversation: Number(e.target.value) || 8 })}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                                            <Button variant="contained" onClick={() => fetchAutomationConversations({ page: 1, search: automationSearch })} disabled={automationLoading} fullWidth>
                                                Buscar
                                            </Button>
                                            <Button variant="outlined" onClick={() => { setAutomationSearch(""); fetchAutomationConversations({ page: 1, search: "" }); }} disabled={automationLoading} fullWidth>
                                                Limpar
                                            </Button>
                                        </Stack>
                                    </Grid>
                                </Grid>
                            </Paper>

                            <Box sx={{ width: "100%", overflowX: "auto" }}>
                                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                                    <thead>
                                        <tr>
                                            {["Lead", "Status", "Última mensagem", "Entrega", "Resumo", "Atualizado", "Ações"].map(col => (
                                                <th key={col} style={{ textAlign: "left", padding: "10px 12px", background: "rgba(0,0,0,0.04)", borderBottom: "1px solid rgba(0,0,0,0.12)" }}>{col}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {automationConversations.length === 0 && !automationLoading && (
                                            <tr><td colSpan={7} style={{ padding: 16, textAlign: "center", color: "#666" }}>Nenhuma conversa registrada.</td></tr>
                                        )}
                                        {automationConversations.map((conversation) => (
                                            <tr key={conversation.id} style={{ borderBottom: "1px solid rgba(0,0,0,0.08)" }}>
                                                <td style={{ padding: "10px 12px" }}>
                                                    <Stack spacing={0.3}>
                                                        <Typography fontWeight={800}>{conversation.name || "Lead sem nome"}</Typography>
                                                        <Typography variant="caption" color="text.secondary">{conversation.phone}</Typography>
                                                    </Stack>
                                                </td>
                                                <td style={{ padding: "10px 12px" }}>
                                                    <Stack direction="row" gap={0.75} flexWrap="wrap">
                                                        <Chip size="small" label={conversation.status === "open" ? "Aberta" : conversation.status} color="primary" variant="outlined" />
                                                        {conversation.humanTakeover && <Chip size="small" label="Humano" color="warning" />}
                                                        {conversation.agentEnabled && <Chip size="small" label="IA" color="success" />}
                                                    </Stack>
                                                </td>
                                                <td style={{ padding: "10px 12px", maxWidth: 260 }}>
                                                    <Typography variant="body2" noWrap>{conversation.lastMessage || "-"}</Typography>
                                                </td>
                                                <td style={{ padding: "10px 12px" }}>
                                                    {conversation.lastMessageDirection === "outbound" ? (
                                                        <WhatsappDeliveryChip
                                                            status={conversation.lastMessageDeliveryStatus}
                                                            errorCode={conversation.lastMessageDeliveryErrorCode}
                                                            errorTitle={conversation.lastMessageDeliveryErrorTitle}
                                                        />
                                                    ) : "-"}
                                                </td>
                                                <td style={{ padding: "10px 12px", maxWidth: 320 }}>
                                                    <Typography variant="body2" color="text.secondary" noWrap>{conversation.memorySummary || "-"}</Typography>
                                                </td>
                                                <td style={{ padding: "10px 12px" }}>{formatDateTime(conversation.lastMessageAt || conversation.createdAt)}</td>
                                                <td style={{ padding: "10px 12px" }}>
                                                    <Stack direction="row" spacing={1}>
                                                        <Button size="small" variant="outlined" onClick={() => openAutomationConversation(conversation.id)}>Ver</Button>
                                                        <Button size="small" variant="contained" color="success" href={buildWhatsappUrl(conversation.phone)} target="_blank" rel="noreferrer" startIcon={<WhatsAppIcon />}>WhatsApp</Button>
                                                    </Stack>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </Box>

                            <PaginationControls
                                page={automationPage}
                                total={automationTotal}
                                rowsPerPage={automationRowsPerPage}
                                loading={automationLoading}
                                rowsPerPageOptions={[10, 20, 50, 100]}
                                onPageChange={(nextPage) => fetchAutomationConversations({ page: nextPage })}
                                onRowsPerPageChange={(nextLimit) => fetchAutomationConversations({ page: 1, limit: nextLimit })}
                            />

                            <Dialog open={automationDialogOpen} onClose={() => setAutomationDialogOpen(false)} fullWidth maxWidth="md">
                                <DialogTitle>Conversa WhatsApp</DialogTitle>
                                <DialogContent dividers>
                                    {automationConversationLoading && <Box textAlign="center" py={4}><CircularProgress /></Box>}
                                    {!automationConversationLoading && automationSelectedConversation && (
                                        <Stack spacing={2}>
                                            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                                                <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" gap={1}>
                                                    <Box>
                                                        <Typography fontWeight={900}>{automationSelectedConversation.contact?.name || "Lead sem nome"}</Typography>
                                                        <Typography variant="body2" color="text.secondary">{automationSelectedConversation.contact?.phone}</Typography>
                                                    </Box>
                                                    <Button variant="contained" color="success" href={buildWhatsappUrl(automationSelectedConversation.contact?.phone)} target="_blank" rel="noreferrer" startIcon={<WhatsAppIcon />}>
                                                        Abrir WhatsApp
                                                    </Button>
                                                </Stack>
                                            </Paper>

                                            {automationSelectedConversation.conversation?.memory_summary && (
                                                <Alert severity="info" sx={{ borderRadius: 2 }}>
                                                    {automationSelectedConversation.conversation.memory_summary}
                                                </Alert>
                                            )}

                                            <Stack spacing={1.2}>
                                                {(automationSelectedConversation.messages || []).map((message) => {
                                                    const isOutbound = message.direction === "outbound";
                                                    return (
                                                        <Box key={message.id} sx={{ display: "flex", justifyContent: isOutbound ? "flex-end" : "flex-start" }}>
                                                            <Paper
                                                                variant="outlined"
                                                                sx={{
                                                                    p: 1.5,
                                                                    borderRadius: 3,
                                                                    maxWidth: "78%",
                                                                    bgcolor: isOutbound ? "success.light" : "background.paper",
                                                                }}
                                                            >
                                                                <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>{message.content}</Typography>
                                                                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" mt={0.75}>
                                                                    <Typography variant="caption" color="text.secondary">{formatDateTime(message.createdAt)}</Typography>
                                                                    {isOutbound && (
                                                                        <WhatsappDeliveryChip
                                                                            status={message.deliveryStatus}
                                                                            errorCode={message.deliveryErrorCode}
                                                                            errorTitle={message.deliveryErrorTitle}
                                                                        />
                                                                    )}
                                                                </Stack>
                                                                {isOutbound && message.deliveryStatus === "failed" && (
                                                                    <Alert severity="error" sx={{ mt: 1, borderRadius: 2 }}>
                                                                        <Typography variant="caption" fontWeight={800}>Falha na entrega{message.deliveryErrorCode ? ` (${message.deliveryErrorCode})` : ""}</Typography>
                                                                        <Typography variant="caption" display="block">{message.deliveryErrorTitle || message.deliveryErrorMessage || "A Meta não entregou a mensagem."}</Typography>
                                                                        {message.deliveryErrorMessage && message.deliveryErrorMessage !== message.deliveryErrorTitle && (
                                                                            <Typography variant="caption" display="block">{message.deliveryErrorMessage}</Typography>
                                                                        )}
                                                                    </Alert>
                                                                )}
                                                            </Paper>
                                                        </Box>
                                                    );
                                                })}
                                            </Stack>
                                        </Stack>
                                    )}
                                </DialogContent>
                                <DialogActions>
                                    <Button onClick={() => setAutomationDialogOpen(false)}>Fechar</Button>
                                </DialogActions>
                            </Dialog>
                        </Paper>
                    )}

                    {/* MÓDULO 5 - AVALIAÇÕES */}
                    {mainTab === 4 && isMaster && (
                        <Paper elevation={3} sx={{ p: { xs: 2, sm: 3 }, borderRadius: 4 }}>
                            <Box
                                display="flex"
                                alignItems={{ xs: "stretch", sm: "center" }}
                                justifyContent="space-between"
                                gap={2}
                                mb={2}
                                sx={{ flexDirection: { xs: "column", md: "row" } }}
                            >
                                <Typography
                                    variant="h5"
                                    fontWeight={800}
                                    sx={{ display: "flex", alignItems: "center", gap: 1 }}
                                >
                                    <IconButton size="large">
                                        <StarRateRoundedIcon fontSize="large" color="primary" />
                                    </IconButton>
                                    Avaliações
                                </Typography>

                                <Stack
                                    direction={{ xs: "column", sm: "row" }}
                                    spacing={1}
                                    alignItems={{ xs: "stretch", sm: "center" }}
                                    sx={{ width: { xs: "100%", sm: "auto" } }}
                                >
                                    <Tooltip title="Recarregar avaliações">
                                        <IconButton
                                            onClick={() => fetchAvaliacoes(avaliacoesPage, avaliacoesRowsPerPage)}
                                            sx={{ width: { xs: "100%", sm: "auto" } }}
                                        >
                                            <RefreshIcon />
                                        </IconButton>
                                    </Tooltip>

                                    <Button
                                        variant={avaliacoesFiltersOpen ? "contained" : "outlined"}
                                        startIcon={<FilterListIcon />}
                                        endIcon={
                                            <ExpandMoreIcon
                                                sx={{
                                                    transform: avaliacoesFiltersOpen
                                                        ? "rotate(180deg)"
                                                        : "rotate(0deg)",
                                                    transition: "0.2s"
                                                }}
                                            />
                                        }
                                        onClick={() => setAvaliacoesFiltersOpen(v => !v)}
                                        fullWidth
                                        sx={{ width: { xs: "100%", sm: "auto" } }}
                                    >
                                        Filtros
                                    </Button>
                                </Stack>
                            </Box>

                            {/* Resumo dos filtros quando fechado */}
                            {!avaliacoesFiltersOpen && (fAvalSentimento || fAvalMesAno || fAvalNome || fAvalTipo) && (
                                <Box mb={2}>{activeAvalFilterChips}</Box>
                            )}

                            {/* Filtros Avaliações */}
                            <Collapse in={avaliacoesFiltersOpen} timeout="auto" unmountOnExit>
                                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, mb: 2 }}>
                                    <Grid container spacing={2} alignItems="center">
                                        <Grid item xs={12} md={3}>
                                            <TextField
                                                select
                                                label="Sentimento"
                                                fullWidth
                                                value={fAvalSentimento}
                                                onChange={e => setFAvalSentimento(e.target.value)}
                                            >
                                                {AVAL_SENTIMENTO_OPTS.map(o => (
                                                    <MenuItem key={o.value} value={o.value}>
                                                        {o.label}
                                                    </MenuItem>
                                                ))}
                                            </TextField>
                                        </Grid>

                                        <Grid item xs={12} md={3}>
                                            <TextField
                                                label="Mês/Ano"
                                                placeholder="MM/AAAA"
                                                fullWidth
                                                value={fAvalMesAno}
                                                onChange={e => setFAvalMesAno(maskMonthYearInput(e.target.value))}
                                                inputProps={{ maxLength: 7, inputMode: "numeric" }}
                                            />
                                        </Grid>

                                        <Grid item xs={12} md={3}>
                                            <TextField
                                                label="Nome do usuário"
                                                fullWidth
                                                value={fAvalNome}
                                                onChange={e => setFAvalNome(e.target.value)}
                                            />
                                        </Grid>

                                        <Grid item xs={12} md={3}>
                                            <TextField
                                                select
                                                label="Tipo (status do plano)"
                                                fullWidth
                                                value={fAvalTipo}
                                                onChange={e => setFAvalTipo(e.target.value)}
                                            >
                                                {TIPO_AVALIADOR_OPTS.map(o => (
                                                    <MenuItem key={o.value} value={o.value}>
                                                        {o.label}
                                                    </MenuItem>
                                                ))}
                                            </TextField>
                                        </Grid>

                                        <Grid item xs={12} md={3}>
                                            <Stack
                                                direction={{ xs: "column", sm: "row" }}
                                                spacing={1}
                                            >
                                                <Button
                                                    variant="contained"
                                                    onClick={() => fetchAvaliacoes(1, avaliacoesRowsPerPage)}
                                                    disabled={avaliacoesLoading}
                                                    fullWidth
                                                >
                                                    {avaliacoesLoading ? (
                                                        <CircularProgress size={18} sx={{ color: "white" }} />
                                                    ) : "Aplicar"}
                                                </Button>
                                                <Button
                                                    variant="outlined"
                                                    onClick={clearAvaliacoesFilters}
                                                    disabled={avaliacoesLoading}
                                                    fullWidth
                                                >
                                                    Limpar
                                                </Button>
                                            </Stack>
                                        </Grid>
                                    </Grid>

                                    {(fAvalSentimento || fAvalMesAno || fAvalNome || fAvalTipo) && (
                                        <Box mt={2}>{activeAvalFilterChips}</Box>
                                    )}
                                </Paper>
                            </Collapse>

                            {/* Lista de avaliações em cards: mobile = uma coluna; desktop = grid (3 por linha) */}
                            {isMobile ? (
                                <Stack spacing={1.5} mt={2}>
                                    {(!avaliacoes || avaliacoes.length === 0) && !avaliacoesLoading && (
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            align="center"
                                        >
                                            Nenhuma avaliação encontrada.
                                        </Typography>
                                    )}

                                    {avaliacoes.map(a => (
                                        <AvaliacaoCard key={a.evaluationId} a={a} />
                                    ))}
                                </Stack>
                            ) : (
                                <Box mt={2}>
                                    {(!avaliacoes || avaliacoes.length === 0) && !avaliacoesLoading && (
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            align="center"
                                            sx={{ mb: 2 }}
                                        >
                                            Nenhuma avaliação encontrada.
                                        </Typography>
                                    )}

                                    <Grid container spacing={2}>
                                        {avaliacoes.map(a => (
                                            <Grid key={a.evaluationId} item xs={12} sm={6} md={4}>
                                                <AvaliacaoCard a={a} />
                                            </Grid>
                                        ))}
                                    </Grid>
                                </Box>
                            )}

                            <PaginationControls
                                page={avaliacoesPage}
                                total={avaliacoesTotal}
                                rowsPerPage={avaliacoesRowsPerPage}
                                loading={avaliacoesLoading}
                                rowsPerPageOptions={[6, 9, 12, 24]}
                                onPageChange={(nextPage) => fetchAvaliacoes(nextPage, avaliacoesRowsPerPage)}
                                onRowsPerPageChange={(nextLimit) => fetchAvaliacoes(1, nextLimit)}
                            />
                        </Paper>
                    )}

                    {/* MÓDULO 6 - PARCEIROS */}
                    {mainTab === 5 && (
                        <Paper elevation={3} sx={{ p: { xs: 2, sm: 3 }, borderRadius: 4 }}>
                            <Box
                                display="flex"
                                alignItems={{ xs: "stretch", sm: "center" }}
                                justifyContent="space-between"
                                gap={2}
                                mb={2}
                                sx={{ flexDirection: { xs: "column", md: "row" } }}
                            >
                                <Typography
                                    variant="h5"
                                    fontWeight={800}
                                    sx={{ display: "flex", alignItems: "center", gap: 1 }}
                                >
                                    <IconButton size="large">
                                        <Diversity3Icon fontSize="large" color="primary" />
                                    </IconButton>
                                    Parceiros
                                </Typography>
                                <Alert severity="info" sx={{ mt: 1, borderRadius: 2 }}>
                                    Este módulo é voltado para ações com professores individuais/avulsos.
                                </Alert>

                                <Stack
                                    direction={{ xs: "column", sm: "row" }}
                                    spacing={1}
                                    alignItems={{ xs: "stretch", sm: "center" }}
                                    sx={{ width: { xs: "100%", sm: "auto" } }}
                                >

                                    <Button
                                        variant="contained"
                                        startIcon={<PersonAddAlt1Icon />}
                                        onClick={() => setDlgNovoParceiroOpen(true)}
                                        fullWidth
                                        sx={{ width: { xs: "100%", sm: "auto" } }}
                                    >
                                        Novo Parceiro
                                    </Button>
                                </Stack>

                            </Box>

                            {/* Tabs internas: Parceiros / Usuários por indicação */}
                            <Tabs
                                value={partnerTab}
                                onChange={(_, v) => setPartnerTab(v)}
                                sx={{ mb: 2, borderBottom: theme => `1px solid ${theme.palette.divider}` }}
                            >
                                <Tab label="Parceiros" />
                                <Tab label="Usuários por indicação" />
                            </Tabs>

                            {/* TAB 0: Lista de Parceiros */}
                            {partnerTab === 0 && (
                                <Box>
                                    <Stack
                                        direction={{ xs: "column", sm: "row" }}
                                        spacing={1}
                                        alignItems={{ xs: "stretch", sm: "center" }}
                                        justifyContent="flex-end"
                                        ml="auto"
                                        sx={{ width: "100%" }}
                                    >
                                        <Tooltip title="Recarregar parceiros">
                                            <IconButton
                                                onClick={() => fetchPartners(partnerPage, partnerRowsPerPage)}
                                                sx={{ width: { xs: "100%", sm: "auto" } }}
                                            >
                                                <RefreshIcon />
                                            </IconButton>
                                        </Tooltip>

                                        <Button
                                            variant={(partnerTab === 0 ? partnerFiltersOpen : indicacaoFiltersOpen) ? "contained" : "outlined"}
                                            startIcon={<FilterListIcon />}
                                            endIcon={
                                                <ExpandMoreIcon
                                                    sx={{
                                                        transform: (partnerTab === 0 ? partnerFiltersOpen : indicacaoFiltersOpen)
                                                            ? "rotate(180deg)"
                                                            : "rotate(0deg)",
                                                        transition: "0.2s"
                                                    }}
                                                />
                                            }
                                            onClick={() => {
                                                if (partnerTab === 0) {
                                                    setPartnerFiltersOpen(v => !v);
                                                } else {
                                                    setIndicacaoFiltersOpen(v => !v);
                                                }
                                            }}
                                            fullWidth
                                            sx={{ width: { xs: "100%", sm: "auto" } }}
                                        >
                                            Filtros
                                        </Button>
                                    </Stack>

                                    {/* Filtros parceiros */}
                                    <Collapse in={partnerFiltersOpen} timeout="auto" unmountOnExit sx={{ mt: 2 }}>
                                        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, mb: 2 }}>
                                            <Grid container spacing={2} alignItems="center">
                                                <Grid item xs={12} md={3}>
                                                    <TextField
                                                        label="Nome"
                                                        fullWidth
                                                        value={pNome}
                                                        onChange={e => setPNome(e.target.value)}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} md={3}>
                                                    <TextField
                                                        label="E-mail"
                                                        fullWidth
                                                        value={pEmail}
                                                        onChange={e => setPEmail(e.target.value)}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} md={3}>
                                                    <TextField
                                                        label="Código de indicação"
                                                        fullWidth
                                                        value={pIndicacao}
                                                        onChange={e => setPIndicacao(e.target.value)}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} md={2}>
                                                    <TextField
                                                        select
                                                        label="Status"
                                                        fullWidth
                                                        value={pStatus}
                                                        onChange={e => setPStatus(e.target.value)}
                                                    >
                                                        {PARTNER_STATUS_OPTS.map(o => (
                                                            <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                                                        ))}
                                                    </TextField>
                                                </Grid>

                                                {/* Botões: quebram para baixo no mobile */}
                                                <Grid item xs={12} md={3}>
                                                    <Stack
                                                        direction={{ xs: "column", sm: "row" }}
                                                        spacing={1}
                                                    >
                                                        <Button
                                                            variant="contained"
                                                            onClick={() => fetchPartners(1, partnerRowsPerPage)}
                                                            disabled={partnersLoading}
                                                            fullWidth
                                                        >
                                                            {partnersLoading ? (
                                                                <CircularProgress size={18} sx={{ color: "white" }} />
                                                            ) : "Aplicar"}
                                                        </Button>
                                                        <Button
                                                            variant="outlined"
                                                            onClick={clearPartnerFilters}
                                                            disabled={partnersLoading}
                                                            fullWidth
                                                        >
                                                            Limpar
                                                        </Button>
                                                    </Stack>
                                                </Grid>
                                            </Grid>
                                        </Paper>
                                    </Collapse>

                                    {/* Lista de parceiros: cards no mobile, tabela no desktop */}
                                    {isMobile ? (
                                        <Stack spacing={1.5} mt={2}>
                                            {(!partners || partners.length === 0) && !partnersLoading && (
                                                <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                    align="center"
                                                >
                                                    Nenhum parceiro encontrado.
                                                </Typography>
                                            )}

                                            {partners.map((p) => (
                                                <MobilePartnerCard
                                                    key={p.partnerId}
                                                    p={p}
                                                    onToggle={() => handleToggleParceiroStatus(p)}
                                                    copy={copy}
                                                />
                                            ))}
                                        </Stack>
                                    ) : (
                                        <Box sx={{
                                            width: "100%",
                                            overflowX: "auto",
                                            border: (theme) => `1px solid ${theme.palette.divider}`,
                                            borderRadius: 2,
                                            mt: 2
                                        }}>
                                            <table style={{ width: "100%", borderCollapse: "collapse" }}>
                                                <thead>
                                                    <tr>
                                                        {[
                                                            { label: "Nome", align: "left" },
                                                            { label: "Celular", align: "center" },
                                                            { label: "Código indicação", align: "center" },
                                                            { label: "Link indicação", align: "left" },
                                                            { label: "Desconto", align: "center" },
                                                            { label: "Status", align: "center" },
                                                            { label: "Ações", align: "center" },
                                                        ].map(col => (
                                                            <th
                                                                key={col.label}
                                                                style={{
                                                                    textAlign: col.align,
                                                                    padding: "12px 16px",
                                                                    background: "rgba(0,0,0,0.04)",
                                                                    borderBottom: "1px solid rgba(0,0,0,0.12)",
                                                                    position: "sticky",
                                                                    top: 0,
                                                                    zIndex: 1,
                                                                    backdropFilter: "blur(2px)",
                                                                }}
                                                            >
                                                                {col.label}
                                                            </th>
                                                        ))}
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {partners.map((p) => {
                                                        const fullLink = p.referralCode
                                                            ? `${window.location.origin}/cadastro?indicacao=${p.referralCode}`
                                                            : "";

                                                        const MAX_CHARS = 20;
                                                        const displayLink =
                                                            fullLink.length > MAX_CHARS
                                                                ? fullLink.slice(0, MAX_CHARS) + "..."
                                                                : fullLink;

                                                        return (
                                                            <tr
                                                                key={p.partnerId}
                                                                style={{ borderBottom: "1px solid rgba(0,0,0,0.12)" }}
                                                            >
                                                                <td style={{ padding: "12px 16px" }}>{p.name || "-"}</td>
                                                                <td style={{ padding: "12px 16px" }} align="center">
                                                                    {p.phone && (
                                                                        <Tooltip title="Abrir WhatsApp">
                                                                            <IconButton
                                                                                size="small"
                                                                                component="a"
                                                                                href={`https://wa.me/${p.phone.replace(/\D/g, '')}`}
                                                                                target="_blank"
                                                                                rel="noopener noreferrer"
                                                                            >
                                                                                <WhatsAppIcon fontSize="small" sx={{ color: "#25D366" }} />
                                                                            </IconButton>
                                                                        </Tooltip>
                                                                    )}

                                                                    {p.phone || "-"}
                                                                </td>
                                                                <td style={{ padding: "12px 16px" }}>
                                                                    {p.referralCode || "-"}
                                                                    {p.referralCode && (
                                                                        <Tooltip title="Copiar Código">
                                                                            <IconButton
                                                                                onClick={() => copy(p.referralCode, "Código")}
                                                                                size="small"
                                                                            >
                                                                                <ContentCopyIcon fontSize="small" />
                                                                            </IconButton>
                                                                        </Tooltip>
                                                                    )}
                                                                </td>

                                                                {/* Link indicação truncado apenas na exibição */}
                                                                <td style={{ padding: "12px 16px" }}>
                                                                    {p.referralCode ? (
                                                                        <>
                                                                            <span style={{ whiteSpace: "nowrap" }}>
                                                                                {displayLink}
                                                                            </span>
                                                                            <Tooltip title="Copiar Link">
                                                                                <IconButton
                                                                                    onClick={() => copy(fullLink, "Link")}
                                                                                    size="small"
                                                                                >
                                                                                    <ContentCopyIcon fontSize="small" />
                                                                                </IconButton>
                                                                            </Tooltip>
                                                                        </>
                                                                    ) : (
                                                                        "-"
                                                                    )}
                                                                </td>
                                                                <td style={{ padding: "12px 16px" }} align="center">
                                                                    {p.couponDiscountPercentage ? (
                                                                        <>

                                                                            <span style={{ whiteSpace: "nowrap" }}>
                                                                                <DiscountIcon fontSize="small" sx={{ mr: 0.5, verticalAlign: 'middle', color: "#757575" }} />{`${p.couponDiscountPercentage}%`}
                                                                            </span>
                                                                        </>
                                                                    ) : (
                                                                        "-"
                                                                    )}
                                                                </td>

                                                                <td style={{ padding: "12px 16px" }} align="center">
                                                                    {p.status === 1 ? (
                                                                        <Chip size="small" color="success" label="Ativo" />
                                                                    ) : (
                                                                        <Chip size="small" color="error" label="Inativo" />
                                                                    )}
                                                                </td>
                                                                <td style={{ padding: "12px 16px" }} align="center">
                                                                    <Button
                                                                        sx={{ textTransform: "none" }}
                                                                        size="small"
                                                                        variant="outlined"
                                                                        color={p.status === 1 ? "error" : "success"}
                                                                        startIcon={
                                                                            p.status === 1 ? <ToggleOffIcon /> : <ToggleOnIcon />
                                                                        }
                                                                        onClick={() => handleToggleParceiroStatus(p)}
                                                                    >
                                                                        {p.status === 1 ? "Desativar" : "Ativar"}
                                                                    </Button>
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>

                                            </table>
                                        </Box>
                                    )}

                                    <PaginationControls
                                        page={partnerPage}
                                        total={partnerTotal}
                                        rowsPerPage={partnerRowsPerPage}
                                        loading={partnersLoading}
                                        onPageChange={(nextPage) => fetchPartners(nextPage, partnerRowsPerPage)}
                                        onRowsPerPageChange={(nextLimit) => fetchPartners(1, nextLimit)}
                                    />
                                </Box>
                            )}

                            {/* TAB 1: Usuários por indicação */}
                            {partnerTab === 1 && (
                                <Box>
                                    <Stack
                                        direction={{ xs: "column", sm: "row" }}
                                        spacing={1}
                                        alignItems={{ xs: "stretch", sm: "center" }}
                                        justifyContent="flex-end"
                                        ml="auto"
                                        sx={{ width: "100%" }}
                                    >
                                        <Tooltip title="Recarregar usuários por indicação">
                                            <IconButton
                                                onClick={() => fetchIndicacaoUsers(indicacaoPage, indicacaoRowsPerPage)}
                                                sx={{ width: { xs: "100%", sm: "auto" } }}
                                                disabled={!hasIndicacaoUserFilter}
                                            >
                                                <RefreshIcon />
                                            </IconButton>
                                        </Tooltip>

                                        <Button
                                            variant={(partnerTab === 0 ? partnerFiltersOpen : indicacaoFiltersOpen) ? "contained" : "outlined"}
                                            startIcon={<FilterListIcon />}
                                            endIcon={
                                                <ExpandMoreIcon
                                                    sx={{
                                                        transform: (partnerTab === 0 ? partnerFiltersOpen : indicacaoFiltersOpen)
                                                            ? "rotate(180deg)"
                                                            : "rotate(0deg)",
                                                        transition: "0.2s"
                                                    }}
                                                />
                                            }
                                            onClick={() => {
                                                if (partnerTab === 0) {
                                                    setPartnerFiltersOpen(v => !v);
                                                } else {
                                                    setIndicacaoFiltersOpen(v => !v);
                                                }
                                            }}
                                            fullWidth
                                            sx={{ width: { xs: "100%", sm: "auto" } }}
                                        >
                                            Filtros
                                        </Button>
                                    </Stack>

                                    <Collapse in={indicacaoFiltersOpen} timeout="auto" unmountOnExit sx={{ mt: 2 }}>
                                        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, mb: 2 }}>
                                            <Grid container spacing={2} alignItems="center">
                                                <Grid item xs={12} md={4}>
                                                    <TextField
                                                        label="Nome Parceiro"
                                                        fullWidth
                                                        value={uNome}
                                                        onChange={e => setUNome(e.target.value)}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} md={4}>
                                                    <TextField
                                                        label="E-mail Parceiro"
                                                        fullWidth
                                                        value={uEmail}
                                                        onChange={e => setUEmail(e.target.value)}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} md={3}>
                                                    <TextField
                                                        label="Código de indicação"
                                                        fullWidth
                                                        value={uIndicacao}
                                                        onChange={e => setUIndicacao(e.target.value)}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} md={3}>
                                                    <Stack
                                                        direction={{ xs: "column", sm: "row" }}
                                                        spacing={1}
                                                    >
                                                        <Button
                                                            variant="contained"
                                                            onClick={() => fetchIndicacaoUsers(1, indicacaoRowsPerPage)}
                                                            disabled={indicacaoLoading || !hasIndicacaoUserFilter}
                                                            fullWidth
                                                        >
                                                            {indicacaoLoading ? (
                                                                <CircularProgress size={18} sx={{ color: "white" }} />
                                                            ) : "Aplicar"}
                                                        </Button>
                                                        <Button
                                                            variant="outlined"
                                                            onClick={clearIndicacaoUserFilters}
                                                            disabled={indicacaoLoading}
                                                            fullWidth
                                                        >
                                                            Limpar
                                                        </Button>
                                                    </Stack>
                                                </Grid>
                                            </Grid>
                                            {!hasIndicacaoUserFilter && (
                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                    sx={{ mt: 1, display: "block" }}
                                                >
                                                    <InfoIcon style={{ fontSize: 18, color: "#1976D2", verticalAlign: "middle", marginRight: 2 }} />Informe algum dado do parceiro para exibir os usuários indicados por ele.
                                                </Typography>
                                            )}
                                        </Paper>
                                    </Collapse>


                                    {isMobile ? (
                                        <Stack spacing={1.5} mt={2}>
                                            {(!indicacaoUsers || indicacaoUsers.length === 0) &&
                                                !indicacaoLoading &&
                                                hasIndicacaoUserFilter && (
                                                    <Typography
                                                        variant="body2"
                                                        color="text.secondary"
                                                        align="center"
                                                    >
                                                        Nenhum usuário encontrado para os filtros informados.
                                                    </Typography>
                                                )}

                                            {indicacaoUsers.map((u) => (
                                                <MobileIndicacaoUserCard key={u.id} u={u} />
                                            ))}
                                        </Stack>
                                    ) : (
                                        <Box sx={{
                                            width: "100%",
                                            overflowX: "auto",
                                            border: (theme) => `1px solid ${theme.palette.divider}`,
                                            borderRadius: 2,
                                            mt: 2
                                        }}>
                                            <table style={{ width: "100%", borderCollapse: "collapse" }}>
                                                <thead>
                                                    <tr>
                                                        {[
                                                            { label: "Nome", align: "left" },
                                                            { label: "E-mail", align: "left" },
                                                            { label: "Região", align: "left" },
                                                            { label: "Código indicação", align: "center" },
                                                            { label: "Status", align: "center" },
                                                            { label: "Plano", align: "center" },
                                                        ].map(col => (
                                                            <th
                                                                key={col.label}
                                                                style={{
                                                                    textAlign: col.align,
                                                                    padding: "12px 16px",
                                                                    background: "rgba(0,0,0,0.04)",
                                                                    borderBottom: "1px solid rgba(0,0,0,0.12)",
                                                                    position: "sticky",
                                                                    top: 0,
                                                                    zIndex: 1,
                                                                    backdropFilter: "blur(2px)",
                                                                }}
                                                            >
                                                                {col.label}
                                                            </th>
                                                        ))}
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {(!indicacaoUsers || indicacaoUsers.length === 0) &&
                                                        !indicacaoLoading &&
                                                        hasIndicacaoUserFilter && (
                                                            <tr>
                                                                <td colSpan={6} style={{ padding: 24, textAlign: "center", color: "#666" }}>
                                                                    Nenhum usuário encontrado para os filtros informados.
                                                                </td>
                                                            </tr>
                                                        )}

                                                    {indicacaoUsers.map(u => (
                                                        <tr
                                                            key={u.id}
                                                            style={{ borderBottom: "1px solid rgba(0,0,0,0.12)" }}
                                                        >
                                                            <td style={{ padding: "12px 16px" }}>{u.name || "-"}</td>
                                                            <td style={{ padding: "12px 16px" }}>{u.email || "-"}</td>
                                                            <td style={{ padding: "12px 16px" }}>
                                                                {(u.state && u.city) ? `${u.state} / ${u.city}` : "-"}
                                                            </td>
                                                            <td style={{ padding: "12px 16px" }} align="center">
                                                                {u.referralCode || "-"}
                                                            </td>
                                                            <td style={{ padding: "12px 16px" }} align="center">
                                                                {u.status ? (
                                                                    <Chip
                                                                        size="small"
                                                                        label={STATUS_LABEL[u.status] || u.status}
                                                                        color={STATUS_COLOR[u.status] || "default"}
                                                                        sx={getStatusChipSx(u.status)}
                                                                        variant="filled"
                                                                    />
                                                                ) : "-"}
                                                            </td>
                                                            <td style={{ padding: "12px 16px" }} align="center">
                                                                {u.status === "A" && u.plan ? (
                                                                    <Chip
                                                                        size="small"
                                                                        label={formatPlano(u.plan)}
                                                                        sx={(theme) => ({
                                                                            bgcolor: PLAN_STYLES[u.plan]?.bg(theme) || "action.selected",
                                                                            color: PLAN_STYLES[u.plan]?.fg(theme) || theme.palette.text.primary,
                                                                            fontWeight: 600,
                                                                        })}
                                                                    />
                                                                ) : "-"}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </Box>
                                    )}

                                    {hasIndicacaoUserFilter && (
                                        <PaginationControls
                                            page={indicacaoPage}
                                            total={indicacaoTotal}
                                            rowsPerPage={indicacaoRowsPerPage}
                                            loading={indicacaoLoading}
                                            onPageChange={(nextPage) => fetchIndicacaoUsers(nextPage, indicacaoRowsPerPage)}
                                            onRowsPerPageChange={(nextLimit) => fetchIndicacaoUsers(1, nextLimit)}
                                        />
                                    )}
                                </Box>
                            )}
                        </Paper>
                    )}

                    {/* MÓDULO 7 - ASSINATURA (LINK ASAAS) */}
                    {mainTab === 6 && isMaster && (
                        <Paper elevation={3} sx={{ p: { xs: 2, sm: 3 }, borderRadius: 4 }}>
                            <Box
                                display="flex"
                                alignItems={{ xs: "stretch", sm: "center" }}
                                justifyContent="space-between"
                                gap={2}
                                mb={2}
                                sx={{ flexDirection: { xs: "column", md: "row" } }}
                            >
                                <Typography
                                    variant="h5"
                                    fontWeight={800}
                                    sx={{ display: "flex", alignItems: "center", gap: 1 }}
                                >
                                    <IconButton size="large">
                                        <PaymentRoundedIcon fontSize="large" color="primary" />
                                    </IconButton>
                                    Gerar link de assinatura (Asaas)
                                </Typography>
                                <Alert severity="info" sx={{ mt: 1, borderRadius: 2 }}>
                                    Este módulo deve ser usado apenas para professores individuais/avulsos.
                                </Alert>

                                <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ width: { xs: "100%", sm: "auto" } }}>
                                    <Button
                                        variant="outlined"
                                        onClick={limparAssinatura}
                                        fullWidth
                                        sx={{ width: { xs: "100%", sm: "auto" } }}
                                    >
                                        Limpar
                                    </Button>
                                </Stack>
                            </Box>

                            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                                <Grid container spacing={2} alignItems="center">
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            label="E-mail do usuário"
                                            fullWidth
                                            value={assinEmail}
                                            onChange={(e) => {
                                                const v = e.target.value;
                                                setAssinEmail(v);
                                                setAssinCheckoutUrl("");
                                            }}
                                            placeholder="ex.: cliente@dominio.com"
                                            InputProps={{
                                                endAdornment: assinLookupLoading ? <CircularProgress size={18} /> : null
                                            }}
                                        />
                                    </Grid>

                                    <Grid item xs={12} md={3}>
                                        <TextField
                                            select
                                            label="Plano"
                                            fullWidth
                                            value={assinPlano}
                                            onChange={(e) => setAssinPlano(e.target.value)}
                                        >
                                            {PLANO_OPTS.filter(p => p.value).map(o => (
                                                <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                                            ))}
                                        </TextField>
                                    </Grid>

                                    <Grid item xs={12} md={3}>
                                        <Stack direction="row" spacing={1}>
                                            <Button
                                                variant="outlined"
                                                fullWidth
                                                onClick={() => lookupUserByEmail(assinEmail)}
                                                disabled={assinLookupLoading || !isValidEmail(assinEmail)}
                                            >
                                                Buscar
                                            </Button>

                                            <Button
                                                variant="contained"
                                                fullWidth
                                                onClick={gerarLinkAssinatura}
                                                disabled={assinGerarLoading || assinLookupLoading || !isValidEmail(assinEmail)}
                                                startIcon={assinGerarLoading ? <CircularProgress size={16} sx={{ color: "white" }} /> : <LinkRoundedIcon />}
                                            >
                                                {assinGerarLoading ? "Gerando..." : (isMobile ? "Gerar" : "Gerar link")}
                                            </Button>
                                        </Stack>
                                    </Grid>
                                </Grid>

                                <Divider sx={{ my: 2 }} />

                                {/* Prévia do usuário */}
                                {!assinUser && isValidEmail(assinEmail) && !assinLookupLoading && (
                                    <Box sx={{ mt: 1 }}>
                                        <Typography variant="body2" color="error">
                                            Usuário não encontrado para este e-mail.
                                        </Typography>
                                    </Box>
                                )}

                                {assinUser && (
                                    <Box
                                        sx={{
                                            mt: 1,
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 1,
                                            flexWrap: "wrap",                 // permite quebrar linha
                                        }}
                                    >
                                        <Chip
                                            size="small"
                                            color="success"
                                            label="Usuário encontrado"
                                            sx={{ fontWeight: 600, flexShrink: 0 }}
                                        />

                                        <Typography
                                            variant="body2"
                                            sx={{
                                                wordBreak: "break-word",      // quebra palavras longas
                                                overflowWrap: "anywhere",     // força quebra se necessário
                                            }}
                                        >
                                            <b>{assinUser.name}</b> — {assinUser.email}
                                        </Typography>
                                    </Box>

                                )}


                                <Divider sx={{ my: 2 }} />

                                {/* Resultado: checkoutUrl */}
                                <Box>
                                    <Typography variant="subtitle2" fontWeight={900} sx={{ mb: 1 }}>
                                        Link de checkout
                                    </Typography>

                                    <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems="stretch">
                                        <TextField
                                            fullWidth
                                            value={assinCheckoutUrl}
                                            placeholder="Gere o link para aparecer aqui..."
                                            InputProps={{ readOnly: true }}
                                        />

                                        <Button
                                            variant="outlined"
                                            disabled={!assinCheckoutUrl}
                                            onClick={() => copy(assinCheckoutUrl, "Link")}
                                            startIcon={<ContentCopyIcon />}
                                            sx={{ whiteSpace: "nowrap" }}
                                        >
                                            Copiar
                                        </Button>

                                        <Button
                                            variant="contained"
                                            disabled={!assinCheckoutUrl}
                                            component="a"
                                            href={assinCheckoutUrl || undefined}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            startIcon={<OpenInNewRoundedIcon />}
                                            sx={{ whiteSpace: "nowrap" }}
                                        >
                                            Abrir
                                        </Button>
                                    </Stack>

                                    {assinCheckoutUrl && (
                                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                                            Envie este link para o cliente. Ele abre o checkout do Asaas já com os dados preenchidos.
                                        </Typography>
                                    )}
                                </Box>
                            </Paper>
                        </Paper>
                    )}

                    {/* MÓDULO 8 - REMARKETING (DISPARO DE E-MAILS) */}
                    {mainTab === 7 && isMaster && (
                        <Paper elevation={3} sx={{ p: { xs: 2, sm: 3 }, borderRadius: 4 }}>
                            <Box
                                display="flex"
                                alignItems={{ xs: "stretch", sm: "center" }}
                                justifyContent="space-between"
                                gap={2}
                                mb={2}
                                sx={{ flexDirection: { xs: "column", md: "row" } }}
                            >
                                <Box>
                                    <Typography variant="h5" fontWeight={800} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                        <IconButton size="large"><EmailRoundedIcon fontSize="large" color="primary" /></IconButton>
                                        Remarketing
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Monte e dispare e-mails personalizados para professores individuais/avulsos.
                                    </Typography>
                                </Box>

                                <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ width: { xs: "100%", sm: "auto" } }}>
                                    <Button
                                        variant="outlined"
                                        startIcon={<RefreshIcon />}
                                        onClick={() => fetchRemarketingRecipients()}
                                        disabled={remarketingLoadingUsers || remarketingSending}
                                    >
                                        Atualizar usuários
                                    </Button>
                                    <Button
                                        color="error"
                                        variant="outlined"
                                        startIcon={<DeleteOutlineRoundedIcon />}
                                        onClick={clearRemarketingForm}
                                        disabled={remarketingSending}
                                    >
                                        Limpar
                                    </Button>
                                </Stack>
                            </Box>

                            <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
                                Esta ação é voltada para professores individuais/avulsos.
                            </Alert>

                            <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
                                Placeholders disponíveis: <strong>{"{{nome}}"}</strong>, <strong>{"{{primeiroNome}}"}</strong> e <strong>{"{{email}}"}</strong>. Eles serão substituídos automaticamente para cada destinatário.
                            </Alert>

                            <Grid container spacing={2.5}>
                                <Grid item xs={12} lg={7}>
                                    <Stack spacing={2}>
                                        <TextField
                                            label="Título do e-mail"
                                            value={remarketingSubject}
                                            onChange={(e) => setRemarketingSubject(e.target.value)}
                                            inputProps={{ maxLength: 120 }}
                                            helperText={`${remarketingSubject.length}/120 caracteres`}
                                            disabled={remarketingSending}
                                            fullWidth
                                        />

                                        <Box>
                                            <Typography variant="subtitle2" fontWeight={900} mb={1}>
                                                Corpo do e-mail
                                            </Typography>
                                            <RichEmailEditor
                                                value={remarketingHtml}
                                                onChange={setRemarketingHtml}
                                                disabled={remarketingSending}
                                            />
                                        </Box>

                                        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.2}>
                                            <Button
                                                variant="contained"
                                                size="large"
                                                startIcon={remarketingSending ? <CircularProgress size={18} sx={{ color: "white" }} /> : <SendRoundedIcon />}
                                                onClick={handleSendRemarketingEmail}
                                                disabled={remarketingSending}
                                                fullWidth={isMobile}
                                            >
                                                {remarketingSending ? "Enviando..." : "Enviar e-mail"}
                                            </Button>

                                            <Button
                                                variant="outlined"
                                                size="large"
                                                startIcon={<InsertEmoticonRoundedIcon />}
                                                onClick={() => setRemarketingHtml((prev) => `${prev}<p>✨ </p>`)}
                                                disabled={remarketingSending}
                                                fullWidth={isMobile}
                                            >
                                                Inserir linha com emoji
                                            </Button>
                                        </Stack>
                                    </Stack>
                                </Grid>

                                <Grid item xs={12} lg={5}>
                                    <Stack spacing={2}>
                                        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                                            <Typography variant="subtitle1" fontWeight={900} mb={1}>
                                                Destinatários
                                            </Typography>

                                            <RadioGroup
                                                value={remarketingTarget}
                                                onChange={(e) => setRemarketingTarget(e.target.value)}
                                            >
                                                <FormControlLabel
                                                    value="all"
                                                    control={<Radio />}
                                                    label="Enviar para TODOS os usuários cadastrados"
                                                    disabled={remarketingSending}
                                                />
                                                <FormControlLabel
                                                    value="selected"
                                                    control={<Radio />}
                                                    label="Enviar para usuários específicos"
                                                    disabled={remarketingSending}
                                                />
                                                <FormControlLabel
                                                    value="non_subscribers"
                                                    control={<Radio />}
                                                    label="Enviar somente para usuários não assinantes"
                                                    disabled={remarketingSending}
                                                />
                                            </RadioGroup>

                                            {remarketingTarget === "all" && (
                                                <Alert severity="warning" sx={{ mt: 1, borderRadius: 2 }}>
                                                    Essa opção dispara para todos os usuários com e-mail cadastrado. O backend ainda valida MASTER antes de enviar.
                                                </Alert>
                                            )}

                                            {remarketingTarget === "non_subscribers" && (
                                                <Alert severity="info" sx={{ mt: 1, borderRadius: 2 }}>
                                                    Essa opção dispara somente para usuários com e-mail cadastrado que não possuem assinatura ativa.
                                                </Alert>
                                            )}

                                            {remarketingTarget === "selected" && (
                                                <Box mt={2}>
                                                    <Stack direction={{ xs: "column", sm: "row" }} spacing={1} mb={1.5}>
                                                        <TextField
                                                            label="Buscar por nome ou e-mail"
                                                            size="small"
                                                            value={remarketingSearch}
                                                            onChange={(e) => setRemarketingSearch(e.target.value)}
                                                            onKeyDown={(e) => {
                                                                if (e.key === "Enter") fetchRemarketingRecipients(remarketingSearch);
                                                            }}
                                                            disabled={remarketingLoadingUsers || remarketingSending}
                                                            fullWidth
                                                        />
                                                        <Button
                                                            variant="outlined"
                                                            startIcon={remarketingLoadingUsers ? <CircularProgress size={16} /> : <SearchRoundedIcon />}
                                                            onClick={() => fetchRemarketingRecipients(remarketingSearch)}
                                                            disabled={remarketingLoadingUsers || remarketingSending}
                                                        >
                                                            Buscar
                                                        </Button>
                                                    </Stack>

                                                    <Typography variant="caption" color="text.secondary">
                                                        Exibindo {remarketingUsers.length} de {remarketingTotalUsers} encontrado(s). Selecionados: {remarketingSelectedUsers.length}.
                                                    </Typography>

                                                    <Paper variant="outlined" sx={{ mt: 1, maxHeight: 280, overflow: "auto", borderRadius: 2 }}>
                                                        {remarketingLoadingUsers ? (
                                                            <Box p={2} display="flex" alignItems="center" gap={1}>
                                                                <CircularProgress size={18} />
                                                                <Typography variant="body2">Carregando usuários...</Typography>
                                                            </Box>
                                                        ) : remarketingUsers.length === 0 ? (
                                                            <Box p={2}>
                                                                <Typography variant="body2" color="text.secondary">
                                                                    Nenhum usuário encontrado.
                                                                </Typography>
                                                            </Box>
                                                        ) : (
                                                            remarketingUsers.map((user) => {
                                                                const checked = remarketingSelectedUsers.some((item) => item.id === user.id);

                                                                return (
                                                                    <Box
                                                                        key={user.id}
                                                                        sx={{
                                                                            px: 1.5,
                                                                            py: 1,
                                                                            borderBottom: "1px solid",
                                                                            borderColor: "divider",
                                                                            '&:last-of-type': { borderBottom: 0 },
                                                                        }}
                                                                    >
                                                                        <FormControlLabel
                                                                            control={
                                                                                <Checkbox
                                                                                    checked={checked}
                                                                                    onChange={() => toggleRemarketingSelected(user)}
                                                                                    disabled={remarketingSending}
                                                                                />
                                                                            }
                                                                            label={
                                                                                <Box sx={{ minWidth: 0 }}>
                                                                                    <Typography variant="body2" fontWeight={800} noWrap>
                                                                                        {user.name || "Sem nome"}
                                                                                    </Typography>
                                                                                    <Typography variant="caption" color="text.secondary" sx={{ overflowWrap: "anywhere" }}>
                                                                                        {user.email}
                                                                                    </Typography>
                                                                                </Box>
                                                                            }
                                                                            sx={{ alignItems: "flex-start", width: "100%" }}
                                                                        />
                                                                    </Box>
                                                                );
                                                            })
                                                        )}
                                                    </Paper>

                                                    {remarketingUsers.length > 0 && (
                                                        <Button
                                                            variant="text"
                                                            size="small"
                                                            startIcon={<SelectAllRoundedIcon />}
                                                            disabled={remarketingSending}
                                                            onClick={() => {
                                                                setRemarketingSelectedUsers((prev) => {
                                                                    const merged = [...prev];
                                                                    remarketingUsers.forEach((user) => {
                                                                        if (!merged.some((item) => item.id === user.id)) merged.push(user);
                                                                    });
                                                                    return merged;
                                                                });
                                                            }}
                                                            sx={{ mt: 1 }}
                                                        >
                                                            Selecionar usuários exibidos
                                                        </Button>
                                                    )}

                                                    {remarketingSelectedUsers.length > 0 && (
                                                        <Box mt={1.5}>
                                                            <Typography variant="caption" color="text.secondary">
                                                                Selecionados
                                                            </Typography>
                                                            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap mt={0.75}>
                                                                {remarketingSelectedUsers.map((user) => (
                                                                    <Chip
                                                                        key={user.id}
                                                                        label={user.name || user.email}
                                                                        onDelete={remarketingSending ? undefined : () => toggleRemarketingSelected(user)}
                                                                        size="small"
                                                                    />
                                                                ))}
                                                            </Stack>
                                                        </Box>
                                                    )}
                                                </Box>
                                            )}
                                        </Paper>

                                        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                                            <Typography variant="subtitle1" fontWeight={900} mb={1}>
                                                Prévia do corpo
                                            </Typography>
                                            <Paper
                                                variant="outlined"
                                                sx={{
                                                    p: 2,
                                                    borderRadius: 2,
                                                    maxHeight: 310,
                                                    overflow: "auto",
                                                    bgcolor: "background.default",
                                                    fontSize: 14,
                                                    lineHeight: 1.7,
                                                }}
                                            >
                                                <Box dangerouslySetInnerHTML={{ __html: remarketingHtml || "" }} />
                                            </Paper>
                                        </Paper>

                                        {remarketingResult && (
                                            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                                                <Typography variant="subtitle1" fontWeight={900} mb={1}>
                                                    Resultado do envio
                                                </Typography>

                                                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap mb={1.5}>
                                                    <Chip color="success" label={`Sucesso: ${remarketingResult.successCount || 0}`} />
                                                    <Chip color="error" label={`Falhas: ${remarketingResult.failedCount || 0}`} />
                                                    <Chip label={`Ignorados: ${remarketingResult.skippedCount || 0}`} />
                                                    <Chip variant="outlined" label={`Total: ${remarketingResult.totalCandidates || 0}`} />
                                                </Stack>

                                                <Paper variant="outlined" sx={{ maxHeight: 250, overflow: "auto", borderRadius: 2 }}>
                                                    {(remarketingResult.results || []).map((item, index) => (
                                                        <Box
                                                            key={`${item.email}-${index}`}
                                                            sx={{
                                                                px: 1.5,
                                                                py: 1,
                                                                borderBottom: "1px solid",
                                                                borderColor: "divider",
                                                                '&:last-of-type': { borderBottom: 0 },
                                                            }}
                                                        >
                                                            <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                                                                <Box sx={{ minWidth: 0 }}>
                                                                    <Typography variant="body2" fontWeight={800} noWrap>
                                                                        {item.name || item.email || "Usuário"}
                                                                    </Typography>
                                                                    <Typography variant="caption" color="text.secondary" sx={{ overflowWrap: "anywhere" }}>
                                                                        {item.email || "-"}
                                                                    </Typography>
                                                                    {item.reason && (
                                                                        <Typography variant="caption" color="error" display="block">
                                                                            {item.reason}
                                                                        </Typography>
                                                                    )}
                                                                </Box>
                                                                <Chip
                                                                    size="small"
                                                                    color={item.status === "sent" ? "success" : item.status === "failed" ? "error" : "default"}
                                                                    label={item.status === "sent" ? "Enviado" : item.status === "failed" ? "Falhou" : "Ignorado"}
                                                                />
                                                            </Stack>
                                                        </Box>
                                                    ))}
                                                </Paper>
                                            </Paper>
                                        )}
                                    </Stack>
                                </Grid>
                            </Grid>
                        </Paper>
                    )}

                </Box>
            </Box>

            {/* Dialog: novo ADM (MASTER) */}
            <Dialog open={dlgNovoOpen} onClose={() => setDlgNovoOpen(false)} fullWidth maxWidth="xs">
                <DialogTitle>Novo usuário ADM</DialogTitle>
                <DialogContent dividers>
                    <TextField
                        label="E-mail"
                        type="email"
                        fullWidth
                        value={novoEmail}
                        onChange={(e) => setNovoEmail(e.target.value)}
                    />
                    <Typography variant="caption" color="text.secondary">
                        A senha padrão será <b>adm@123</b> (o usuário poderá trocá-la depois).
                    </Typography>
                </DialogContent>
                <DialogActions style={{ marginBottom: 4 }}>
                    <Button onClick={() => setDlgNovoOpen(false)}>Cancelar</Button>
                    <Button
                        variant="contained"
                        onClick={handleCriarAdm}
                        disabled={novoLoading}
                        startIcon={novoLoading ? <CircularProgress size={16} sx={{ color: "white" }} /> : null}
                    >
                        {novoLoading ? "Criando..." : "Criar"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Dialog: alterar minha senha */}
            <Dialog open={dlgSenhaOpen} onClose={() => setDlgSenhaOpen(false)} fullWidth maxWidth="xs">
                <DialogTitle>Alterar minha senha</DialogTitle>
                <DialogContent dividers>
                    <TextField
                        label="Nova Senha"
                        type="password"
                        fullWidth
                        value={novaSenha}
                        onChange={(e) => setNovaSenha(e.target.value)}
                    />
                </DialogContent>
                <DialogActions style={{ marginBottom: 4 }}>
                    <Button onClick={() => setDlgSenhaOpen(false)}>Cancelar</Button>
                    <Button
                        variant="contained"
                        onClick={handleAlterarSenha}
                        disabled={senhaLoading}
                        startIcon={senhaLoading ? <CircularProgress size={16} sx={{ color: "white" }} /> : null}
                    >
                        {senhaLoading ? "Salvando..." : "Salvar"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Dialog: novo parceiro */}
            <Dialog
                open={dlgNovoParceiroOpen}
                onClose={handleCloseNovoParceiro}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle>Novo parceiro (influenciador)</DialogTitle>
                <DialogContent dividers>
                    <Stack spacing={2}>
                        <TextField
                            label="Nome"
                            fullWidth
                            value={parceiroNome}
                            onChange={e => setParceiroNome(e.target.value)}
                            disabled={parceiroLoading || !!parceiroCodigoGerado}
                        />
                        <TextField
                            label="E-mail"
                            type="email"
                            fullWidth
                            value={parceiroEmail}
                            onChange={e => setParceiroEmail(e.target.value)}
                            disabled={parceiroLoading || !!parceiroCodigoGerado}
                        />
                        <TextField
                            label="Celular"
                            fullWidth
                            value={parceiroCelular}
                            onChange={e => setParceiroCelular(mascaraCelular(e.target.value))}
                            disabled={parceiroLoading || !!parceiroCodigoGerado}
                        />
                        <TextField
                            label="Desconto (%)"
                            fullWidth
                            value={parceiroDesconto}
                            onChange={e => setParceiroDesconto(somenteNumeros(e.target.value))}
                            disabled={parceiroLoading || !!parceiroCodigoGerado}
                            placeholder="Porcentagem de desconto atrelado ao cupom do parceiro"
                            inputProps={{ maxLength: 2 }}
                        />
                        <Typography variant="caption" color="text.secondary">
                            O código de indicação será gerado automaticamente ao salvar o parceiro.
                        </Typography>

                        {parceiroCodigoGerado && (
                            <>
                                <Paper
                                    variant="outlined"
                                    sx={{ mt: 2, p: 1.5, borderRadius: 2, bgcolor: "action.hover" }}
                                >
                                    <Typography variant="subtitle2" gutterBottom>
                                        Código de indicação:
                                    </Typography>
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <TextField
                                            variant="outlined"
                                            size="small"
                                            fullWidth
                                            value={parceiroCodigoGerado}
                                            InputProps={{ readOnly: true }}
                                        />
                                        <Tooltip title="Copiar Código">
                                            <IconButton onClick={() => copy(parceiroCodigoGerado, "Código")}>
                                                <ContentCopyIcon />
                                            </IconButton>
                                        </Tooltip>
                                    </Stack>

                                    <Typography variant="subtitle2" gutterBottom sx={{ mt: 1.5 }}>
                                        Link completo:
                                    </Typography>
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <TextField
                                            variant="outlined"
                                            size="small"
                                            fullWidth
                                            value={`${window.location.origin}/cadastro?indicacao=${parceiroCodigoGerado}`}
                                            InputProps={{ readOnly: true }}
                                        />
                                        <Tooltip title="Copiar Link">
                                            <IconButton onClick={() => copy(`${window.location.origin}/cadastro?indicacao=${parceiroCodigoGerado}`, "Link")}>
                                                <ContentCopyIcon />
                                            </IconButton>
                                        </Tooltip>
                                    </Stack>

                                </Paper>
                            </>
                        )}
                    </Stack>
                </DialogContent>
                <DialogActions style={{ marginBottom: 4 }}>
                    <Button onClick={handleCloseNovoParceiro} disabled={parceiroLoading}>
                        Fechar
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleNovoParceiro}
                        disabled={parceiroLoading || !!parceiroCodigoGerado}
                        startIcon={parceiroLoading ? <CircularProgress size={16} sx={{ color: "white" }} /> : null}
                    >
                        {parceiroLoading ? "Salvando..." : (parceiroCodigoGerado ? "Salvo" : "Salvar")}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Dialog: contato */}
            <Dialog open={dlgContatoOpen} onClose={closeContatoDialog} fullWidth maxWidth="lg">
                <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box sx={{ minWidth: 0 }}>
                        <Typography fontWeight={900} noWrap>
                            {contatoUser?.name || "Contato do usuário"}
                        </Typography>
                    </Box>

                    {(() => {
                        const last = contatoHistorico?.[0]?.contactDate ? isoToBrDate(contatoHistorico[0].contactDate) : "";
                        const info = getContatoChipInfoByLastDate(last);
                        return <Chip
                            size="small"
                            icon={info.icon}
                            label={info.label}
                            sx={info.sx}
                        />;
                    })()}
                </DialogTitle>

                <DialogContent dividers>
                    <Stack spacing={2}>
                        <TextField
                            label="Data Contato"
                            placeholder="DD/MM/AAAA"
                            value={contatoData}
                            onChange={(e) => setContatoData(maskDateInput(e.target.value))}
                            inputProps={{ maxLength: 10, inputMode: "numeric" }}
                            disabled={contatoLoading}
                            fullWidth
                        />

                        <TextField
                            label="Feedback"
                            value={contatoFeedback}
                            onChange={(e) => setContatoFeedback(e.target.value)}
                            multiline
                            minRows={4}
                            disabled={contatoLoading}
                            fullWidth
                        />

                        {/* HISTÓRICO */}
                        <Box sx={{ mt: 1 }}>
                            <Typography variant="subtitle2" fontWeight={900} sx={{ mb: 1 }}>
                                Histórico
                            </Typography>

                            {(!contatoHistorico || contatoHistorico.length === 0) ? (
                                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: "action.hover" }}>
                                    <Typography variant="body2" color="text.secondary">
                                        Nenhum contato registrado ainda. Registre o primeiro acima 👆
                                    </Typography>
                                </Paper>
                            ) : (
                                <Grid container spacing={1}>
                                    {contatoHistorico.map((c) => {
                                        const dataBr = c.contactDate ? isoToBrDate(c.contactDate) : "--/--/----";
                                        return (
                                            <Grid item xs={12} sm={6} key={c.contactId}>
                                                <Paper
                                                    variant="outlined"
                                                    sx={{
                                                        p: 1.5,
                                                        borderRadius: 2,
                                                        bgcolor: "action.hover",
                                                        height: "100%", // mantém altura consistente
                                                    }}
                                                >
                                                    <Stack spacing={0.75}>
                                                        <Stack
                                                            direction="row"
                                                            alignItems="center"
                                                            justifyContent="space-between"
                                                            spacing={1}
                                                        >
                                                            <Typography variant="body2" fontWeight={800}>
                                                                {dataBr}
                                                            </Typography>

                                                            {c.createdAt ? (
                                                                <Typography variant="caption" color="text.secondary">
                                                                    {new Date(c.createdAt).toLocaleString("pt-BR")}
                                                                </Typography>
                                                            ) : null}
                                                        </Stack>

                                                        <Typography
                                                            variant="body2"
                                                            sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
                                                        >
                                                            {c.feedback || (
                                                                <span style={{ opacity: 0.7 }}>(sem feedback)</span>
                                                            )}
                                                        </Typography>
                                                    </Stack>
                                                </Paper>
                                            </Grid>
                                        );
                                    })}
                                </Grid>
                            )}
                        </Box>
                    </Stack>
                </DialogContent>

                {isMaster && (

                    <DialogActions sx={{ mb: 0.5 }}>
                        <Button onClick={closeContatoDialog} disabled={contatoLoading}>
                            Fechar
                        </Button>

                        <Button
                            variant="contained"
                            onClick={salvarContato}
                            disabled={contatoLoading}
                            startIcon={contatoLoading ? <CircularProgress size={16} sx={{ color: "white" }} /> : null}
                        >
                            {contatoLoading ? "Salvando..." : "Salvar"}
                        </Button>
                    </DialogActions>

                )}
            </Dialog>

        </Box>
    );

}

export default function AdminPage() {
    return <AdminPageOperacional />;
}
